import { useCallback, useRef, useEffect, useLayoutEffect, useState, useMemo } from 'react'
import { useQueryClient, useQueries, useQuery } from '@tanstack/react-query'
import { GripVertical, Clock, ChevronRight, LayoutGrid, X, Plus } from 'lucide-react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { TaskBar, SettingsView, BudgetBillingView, budgetBillingPagePath, parseBudgetBillingPath, OrgCreateDialog, ThemeProvider, ThemedArea, useTheme, getThemeClass, ErrorBoundary, AppShellSkeleton, IdentitySwitchCurtainHost, registerIdentityDirectory, ToastProvider, useToast, ConfirmProvider, DeckVisibility, ConflictProvider, UndoProvider, useUndo, useMyCapabilities, useBudgetBillingAccess, ShellManagerProvider, useShell, ShellTopBarProvider, ShellTopBarSlot, ShellTopBarPortal, AppTopBar, IconAssetUrlContext, WriteStatusProvider, useWriteStatus, BillingCurrencyProvider, clearAllDrafts, DraftScopeProvider, IDENTITY_ONLY_HIDDEN_VIEWS, type View, composeRailEntries, railApps, type RailEntry, type RailApp, railKeyOfPin, railKeyOfFolder } from '@papan/core-ui/ui'
import { BrandProvider } from '@papan/brand/react'
import { spaIconAssetResolver } from './lib/iconAssetResolver'
import { ForgotPasswordModal } from './components/ForgotPasswordModal'
import { ShareLinkReviewDialog, type ShareLinkReviewTarget } from './components/ShareLinkReviewDialog'
import { IdentityOnlyNotice } from './components/IdentityOnlyNotice'
import { KeybookDisabledNotice } from './components/KeybookDisabledNotice'
import { landingPath, landingPathFor, landingViewFor, useChatHomeEnabled, useKeybookEnabled, useLandingExperience, useLandingSettled } from './lib/landing'
import { loginPathWithNext } from './lib/session-loss'
import { DESKTOP_APP_ROUTE_PATHS, appDeepLinkEntry } from './lib/app-deep-link'
import { UNHANDLED_MUTATION_ERROR_EVENT } from './lib/mutationErrorBackstop'
import { isIdentityOnlyTenant } from '@papan/orgs/client'
import { parseMiniappIcon, isProjectFolder, presentsAsMiniapp, stripPointerSuffix, appDisplayName, parseMiniappIconRev, resolveNotificationOutcome, describeNotification, readerTenantPathPrefix, createAccessBurst, type TenantMode, type FolderColorKey } from '@papan/types'
import { useTranslation } from '@papan/i18n'
import { isMountId } from '@papan/files/ui/protected-branches'
import { DriveView, BinView, QuarantineView, DriveShell, DriveHomeView, RecentView, ProjectsView, SharedView, SearchResultsView, AppsView, recordAppOpen, filesApi, FileListCacheSync, UploadManagerProvider, UploadDock, NewProjectProvider, useNewProject, useDeleteRunner, MiniappIconEditorDialog, moveItemsInto, type DriveShellPage } from '@papan/files/ui'
import { useFile, permissionQueryOptions, appWriteFailureKeys } from '@papan/files/client/queries'
import { appRunLogApi } from '@papan/files/client/app-run-log'
import { uploadFilesAsBatch } from '@papan/files/client/dropUpload'
import { SessionDeck } from './components/SessionDeck'
import { KeybookView, KeybookGate, SecureStoragePage, LeavingKeysNotice } from '@papan/keybook/ui'
import { ConnectionsPage, AcceptConnectionPopup, ConfirmConnectionPopup, ReshareApprovalPopup, useConfirmBlockEndsConnection } from '@papan/org-connections/ui'
import { BillingPage } from '@papan/billing/ui'
import { billingHandoff } from './billingHandoff'
import { LinksPage } from '@papan/share-links/ui'
import { SpendingPage, BudgetsEditor, StorageUsagePage, VersionRetentionPage, FileActivityPage, StorageBlockedDialog, type StorageBlockedKind } from '@papan/orgs/ui'
import { LauncherView } from '@papan/launcher/ui'
import { LauncherCacheSync } from '@papan/launcher/client'
import { setKeybookContext } from '@papan/keybook/client'
import { WorkspaceCache, EmptyWorkspaceView, SingleFileView, ChatHomeView, ShootingStars, ChatStarToggle, WorkspaceManagerProvider, useWorkspaceManager, DrivePickerDialog, FolderPickerDialog } from '@papan/workspace/ui'
import { workspaceApi, getWorkspaceName, isDefinitiveResolveFailure, opensAsPlainFolder } from '@papan/workspace/client'
import { RealtimeProvider, useRealtime, devOfflineState } from '@papan/realtime/client'
import { setDevOfflineApi, request, onApiFailure, currentSessionSignal, sessionEndedSince, pinnedBatch } from '@papan/api-client'
import { accountEpoch } from '@papan/core-ui/ui/sessionEnded'
import { useTenantDenied } from './lib/tenant-denied'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { HandoffPickerPage } from './pages/HandoffPickerPage'
import { TenantProvider, useTenant } from './hooks/useTenant'
import { useShellPersistence, persistShellOpen, closeShellInstance, pinShellApp, unpinShellApp, setShellPinFolder, deleteShellFolder, createShellFolder, setShellRailOrder, setShellFolderOrder, updateShellFolder, SHELL_CLIENT_ID } from './hooks/useShellPersistence'
import { useIsPhone } from './hooks/useIsPhone'
import { useOrgCreateLimit } from './hooks/useOrgCreateLimit'
import { IdentityShell } from './IdentityShell'
import { decideIdentityShell } from './lib/identityShell'
import { EntryCapture, EntryUnroutedMark } from './lib/EntryCapture'
import { classifyDeck, restoredDeckRoute, WORKSPACE_PATH_RE, type DeckKind } from './lib/deckKind'
import { MobileShell } from './mobile/MobileShell'
import { AppWindow, type OpenFileTargetWire, type OpenPdappTargetWire, type OpenFileOptsWire } from '@papan/collab/ui'
import { requestMiniappView, onOpenAppWindowRequest, encodeServePath } from '@papan/collab/client'
import { LoginPage } from './pages/LoginPage'
import { AppSignInContinuePage } from './pages/AppSignInContinuePage'
import { LogtoBridgeSignInPage } from './pages/LogtoBridgeSignInPage'
import { ShareSignInContinuePage } from './pages/ShareSignInContinuePage'
import { PromoRedeemPage } from './pages/PromoRedeemPage'
import { InviteAcceptPage } from './pages/InviteAcceptPage'
import { McpConsentPage } from './pages/McpConsentPage'
import { McpConnectionsPage } from './pages/McpConnectionsPage'
import { IdentityRequiredDialog } from './pages/IdentityRequiredDialog'
import { TenantAccessDeniedScreen } from './pages/TenantAccessDeniedScreen'
import { ShareViewerChrome } from './pages/ShareViewerChrome'
import { SignupPage } from './pages/SignupPage'
import { ForgotPasswordPage } from './pages/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { VerifyEmailPage } from './pages/VerifyEmailPage'
import { PrivacyPage } from './pages/PrivacyPage'
import { AguiLabPage } from './pages/AguiLabPage'
import { buildPlatformPages } from './platformPages'
import { usePasswordActions } from './lib/password'
import { isPapanDrag, readPapanDrag } from '@papan/core-ui/ui/papanDrag'

/** Taskbar "Edit icon" — a dock entry only carries the app's ref (folder id),
 *  so fetch the file detail (name + metadataJson) before opening the shared
 *  icon editor. The dock chip refreshes via the realtime file:updated event
 *  (AppWindow → setAppIcon), so no manual refresh is needed after save. */
function MiniappIconEditorForApp({ fileId, onClose }: { fileId: string; onClose: () => void }) {
  const { data } = useFile(fileId, { staleTime: 30_000 })
  const { tenantId } = useTenant()
  if (!data) return null
  return (
    <MiniappIconEditorDialog
      file={{ id: fileId, name: data.name, metadataJson: data.metadataJson }}
      tenantId={tenantId || undefined}
      onClose={onClose}
    />
  )
}


/**
 * Passes tenantId + auth state to RealtimeProvider. Mounted ABOVE <Routes>, so
 * it survives the auth flip and `enabled={isAuthenticated}` actually reaches a
 * live provider on session death (that's what stops the reconnect churn). Note
 * the gate engages only once the session is CONFIRMED dead (#501): between the
 * first session-death 401 and refreshSession() flipping isAuthenticated, realtime
 * churns for one round trip — accepted, per the spec's "confirmed dead" scope.
 */
function TenantScopedRealtime({ children }: { children: React.ReactNode }) {
  const { tenantId } = useTenant()
  const { isAuthenticated } = useAuth()
  return <RealtimeProvider tenantId={tenantId} enabled={isAuthenticated}>{children}</RealtimeProvider>
}

/**
 * #956 / #1058: file events and resync reach the file caches on every page, not
 * only where their view is open. Keyed by tenant AND auth state: neither a
 * tenant switch nor sign-out remounts anything up here, and a remount is what
 * drops the previous scope's pending coalesced marks (they would otherwise
 * refetch the next tenant's on-screen queries, or run after sign-out).
 */
function SessionCacheSync() {
  const { tenantId, activeOrgEmail } = useTenant()
  const { isAuthenticated } = useAuth()
  const scope = `${tenantId ?? ''}:${isAuthenticated ? 'in' : 'out'}`
  return <>
    <FileListCacheSync key={`files:${scope}`} orgEmail={activeOrgEmail} />
    <LauncherCacheSync key={`launcher:${scope}`} />
  </>
}

/**
 * Passes tenantId to the write-freeze provider (manual billing §12).
 *
 * Inside `TenantProvider` because the freeze is per tenant, and above the shell
 * because two unrelated surfaces read it — the drive's banner and the chat's
 * composer notice. One read, so they cannot disagree while a freeze is lifted.
 */
function TenantScopedWriteStatus({ children }: { children: React.ReactNode }) {
  const { tenantId } = useTenant()
  return <WriteStatusProvider tenantId={tenantId}>{children}</WriteStatusProvider>
}

/** Unsaved drafts kept across a layout swap (#897, `useDraftState`) belong to
 *  one account in one tenant: a tenant switch or logout drops every one, so a
 *  draft can never surface in another org or for another person. */
function DraftStoreReset() {
  const { tenantId } = useTenant()
  const { user } = useAuth()
  const scope = `${user?.id ?? ''}|${tenantId ?? ''}`
  // Cleared IN this render — this component renders before every screen below
  // it, so none of them reads the previous scope's drafts (keys like
  // `chat:openFile` carry no tenant). Silent here; notified from the effect.
  const cleared = useRef(scope)
  if (cleared.current !== scope) {
    cleared.current = scope
    clearAllDrafts({ silent: true })
  }
  const notified = useRef(scope)
  useEffect(() => {
    if (notified.current === scope) return
    notified.current = scope
    clearAllDrafts({ sameReset: true })
  }, [scope])
  return null
}

function TenantScopedWorkspaceManager({ children }: { children: React.ReactNode }) {
  const { tenantId, isReady } = useTenant()
  // The share viewer has no workspace tabs, and its in-place PDApp Settings may
  // scope an ORG identity onto /api/workspaces (the grant picker's Projects
  // place, #757) — a load here would then store the org's open projects as the
  // personal tenant's. Load once the viewer navigates into the app.
  const onShareView = useLocation().pathname.startsWith('/share-view')
  return <WorkspaceManagerProvider tenantId={tenantId} isReady={isReady && !onShareView}>{children}</WorkspaceManagerProvider>
}

/** Phone viewports get the touch-first MobileShell instead of the desktop
 *  OS-shell. Same URLs, same provider stack above — the swap is pure
 *  presentation, and resizing across the breakpoint re-picks live. */
function RootShell() {
  const isPhone = useIsPhone()
  return isPhone ? <MobileShell /> : <AppLayout />
}



// WsNameTooltip lived here: the old popup showed only a workspace's NAME, so
// the path needed a 500ms hover-portal tooltip to be reachable at all. The
// cards render the path as a second line, so the tooltip had nothing left to
// reveal (long values fall back to the native `title` on each line).

/**
 * Send a moved `/billing/*` path to its new home in Settings.
 *
 * Four pages left the Usage & Billing shelf: `usage` (Tier-3) when the shelf
 * was created, and `budget` / `limits` / `subscription` when it became
 * platform-only. Those paths are linkable and bookmarked, and their users can
 * no longer open the shelf at all, so a redirect is the only landing that stays
 * correct. Fires in an effect (not during render) because it navigates.
 *
 * `keepQuery` carries the CURRENT query string across. That is load-bearing for
 * exactly one caller and it is a money path: a Stripe Checkout session created
 * before this move returns the customer to `/billing/subscription?checkout=success`,
 * and BillingPage reads that param to tell "you just paid" from "you backed
 * out". Rebuilding the path without it makes a successful purchase land on a
 * page identical to an abandoned one. New sessions already point at
 * `/settings/subscription`; this covers the ones in flight across a deploy.
 */
function UsageRedirect({ to, keepQuery }: { to: string; keepQuery?: boolean }) {
  const navigate = useNavigate()
  const location = useLocation()
  const search = keepQuery ? location.search : ''
  useEffect(() => {
    navigate(`${to}${search}`, { replace: true })
  }, [navigate, to, search])
  return null
}

function AppLayout() {
  const passwordActions = usePasswordActions()
  // Settings' password escape hatch, shown in place — see ForgotPasswordModal
  // for why this is not a route.
  const [forgotPwOpen, setForgotPwOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { settings } = useTheme()
  const driveRefreshRef = useRef<(() => void) | null>(null)
  const { user, personalTenantId, isAuthenticated, isLoading, signingOut, signOut, refreshSession } = useAuth()
  const { tenantId, isPersonal, tenantSlug, activeOrgEmail, setActiveOrgEmail, switchTenant, chooseIdentityIfUnset, isReady, orgEmailsHydrated, tenantChosen, markTenantChosen, entryPath } = useTenant()
  const [tenantsList, setTenantsList] = useState<Array<{ id: string; name: string; type: string; mode?: TenantMode; emails: Array<{ email: string; role: string }> }>>([])
  const [showOrgCreate, setShowOrgCreate] = useState(false)
  // #538 — this browser's bar pass: lets a PDApp link's identity bar (Papan's frame inside the
  // PDApp's site, where no sign-in cookie reaches) add / remove the PDApp from favorites directly.
  // Minted while signed in; dies with the session (features/share-links/api/bar-routes.ts).
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return
    void request('/api/pdapps-shell/bar-pass', { method: 'POST' }).catch(() => { /* the star asks to sign in */ })
  }, [isAuthenticated, user?.id])
  // Set when the active org tenant can't auto-resolve which identity to use
  // (the user has 2+ memberships in it and none is selected/stored). The server
  // returns 401 `org_email_required` with the candidate emails; we surface a
  // picker instead of letting the drive show a raw error. Keyed by tenant so a
  // stale prompt for a tenant we've navigated away from doesn't linger.
  const [identityRequired, setIdentityRequired] = useState<{ tenantId: string; identities: Array<{ email: string; role: string }>; tenantName: string | null } | null>(null)

  // Set when the server says this account holds NO membership in the active
  // tenant (403 `tenant_access_denied`, #341 Case A). The tenant id comes from
  // the URL and rides every tenant-scoped request, so this one condition fails
  // the entire authenticated surface at once — the drive used to render its
  // ordinary empty view and the user saw an app that had simply stopped, with
  // nothing telling them why. Boolean (not tenant-keyed like `identityRequired`
  // above) because the detect/clear effects below are already scoped to the
  // CURRENT `tenantId` — see their own comments.
  // Set/reset/app-link fallback in ONE hook shared with the mobile shell (#853).
  const [tenantDenied, setTenantDenied] = useTenantDenied(tenantId, personalTenantId)

  // Pending org invites
  const [pendingInvites, setPendingInvites] = useState<Array<{ inviteId: string; tenantId: string; tenantName: string; email: string; role: string }>>([])
  /** The chat-storage refusal currently being explained (#758). */
  const [storageBlocked, setStorageBlocked] = useState<{ kind: StorageBlockedKind; usedBytes?: number; limitBytes?: number } | null>(null)

  // Connection-notification action popup (member flow): clicking an
  // actionable connection notification opens accept/confirm here.
  const [connAction, setConnAction] = useState<
    | { kind: 'accept'; requestId: string; fromEmail: string; fromName: string | null; orgName: string | null; note: string | null; viaReshare?: boolean; shareDriven?: boolean; referrerName?: string | null; referrerEmail?: string | null; declineTargetEmail?: string | null; sharedName?: string | null; sharedIsFolder?: boolean; targetTenantId?: string | null; targetTenantName?: string | null }
    | { kind: 'confirm'; requestId: string; asTenantId: string; asOrgEmail?: string | null; recipientEmail: string; recipientName: string | null; pickedTenantName: string | null; notifiedEmail: string | null; initiatorOrgName: string | null; source: 'org_connection' | 'share_link_mount'; linkPermission: string | null; linkCap?: string | null; shareDriven?: boolean; sharedName?: string | null }
    | { kind: 'reshare-approve'; requestId: string; referrerName: string | null; recipientEmail: string | null; asTenantId?: string | null; asOrgEmail?: string | null }
    | null
  >(null)
  /** The share link a connected app made, open for review from the bell (keep or revoke). */
  const [shareLinkReview, setShareLinkReview] = useState<ShareLinkReviewTarget | null>(null)

  /**
   * Has `my-tenants` answered yet, either way?
   *
   * Only the HOME REDIRECT waits on this, and the distinction is the whole
   * point. `identityOnly` below is deliberately `false` until the list lands —
   * for the SHELL that is right, because a full-looking taskbar that narrows a
   * moment later beats hiding the drive from every user for a frame on every
   * load.
   *
   * A `<Navigate replace>` is not a frame. It is a one-shot decision, and
   * getting it wrong lands an identity-only person on the chat home — whose
   * empty state invites them to create a project they cannot create — with no
   * later re-render able to take it back. Verified in a browser: without this
   * the launcher override never fired at all, warm or cold.
   */
  const [tenantsSettled, setTenantsSettled] = useState(false)

  // Fetch tenants list + pending invites
  useEffect(() => {
    if (!isAuthenticated) return
    setIdentityRequired(null)
    setTenantsSettled(false)
    import('@papan/orgs/client').then(({ orgsApi }) => {
      orgsApi.listMyTenants()
        .then(data => { setTenantsList(data.tenants); setTenantsSettled(true) })
        .catch((e: { code?: string; availableIdentities?: Array<{ email: string; role: string }>; tenantName?: string | null }) => {
          // Settles either way, ALWAYS. The home redirect below waits on this,
          // and a flag that only resolves on success turns any transient
          // failure into an indefinite skeleton.
          setTenantsSettled(true)
          // This tenant has 2+ of the user's memberships and no identity is
          // selected yet (typically a deep link into an org Drive, e.g. "Open
          // in my Drive" from a share). Every tenant-scoped call — including
          // this one — 401s until an identity is chosen. Surface the picker
          // with each identity's role + the tenant name.
          if (e?.code === 'org_email_required' && Array.isArray(e.availableIdentities) && e.availableIdentities.length > 0) {
            setIdentityRequired({ tenantId, identities: e.availableIdentities, tenantName: e.tenantName ?? null })
          }
        })
      orgsApi.listMyInvites().then(data => setPendingInvites(data.invites)).catch(() => {})
    })
  }, [isAuthenticated, tenantId])

  // #341 Case A — surface "no membership in the active tenant" instead of
  // rendering nothing. The `.catch` above only handles `org_email_required`
  // (the identity-picker case); a `tenant_access_denied` 403 fell through it
  // entirely and set no state, so the drive rendered its ordinary empty view.
  // Subscribing to `onApiFailure` catches it on EVERY tenant-scoped call, not
  // only this bootstrap one — files, workspaces, bin, agent, notifications all
  // reject together once the URL's tenant id goes stale (account switch, or a
  // membership removed under a live tab).
  //
  // The tight `status === 403 && code === 'tenant_access_denied'` filter is
  // deliberate: it must not fire on #501's session-death 401s, which carry no
  // `code` at all.
  //
  // Narrow, self-correcting edge: a straggler 403 from the PREVIOUS tenant that
  // lands just after a switch could re-set this for a tenant that is actually
  // fine; the resolve-clear effect below drops it on the next
  // `tenantsList`/`tenantId` settle. A personal tenant is NOT immune: once the
  // cookie changes account in another tab, this tab's own `/~` is refused for real
  // (#341) — so `useTenantDenied` never ignores an own-drive refusal on its own say:
  // it asks the server (one probe as that drive) whether it is a straggler (#853).
  // (The subscription and the reset-on-switch live in `useTenantDenied`.)

  // A tenant that resolves is a tenant we have access to — clears a stale
  // verdict WITHOUT waiting for a navigation, e.g. a membership restored
  // mid-session while the person stayed on the same (denied) tenant.
  useEffect(() => {
    if (tenantsList.some(t => t.id === tenantId)) setTenantDenied(false)
  }, [tenantsList, tenantId])

  /**
   * Is the ACTIVE tenant identity-only? (0175, §8 — Phase E.)
   *
   * The one derivation in the desktop shell; everything below reads this rather
   * than re-deriving from `type`, and nothing anywhere re-resolves the rule.
   * Absent `mode` is FULL — see `isIdentityOnlyTenant`: this is disclosure, not
   * enforcement, and an API instance that predates the field is reachable
   * during a rolling restart.
   *
   * `tenantsList` is empty on the first render pass, so this is `false` until it
   * lands. That is the correct direction: a brief full-looking shell that then
   * narrows beats hiding the drive from every user for a frame on every load.
   */
  //
  // TWO sources, OR'd, and the second is what makes §8's last bullet work
  // ("react to the stable 403 by refreshing session mode"):
  //
  //  - `my-tenants` answers for EVERY tenant, which the switcher and the
  //    auto-switch target below both need — but it is fetched on tenant change,
  //    so a platform admin flipping the mode mid-session does not reach it.
  //  - `useWriteStatus` answers for the ACTIVE tenant only, is polled, and
  //    exposes `refresh()` — so the surface narrows on its own after an
  //    operator's change, with no reload and no new mechanism.
  //
  // Both read the SAME server-resolved field through the SAME resolver, so they
  // cannot disagree about the rule — only about how fresh they are. OR'ing
  // takes whichever noticed first; there is no case where one is right and the
  // other is wrong, only one that is later.
  //
  // ⚠ But an OR only converges in ONE direction, and the effect below is what
  // makes the other one work. See its docblock — this pair is not optional.
  const writeStatus = useWriteStatus()
  const cachedTenantIdentityOnly = isIdentityOnlyTenant(tenantsList.find(t => t.id === tenantId))
  const identityOnly = useMemo(
    () => cachedTenantIdentityOnly || writeStatus.identityOnly,
    [cachedTenantIdentityOnly, writeStatus.identityOnly],
  )

  // Platform-wide keybook switch (0179). Disclosure only — the server's
  // /api/keybook gate is the enforcement; this hides the taskbar item, the
  // keep-alive deck and Settings → Secure Storage.
  const keybookEnabled = useKeybookEnabled()
  // The chat home exists only on a chat-landing deployment (#368): on a
  // launcher deployment its deck never mounts and its URLs are left unrouted,
  // so the catch-all sends them home. `chatHomeAvailableFor` is the rule.
  const chatOn = useChatHomeEnabled()

  /**
   * Re-read `my-tenants` when the polled signal says the drive came BACK.
   *
   * An OR narrows as soon as EITHER source notices, which is right — but it
   * widens only when BOTH have cleared, and `my-tenants` is fetched on tenant
   * change alone. So an operator restoring a drive mid-session left the person
   * looking at "There is no drive here" until they reloaded or switched tenants,
   * even though the poll had already learned the truth. Verified in a browser:
   * the mode was restored, `write-status` reported `identityOnly: false` on the
   * next poll, and the notice stayed up indefinitely.
   *
   * That is the direction that RESTORES a capability, which CLAUDE.md's
   * write-freeze note calls the one that matters — a lingering restriction is a
   * customer who believes the change did not work.
   *
   * Deliberately one-way: it fires only on `false` from the poll, so it can
   * never be used to widen from the fail-open default. `writeStatus.identityOnly`
   * is `false` before the first response too, hence the `cachedTenantIdentityOnly`
   * guard — with nothing cached claiming otherwise there is nothing to reconcile,
   * and the effect is inert on every full tenant on every deployment.
   */
  //
  // BOTH directions now, because `decideIdentityShell` reads the mode from this
  // LIST rather than from the OR above (see its `activeIsIdentityOnly`). The
  // restore direction is the one CLAUDE.md calls out; the strip direction is
  // what keeps the org-less screen reachable after an operator's change without
  // the shell ever consulting a flag that lags a tenant switch. Re-reading is
  // safe in a way flipping a flag is not — the answer still comes from the
  // server, and the effect settles because the fetch cannot re-trigger itself.
  // Gated on `tenantsSettled` so this reconciles only a MID-SESSION divergence,
  // never the transient one during initial load or a tenant switch — those
  // windows have their own `my-tenants` fetch already in flight (the effect
  // above resets `tenantsSettled` to false and re-reads), so firing here too was
  // a redundant concurrent request. After settle, `tenantsSettled` stays true,
  // so an operator's mid-session change still triggers the re-read.
  useEffect(() => {
    if (!tenantsSettled) return
    if (cachedTenantIdentityOnly === writeStatus.identityOnly) return
    import('@papan/orgs/client').then(({ orgsApi }) => {
      orgsApi.listMyTenants().then(data => setTenantsList(data.tenants)).catch(() => {})
    })
  }, [tenantsSettled, cachedTenantIdentityOnly, writeStatus.identityOnly])

  /**
   * Land in a tenant the person can actually work in (§8, decision 2026-08-12).
   *
   * A Gnexis-style deployment makes every personal tenant identity-only and
   * expects people to work inside an organization. Without this, such a user
   * opens the product, lands on their own stripped-down tenant, and has to find
   * the switcher before anything works — every single time.
   *
   * Four things keep it from being surprising:
   *
   *  - It fires ONLY when the active tenant is identity-only. A full personal
   *    tenant is never moved, so the default product is untouched.
   *  - It picks a FULL tenant. Switching to another identity-only one would
   *    achieve nothing, and on a deployment where every personal tenant is
   *    identity-only the only candidates are organizations anyway.
   *  - It runs once per tenant id (`autoSwitchedRef`), so a user who
   *    deliberately switches BACK to their identity-only tenant stays there.
   *    Without that guard this is an infinite loop with the switcher.
   *  - It waits for `orgEmailsHydrated`, like its neighbour below: switching
   *    into an org before the stored identity is known would clobber the
   *    selection with the user's primary email.
   *
   * Neither zero nor two-plus full tenants auto-switches: the identity shell
   * below takes both. See `fullTenants`.
   */
  /**
   * The shell decision, from the ONE implementation both shells call.
   *
   * `MobileShell` replaces this component outright on a phone, so anything
   * decided inline here simply does not exist there — which is precisely how
   * the picker and the org-less screen shipped desktop-only. See
   * `decideIdentityShell`.
   */
  // #1253: where this page load entered the app (`entryPath`, from useTenant —
  // a `/~/` deep link counts as a choice) and the landing view (which does not).
  // The ONE subscription in this component; the home view below reuses it.
  const landingExperience = useLandingExperience()
  const shellDecision = useMemo(
    () => decideIdentityShell({
      tenants: tenantsList,
      tenantId,
      // `/t/<id>/…` — the URL already names the organization, so there is
      // nothing for the picker to ask. (A `/~/…` deep link is judged inside the
      // rule, from where this shell was entered — #1253.)
      tenantInUrl: Boolean(tenantSlug),
      entryPath,
      landingView: landingExperience,
      settled: tenantsSettled,
      chosen: tenantChosen,
    }),
    [tenantsList, tenantId, tenantSlug, entryPath, landingExperience, tenantsSettled, tenantChosen],
  )
  const { switchable: switchableTenants, pickerActive, personalIsIdentityOnly } = shellDecision

  const autoSwitchedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!orgEmailsHydrated || !identityOnly) return
    if (autoSwitchedRef.current === tenantId) return
    // EXACTLY one, resolved by the shared rule. Taking the FIRST of several is
    // a silent choice made on the person's behalf between destinations only
    // they can rank — and it is not even stable, since it follows `my-tenants`
    // order. Two or more is the picker's case; zero is the org-less shell's.
    const target = shellDecision.autoSwitchTarget
    if (!target) return
    autoSwitchedRef.current = tenantId
    const email = target.emails.find(e => e.email === user?.email)?.email ?? target.emails[0]?.email
    switchTenant(target.id, email)
  }, [orgEmailsHydrated, identityOnly, tenantId, shellDecision, user, switchTenant])

  // Auto-initialize activeOrgEmail when navigating to an org tenant without one set.
  // Last-resort fallback for first-time visits to an org with no prior selection.
  // MUST wait for `orgEmailsHydrated` — otherwise this races the server-side
  // hydration and clobbers selections set via the org-switcher submenu, an e2e
  // helper, or a deep link, with the user's primary email.
  useEffect(() => {
    if (!orgEmailsHydrated) return
    if (isPersonal || activeOrgEmail || tenantsList.length === 0) return
    const tenant = tenantsList.find(t => t.id === tenantId)
    if (!tenant?.emails?.length) return
    // Prefer the user's session email if it has a membership, otherwise first email
    const sessionMatch = user?.email ? tenant.emails.find(e => e.email === user.email) : null
    const bestEmail = sessionMatch?.email ?? tenant.emails[0].email
    setActiveOrgEmail(bestEmail)
  }, [orgEmailsHydrated, isPersonal, tenantId, activeOrgEmail, tenantsList, user, setActiveOrgEmail])
  // Keep keybook client context in sync with active tenant so v1-shape
  // API calls (e.g. from CredentialPrompt) route correctly to v2 endpoints.
  useEffect(() => {
    setKeybookContext(tenantId ?? null, (activeOrgEmail || user?.email) ?? null)
  }, [tenantId, activeOrgEmail, user?.email])

  // May this user create an organization (§8.7)? `undefined` until the answer
  // lands, and every consumer renders the button for `undefined` — see
  // `useOrgCreateLimit`.
  const canCreateOrg = useOrgCreateLimit()

  // Resolved capabilities for the active identity — gates UI on the same
  // values the server enforces (replaces `userRole === 'admin'` checks).
  const queryClient = useQueryClient()
  const { can: canCap, loading: capsLoading } = useMyCapabilities(tenantId, isPersonal, activeOrgEmail)

  // Helper: get user's role for the active tenant from the emails-based tenant list
  const activeRole = useMemo(() => {
    const t = tenantsList.find(t => t.id === tenantId)
    if (!t) return undefined
    if (activeOrgEmail) {
      const emailEntry = t.emails.find(e => e.email === activeOrgEmail)
      if (emailEntry) return emailEntry.role
    }
    // No explicit org email — use session email, then first email
    const sessionEntry = user?.email ? t.emails.find(e => e.email === user.email) : null
    return sessionEntry?.role ?? t.emails[0]?.role
  }, [tenantsList, tenantId, activeOrgEmail, user])

  // May this viewer open the platform shelf — gates the taskbar item AND the
  // /billing route below (hiding a button is not access control; anyone who
  // types the URL must land somewhere real). Mirrors `requirePlatformAdmin`:
  // the role AND acting in the platform org.
  const budgetBilling = useBudgetBillingAccess({
    tenantId, platformRole: user?.platformRole,
  })
  const hasBudgetBilling = budgetBilling.allowed
  // Billing authority — the `manage_billing` capability, matching the server's
  // own gate. Personal tenants short-circuit (the capability query only runs
  // for org tenants); owner is an absolute floor in the resolver, so a
  // personal owner always qualifies.
  const canManageBilling = isPersonal || canCap('manage_billing', 'write')
  /**
   * Whether the Billing SCREEN exists for this caller at all — read, not write.
   *
   * An org's billing is not member business: the plan, the seat count, the
   * tenant's AI allowance and what it owes are all facts about the
   * organization's commercial relationship, and a member has no part in it.
   * So the nav entry is absent, not disabled, and the routes refuse in kind
   * (`requireBillingVisibility`); hiding the item alone would leave the data
   * one `fetch` away and make this a cosmetic change.
   *
   * READ deliberately, where the controls take WRITE. The two questions are
   * "may you look at this?" and "may you change it?", and this repo keeps them
   * apart everywhere else — Spending is read-gated for the view, Budget
   * write-gated for the editor. Gating the page on write would also take it
   * from anyone deliberately granted read-only billing access, which is the
   * grant that exists precisely so someone can see the bill without moving it.
   */
  const canViewBilling = isPersonal || canCap('manage_billing', 'read')

  // Display name for the active tenant — drives the personalized DriveView
  // header ("{{tenant}}'s Drive"). Mirrors AvatarMenu: a personal tenant is
  // labeled by the user's name, an org by the org name. Undefined while the
  // tenant list / user is still loading — DriveView falls back to the generic
  // localized root name in that window.
  const driveTenantName = useMemo(() => {
    if (isPersonal) return user?.name
    return tenantsList.find(t => t.id === tenantId)?.name
  }, [isPersonal, tenantsList, tenantId, user])

  // Build email → org memberships map for email removal warnings
  const orgsByEmail = useMemo(() => {
    const map: Record<string, Array<{ orgName: string; role: string }>> = {}
    for (const t of tenantsList) {
      if (t.type !== 'organization') continue
      for (const e of t.emails) {
        const key = e.email.toLowerCase()
        if (!map[key]) map[key] = []
        map[key].push({ orgName: t.name, role: e.role })
      }
    }
    return map
  }, [tenantsList])

  const wsManager = useWorkspaceManager()
  const { openWorkspaces, activeWorkspaceId, closeWorkspace: _closeWorkspace, loaded: wsLoaded } = wsManager
  const { onFileEvent, onNotification, onOrgEvent } = useRealtime()
  // Another tab or device changed this identity's dock: re-read it (the
  // persistence hook watches this). Compared through a ref so the org-event
  // subscription below is not re-created on every identity change.
  const [dockRevision, setDockRevision] = useState(0)
  const dockIdentityRef = useRef('')
  const [devOffline, setDevOffline] = useState(false)
  const toast = useToast()
  const undo = useUndo()
  const { t: tCoreUi } = useTranslation('core-ui')
  const { t } = useTranslation('files')
  const { t: tCore } = useTranslation('core-ui')
  // Who a tenant is, for the identity portal a switch plays: an org by its name (its avatar seed — AvatarMenu's
  // rule) with the email acted as; the personal account by the person's name, seeded on their email.
  useEffect(() => {
    registerIdentityDirectory((id, orgEmail) => {
      if (id === personalTenantId) return user ? { seed: user.email, title: user.name || user.email, subtitle: tCore('identitySwitch.personal') } : null
      const org = tenantsList.find((t) => t.id === id)
      if (!org) return null
      const as = org.emails.find((e) => e.email.toLowerCase() === (orgEmail ?? '').toLowerCase()) ?? org.emails[0]
      return { seed: org.name, title: org.name, subtitle: as?.email, role: as?.role }
    })
    return () => registerIdentityDirectory(null)
  }, [tenantsList, user, personalTenantId, tCore])

  // #341 Case F: the MutationCache backstop (main.tsx) dispatches this event
  // when a mutation fails and its call site declared no `onError` of its
  // own — the silent-failure case where the optimistic result stayed on
  // screen and nothing told the user the write never happened. The
  // QueryClient that dispatches it is built outside the React tree and
  // can't reach ToastProvider directly, so this is the render half.
  useEffect(() => {
    const handleUnhandledMutationError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail
      toast.error(detail?.message || tCore('mutationError.somethingWentWrong'))
    }
    window.addEventListener(UNHANDLED_MUTATION_ERROR_EVENT, handleUnhandledMutationError)
    return () => {
      window.removeEventListener(UNHANDLED_MUTATION_ERROR_EVENT, handleUnhandledMutationError)
    }
  }, [toast, tCore])

  // #341 Case D: a 429 (rate-limited) response otherwise surfaced as a generic
  // failure. The server sends `retryAfterSec` in the body; show a friendly toast
  // with the wait hint. Toast-only by decision — no inline retry.
  //
  // Coalesce a burst: several rate-limited calls landing together would each
  // toast, stacking identical messages. One toast per 3s window is enough to
  // tell the user they're throttled.
  const lastRateLimitToastAtRef = useRef(0)
  useEffect(() => {
    return onApiFailure((failure) => {
      // ── Chat storage refusals (#758) ────────────────────────────────
      //
      // Subscribed HERE rather than at each call site because the same two
      // refusals come back from the chat send, the AG-UI run, the
      // attachment upload and a fork — four places, one answer. A toast
      // would not do: the member has to be told which of the two it is and
      // what to do about it, and the organisation case has to say outright
      // that clearing their own files frees nothing.
      //
      // REVIEW ROUND 1, FINDING 9 — scoped to the CHAT paths.
      //
      // `STORAGE_QUOTA_EXCEEDED` is not a chat code: every drive write
      // raises it too (`assertCreateWithinStorageCap`), and those call sites
      // — drive copy, Save to Drive, Add-to-Drive materialize, share-link
      // saves — already render their own refusal. Unscoped, this opened a
      // modal ON TOP of each of those, on paths this PR does not otherwise
      // touch. `MEMBER_CHAT_STORAGE_EXCEEDED` is chat-only by construction,
      // but it is scoped the same way so the rule is one rule.
      //
      // Both chat surfaces live under /api/agent (AG-UI is
      // /api/agent/agui), so one prefix covers the send, the AG-UI run, the
      // attachment upload and the fork.
      if (failure.status === 413 && (failure.path ?? '').startsWith('/api/agent')) {
        const b = failure.body ?? {}
        const num = (k: string) => (typeof b[k] === 'number' ? b[k] as number : undefined)
        if (failure.code === 'MEMBER_CHAT_STORAGE_EXCEEDED') {
          setStorageBlocked({ kind: 'member', usedBytes: num('usedBytes'), limitBytes: num('limitBytes') })
          return
        }
        if (failure.code === 'STORAGE_QUOTA_EXCEEDED') {
          setStorageBlocked({ kind: 'organisation', usedBytes: num('usedBytes'), limitBytes: num('quotaBytes') })
          return
        }
      }
      if (failure.status !== 429) return
      // #1316 round 3: a step of a batch started by a session that has ended (sent as its identity) is not news for this one
      if (failure.staleIdentity) return
      // The page that asked renders these itself (#1217) — a second, vaguer toast would contradict it.
      if (failure.selfExplained) return
      // #1292 read-slot refusals are not rate limiting: the busy upload's chip (or the
      // send's own error) already says what happened — a generic "too many requests"
      // beside it blames the wrong thing.
      if (failure.code === 'UPLOAD_BUSY_LARGE' || failure.code === 'SEND_BUSY_LARGE') return
      const now = Date.now()
      if (now - lastRateLimitToastAtRef.current < 3000) return
      lastRateLimitToastAtRef.current = now
      const secs = failure.retryAfterSec
      toast.error(
        typeof secs === 'number' && Number.isFinite(secs) && secs > 0
          ? tCore('rateLimit.retryInSeconds', { secs: Math.ceil(secs) })
          : tCore('rateLimit.tryAgainSoon'),
      )
    })
  }, [toast, tCore])

  /**
   * Invite actions, hoisted out of the taskbar's props.
   *
   * They used to be three inline arrow functions on `<TaskBar>`. The identity
   * shell mounts the SAME `AvatarMenu` and needs the same three, and an
   * org-less person's only route forward is accepting an invite — so a second
   * copy is the one that would matter when it drifted.
   */
  const handleAcceptInvite = useCallback(async (token: string) => {
    try {
      const { orgsApi } = await import('@papan/orgs/client')
      await orgsApi.acceptInviteByToken(token)
      setPendingInvites(prev => prev.filter(p => p.inviteId !== token))
      // Re-read rather than patch: the accepted org is a new switch target, and
      // on the identity shell it is what dismisses the screen.
      orgsApi.listMyTenants().then(data => setTenantsList(data.tenants)).catch(() => {})
      toast.success('Invitation accepted!')
    } catch (err: any) {
      // #1152: this account hasn't proven the invited address — the invitation page asks for the code sent to it.
      if (err?.code === 'INVITE_ADDRESS_PROOF_REQUIRED') { navigate(`/invite/${token}`); return }
      toast.error(err?.message || 'Failed to accept invite')
    }
  }, [toast, navigate])

  const handleDeclineInvite = useCallback(async (token: string) => {
    try {
      const { orgsApi } = await import('@papan/orgs/client')
      await orgsApi.declineInvite(token)
      setPendingInvites(prev => prev.filter(p => p.inviteId !== token))
      toast.success('Invitation declined')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to decline invite')
    }
  }, [toast])

  const confirmBlockEndsConnection = useConfirmBlockEndsConnection()
  const handleBlockInvite = useCallback(async (token: string) => {
    try {
      // dw D6: blocking an org this person's drive is connected to also disconnects them.
      const invite = pendingInvites.find(p => p.inviteId === token)
      if (invite && !(await confirmBlockEndsConnection({ tenantId: invite.tenantId, personal: true }))) return
      const { orgsApi } = await import('@papan/orgs/client')
      await orgsApi.blockInvite(token)
      setPendingInvites(prev => prev.filter(p => p.inviteId !== token))
      toast.success('Blocked invitations from this organization')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to block invite')
    }
  }, [toast, pendingInvites, confirmBlockEndsConnection])

  // Realtime org events — refresh members page and tenant list
  const [orgRefreshKey, setOrgRefreshKey] = useState(0)
  // The Members page reloads at most once per interval (`createAccessBurst`), not once per org
  // event: an admin's run of team / member edits is one event per click, and this reload is in the
  // admin's own tab — the one that passed the read limit (gnexis 2026-10-21). Lives as long as the
  // app, so the effect below re-running never drops a pending reload.
  const [orgRefreshBurst] = useState(() => createAccessBurst(() => setOrgRefreshKey(n => n + 1)))
  useEffect(() => () => { orgRefreshBurst.cancel() }, [orgRefreshBurst])
  const tenantsListRef = useRef(tenantsList)
  tenantsListRef.current = tenantsList
  const tenantIdRef = useRef(tenantId)
  tenantIdRef.current = tenantId
  useEffect(() => {
    return onOrgEvent((event) => {
      if (event.type === 'shell:changed') {
        // Not our own write: this tab already holds that state, and re-reading
        // it could land after a NEWER local change and revert it.
        if (event.email === dockIdentityRef.current && event.clientId !== SHELL_CLIENT_ID) setDockRevision(n => n + 1)
        return
      }
      orgRefreshBurst.push()
      if (event.type === 'org:role_changed') {
        import('@papan/orgs/client').then(({ orgsApi }) => {
          orgsApi.listMyTenants().then(data => setTenantsList(data.tenants)).catch(() => {})
        })
        // Resolved capabilities changed with the role — drop the cache so
        // admin-only UI (member mgmt, purge, delete-org) re-gates immediately
        // instead of lingering up to the 5-min staleTime.
        queryClient.invalidateQueries({ queryKey: ['my-capabilities'] })
        // "Your role was changed" is FIRST-PERSON, so it may only fire for the
        // member whose role actually changed — the event is broadcast to the
        // whole org, so without this guard the admin who made the change (and
        // every other member) sees it too. Mirror the member_removed guard
        // below, but key off event.tenantId (the org where it happened) rather
        // than the active tenant, so a role change in a background org still
        // reaches the right person. Email is the org identity — compare
        // case-insensitively.
        const evOrgEmails = tenantsListRef.current
          .find(t => t.id === event.tenantId)?.emails.map(e => e.email.toLowerCase()) ?? []
        if (event.email && evOrgEmails.includes(event.email.toLowerCase())) {
          toast.info(`Your role was changed to ${event.role}`)
        }
      } else if (event.type === 'org:renamed') {
        // Patch the name in place rather than refetching `my-tenants`: the
        // event already carries it, and this list is what feeds the taskbar,
        // the org switcher and the drive header. The renamer gets this too and
        // it is idempotent — their list was patched optimistically on save.
        if (event.orgName) {
          setTenantsList(prev => prev.map(t => (t.id === event.tenantId ? { ...t, name: event.orgName! } : t)))
        }
      } else if (event.type === 'org:member_removed') {
        const myEmails = tenantsListRef.current.find(t => t.id === tenantIdRef.current)?.emails.map(e => e.email) ?? []
        if (myEmails.includes(event.email)) {
          // the switch FIRST: it ends the session synchronously, and a session end clears every toast (#1316)
          switchTenant(personalTenantId || '', undefined)
          toast.warning('You have been removed from this organization')
          import('@papan/orgs/client').then(({ orgsApi }) => {
            orgsApi.listMyTenants().then(data => setTenantsList(data.tenants)).catch(() => {})
          })
        }
      }
    })
  }, [onOrgEvent, switchTenant, personalTenantId, toast, queryClient, orgRefreshBurst])

  // Realtime invite notifications — refresh pending invites in avatar menu
  useEffect(() => {
    return onNotification((notification) => {
      if (notification.type === 'invite_refresh') {
        import('@papan/orgs/client').then(({ orgsApi }) => {
          orgsApi.listMyInvites().then(data => setPendingInvites(data.invites)).catch(() => {})
        })
      }
    })
  }, [onNotification])

  // Share re-point outcomes — toast the editing user. The collab save loop
  // catches FK / unique-identity errors in persistence.ts and emits a
  // per-user notification with the outcome; without surfacing it here the
  // autosave retries the same error invisibly and the user has no signal
  // whether their JSON edit took. Title + body are joined with an em-dash
  // because the toast API takes a single message string (no description
  // slot today).
  useEffect(() => {
    return onNotification((notification) => {
      if (notification.type === 'share_repointed') {
        toast.success(`${notification.title}${notification.body ? ' — ' + notification.body : ''}`)
      } else if (notification.type === 'share_repoint_failed') {
        toast.warning(`${notification.title}${notification.body ? ' — ' + notification.body : ''}`)
      } else if (notification.type === 'budget_threshold') {
        // Also lands in the bell (not silent) — the toast is what makes an
        // in-flight agent turn's spend visible before the user starts another.
        toast.warning(`${notification.title}${notification.body ? ' — ' + notification.body : ''}`)
      } else if (notification.type === 'promo_granted') {
        // Success, not a warning: a promo is something arriving. The redeemer
        // is looking at the app when this fires, and so is any co-admin whose
        // org just gained budget they didn't ask for.
        toast.success(`${notification.title}${notification.body ? ' — ' + notification.body : ''}`)
      } else if (notification.type === 'promo_expiring') {
        toast.warning(`${notification.title}${notification.body ? ' — ' + notification.body : ''}`)
      }
    })
  }, [onNotification, toast])

  // Tenant-scoped clipboard hygiene on a tenant switch (#752). A COPY clipboard is exactly how a cross-tenant copy is
  // reached (copy in A → switch to B → paste, which confirms first); wiping it
  // here made that whole feature unreachable. A cut can never cross tenants
  // (the server refuses cross-tenant moves), so it is dropped rather than left
  // to offer a Paste that can only fail. Sign-out still clears everything
  // (useAuth).
  //
  // Skipped while `tenantId` is still '' (auth loading on a `/~/…` reload) —
  // comparing against '' dropped every legitimate personal-drive cut. An
  // UNSTAMPED clipboard (written before writers stamped their tenant) can't be
  // attributed, so it is dropped rather than pasted as if same-tenant.
  useEffect(() => {
    if (!tenantId) return
    try {
      const raw = localStorage.getItem('papan-clipboard')
      const clip = raw ? (JSON.parse(raw) as { isCut?: boolean; tenantId?: string }) : null
      // Only the UNSTAMPED legacy clipboard is dropped here. Another tenant's CUT
      // is left alone: localStorage is shared by every tab while the tenant is
      // per tab, so deleting it here wiped a pending cut in the tab that made it
      // (full review A). Every reader hides a foreign cut instead.
      if (clip && !clip.tenantId) localStorage.removeItem('papan-clipboard')
    } catch {
      localStorage.removeItem('papan-clipboard')
    }
  }, [tenantId])

  // Wire dev offline → API base URL (one-time setup)
  useEffect(() => {
    return devOfflineState.subscribe(setDevOfflineApi)
  }, [])

  // URL prefix: /~ for personal tenant, /t/:id for org tenants
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`

  // Which surface this deployment calls home — the taskbar logo's target, and
  // the one surface that gets no close button (closing home onto itself would
  // tear the window down and immediately re-open it, so the button would look
  // broken rather than do nothing).
  //
  // SUBSCRIBED, not read: the landing is a runtime platform setting that
  // arrives over the network, and this component owns both the taskbar's home
  // target and the two `<Navigate>` routes below. One subscription re-renders
  // all three together, so the home button and the redirect cannot end up
  // naming different surfaces for a frame.
  //
  // Mode-aware since Phase E: an identity-only tenant always calls the LAUNCHER
  // home, whatever the deployment configured, because the chat home resolves an
  // agent session against a workspace that must already exist and
  // `workspace.create` is gated there. `landingViewFor` is the one place that
  // override lives — see its docblock.
  // Subscribe, then compose — the same shape `useLandingPath` documents.
  // `landingViewFor` reads the very store this hook subscribes to, so the value
  // is the subscribed one; the subscription is `landingExperience` above.
  const homeView = landingViewFor(identityOnly ? 'identity-only' : 'full')

  /** ONE org-create dialog element, mounted either in this tree or — while the
   *  identity shell covers it — through the shell's overlay slot. */
  const orgCreateDialog = (
    <OrgCreateDialog
      userEmail={user?.email}
      onCreated={(newTenantId, name, ownerEmail) => {
        setTenantsList(prev => [...prev, { id: newTenantId, name, type: 'organization', emails: [{ email: ownerEmail, role: 'owner' }] }])
        setShowOrgCreate(false)
        switchTenant(newTenantId, ownerEmail)
      }}
      onClose={() => setShowOrgCreate(false)}
    />
  )

  /**
   * Does the identity shell take the screen? (§8.2 / §8.3.)
   *
   * TWO independent states, and they answer to different audiences — which is
   * why they are separate booleans rather than one count with holes in it.
   *
   * **The picker is for EVERYONE with 2+ organizations**, identity-only or not
   * (decision 2026-08-14, closing the "non-IOM users reaching this screen"
   * seam §8.2 left open). Landing such a person somewhere by default is a guess
   * whichever mode they are in. The cost was weighed and accepted: a multi-org
   * person now meets a full-screen choice on every cold load, and it overrides
   * the remembered tenant. `tenantChosen` is what keeps it to ONCE per load.
   *
   * **The org-less state stays identity-only.** A full-tenant person with no
   * organization has their own drive and nothing is missing, so the "no
   * organization yet" screen would be false for them.
   *
   * ⚠ `tenantsSettled`, never `tenantsList.length`. Every count here is also
   * what an unanswered `my-tenants` looks like, so gating on the raw list paints
   * this overlay across every user's screen on every cold load — invisible
   * locally, plain on a throttled connection (§9.1). `identityOnly` alone is not
   * enough: it is deliberately `false` until the list lands, which protects the
   * SHELL (a full-looking taskbar that narrows a moment later beats hiding the
   * drive for a frame) but says nothing about a takeover that must not flash.
   * That guard now covers the whole user base rather than identity-only
   * tenants, so it is load-bearing for far more people than when it was written.
   */
  const identityShellActive = shellDecision.active

  // Has the landing fetch finished, either way? Held below, after the auth
  // gates — see the early returns. Subscribed for the same reason `homeView`
  // is: a bare `landingSettled()` would never re-render when the fetch lands.
  const landingReady = useLandingSettled()

  // "Run as app" from a mini-app editor toolbar / workspace panel header:
  // collab is router-free, so it emits a window event and we own the router.
  useEffect(() => onOpenAppWindowRequest((folderId) => {
    navigate(`${prefix}/app/${folderId}`)
  }), [navigate, prefix])

  // Clicking an actionable connection notification (the member flow):
  // accept / confirm open a popup; the admin-approval one routes to the
  // Network tab (where owners/admins approve).
  //
  // Notifications outlive the request: a request can be cancelled, expired,
  // rejected, or already established while its notification still sits in
  // the bell. We re-check the request's CURRENT actionable state against
  // the live inbox before opening anything — a stale click just shows a
  // toast instead of a dead-end modal.
  const handleNotificationAction = useCallback(async (n: { type: string; data?: Record<string, unknown>; requestState?: string; tenantId?: string; createdAt?: string }) => {
    const d = n.data ?? {}
    const requestId = String(d.requestId ?? '')
    const { orgConnectionsApi } = await import('@papan/org-connections/client')
    const stale = () => toast.info('This request is no longer active')
    // A SETTLED request-bound row offers its outcome's follow-on step instead of
    // the original ask — e.g. an approved reshare opens the folder rather than
    // re-opening the approval popup. Same registry call the bell renders from, so
    // the click can't disagree with the affordance. Terminal states resolve to no
    // action and never reach here (the row isn't clickable).
    const outcome = resolveNotificationOutcome(n.type, n.requestState, d)

    // ── Chat storage (#758) ───────────────────────────────────────────
    //
    // Three rows, one destination and one reply. `Adjust limit` and
    // `Manage chat storage` both land on Settings → Storage, where the
    // admin opens the member's own dialog; `Decline` is the row's SECOND
    // action and is the only one that writes.
    if (
      n.type === 'chat_storage_more_space' || n.type === 'chat_storage_raised' || n.type === 'chat_storage_declined'
      // #758 part 2. Same destination, and for these two it is the only
      // place the member can act: the booked one points at the days they
      // have to save or star something, and the after-the-fact one at the
      // figure that just moved. A declared action with no branch here
      // renders a live button that does nothing at all.
      || n.type === 'chat_storage_cleanup_scheduled' || n.type === 'chat_storage_cleaned'
    ) {
      // THE NOTIFICATION'S tenant, not the one the user happens to be in.
      //
      // The bell lists across every organisation the user belongs to, so an
      // admin of A and B reading a request made in B, while acting in A,
      // used to decline it against A — answering a question nobody asked
      // there, leaving B's request open forever, and navigating to A's
      // storage page, whose member list does not contain the asker. It even
      // said "They have been told" (full review 2, finding 2).
      //
      // THE FALLBACK IS NOT ONLY FOR OLD RESPONSES, which an earlier
      // version of this line claimed. The LIVE push has never carried
      // `tenantId`: `features/realtime/server/websocket.ts` sends
      // `event.notification` and drops `event.tenantId`, its sibling on
      // `NotificationEvent`. So a row that arrived by WS and has not yet
      // been repaired by the debounced refetch reaches here with no tenant
      // and gets routed to whichever one the user is acting in — the
      // cross-tenant bug this field exists to prevent (final full review,
      // A2).
      //
      // `NotificationBell` now stamps such a row non-actionable so it
      // cannot be clicked before the refetch lands. This fallback stays for
      // a genuinely old response, and the real fix is one additive field on
      // that WS frame — server-side, written up for Season.
      const nTenant = n.tenantId ?? tenantId
      if (n.type === 'chat_storage_more_space' && (n as { secondary?: boolean }).secondary) {
        const email = String(d.requesterEmail ?? '')
        if (!email) return
        try {
          const { storageApi } = await import('@papan/orgs/client')
          // An owner/admin address IN THAT TENANT, so the route authenticates
          // as the right membership. `getOrgEmail(auth)` would otherwise hand
          // it the email of whichever tenant the user is acting in, and a
          // person whose address differs between their two organisations
          // would get a 403 (scoped review of full review 2, finding 5).
          // Null when it IS the active tenant — no override needed.
          const asEmail = nTenant === tenantId
            ? null
            : (tenantsList.find(t => t.id === nTenant)?.emails
                .find(e => e.role === 'owner' || e.role === 'admin')?.email
              ?? tenantsList.find(t => t.id === nTenant)?.emails[0]?.email
              ?? null)
          const r = await storageApi.declineChatStorageRequest(nTenant, email, asEmail)
          // Someone else already answered — say so, rather than claiming a
          // reply that was not sent (the route refuses to send a second,
          // contradictory one). See full review 2, finding 3.
          // THREE OUTCOMES, not two. The route distinguishes them and this
          // reported the only one that did not happen for a departed
          // member: `{memberGone: true}` means the request was closed and
          // NOBODY was notified — there is no membership and no
          // notification row to write. Bob asks on Monday, leaves on
          // Tuesday, and on Wednesday an admin clears his stale amber row
          // from the bell and is told "They have been told" (final full
          // review, B2). The Storage page's own Decline already says
          // "Declined", which is at least true of the request.
          toast.success(r.alreadyAnswered
            ? 'Someone already answered that'
            : r.memberGone
              ? 'Request closed — they have left, so nobody was notified'
              : 'They have been told')
        } catch {
          // Said plainly: a decline that silently failed leaves the member
          // waiting on an answer the admin believes they gave.
          toast.error('Could not send that reply')
        }
        return
      }
      navigate(`${nTenant.startsWith('p_') ? '/~' : `/t/${nTenant}`}/settings/storage`)
      return
    }

    if (n.type === 'share_request_received') {
      // Actionable only while it's still a live incoming request.
      const { incoming } = await orgConnectionsApi.listIncoming().catch(() => ({ incoming: [] }))
      if (!incoming.some(r => r.requestId === requestId)) { stale(); return }
      setConnAction({
        kind: 'accept',
        requestId,
        fromEmail: String(d.fromEmail ?? ''),
        fromName: d.fromName ? String(d.fromName) : null,
        orgName: d.orgName ? String(d.orgName) : null,
        note: d.resendNote ? String(d.resendNote) : null,
        // Reshare referral: the popup leads with the referrer + folder instead
        // of "wants to connect" (the recipient never dealt with the owner).
        viaReshare: d.viaReshare === true,
        // Share-driven request: folder-first framing (the connection is
        // behind-the-scenes plumbing).
        shareDriven: d.shareDriven === true,
        referrerName: d.referrerName ? String(d.referrerName) : null,
        referrerEmail: d.referrerEmail ? String(d.referrerEmail) : null,
        declineTargetEmail: d.declineTargetEmail ? String(d.declineTargetEmail) : null,
        sharedName: d.sharedName ? String(d.sharedName) : null,
        // File-vs-folder framing for the popup heading/body (absent ⇒ folder).
        ...(typeof d.sharedIsFolder === 'boolean' ? { sharedIsFolder: d.sharedIsFolder } : {}),
        // Reshare target org — the popup pre-selects it in the "Receive as" picker.
        targetTenantId: d.targetTenantId ? String(d.targetTenantId) : null,
        targetTenantName: d.targetTenantName ? String(d.targetTenantName) : null,
      })
    } else if (n.type === 'connection_awaiting_confirm') {
      // The request belongs to the INITIATOR tenant, which may not be the
      // active one — the bell shows notifications across all the user's
      // tenants. listOutgoing() is active-tenant-scoped, so it would miss a
      // request that belongs to another tenant and show a misleading "no
      // longer active" toast. Look it up user-globally instead.
      const { pendingConfirmations } = await orgConnectionsApi.listPendingConfirmations().catch(() => ({ pendingConfirmations: [] }))
      const live = pendingConfirmations.find(r => r.requestId === requestId)
      if (!live) { stale(); return }
      // Confirm DIRECTLY, even when the request belongs to another tenant — the
      // popup sends the request's own tenant + an owner/admin email of it as
      // identity overrides, so the confirm (incl. a share_link_mount access
      // override, which the server gates on canManageConnection) acts in the
      // correct context without switching the user's active tenant.
      const initTenant = tenantsList.find(t => t.id === live.initiatorTenantId)
      const initEmail = initTenant?.emails.find(e => e.role === 'owner' || e.role === 'admin')?.email
        ?? initTenant?.emails[0]?.email ?? null
      setConnAction({
        kind: 'confirm',
        requestId,
        asTenantId: live.initiatorTenantId,
        asOrgEmail: initEmail,
        recipientEmail: String(d.recipientEmail ?? ''),
        recipientName: d.recipientName ? String(d.recipientName) : null,
        pickedTenantName: d.pickedTenantName ? String(d.pickedTenantName) : null,
        notifiedEmail: d.notifiedEmail ? String(d.notifiedEmail) : null,
        initiatorOrgName: d.initiatorOrgName ? String(d.initiatorOrgName) : null,
        source: d.source === 'share_link_mount' ? 'share_link_mount' : 'org_connection',
        linkPermission: live.grantedPermission ?? null,
        linkCap: live.linkCap ?? null,
        shareDriven: d.shareDriven === true,
        sharedName: d.sharedName ? String(d.sharedName) : null,
      })
    } else if (n.type === 'connection_made_by_member') {
      // Informational — open the Network tab so the admin can see the
      // connection in their list (no action to take, no staleness check).
      navigate(`${prefix}/settings/network`)
    } else if (n.type === 'budget_threshold') {
      // A spend or storage ceiling was crossed. Only the TENANT scopes carry an
      // action (see the registry) and those are delivered to owners/admins
      // only, so this always lands somewhere the reader can use.
      //
      // Open the tenant that OVERSPENT, not the active one — same reasoning as
      // the folder branch below. A person's notifications span every org they
      // belong to, so an admin of two orgs reading an alert about one while
      // working in the other would otherwise land on the wrong numbers, and
      // could adjust the wrong caps. Rows emitted before `budgetTenantId`
      // existed fall back to the active prefix.
      const alertTenantId = d.budgetTenantId ? String(d.budgetTenantId) : ''
      // `/~` only for the READER's own personal tenant (#853) — the rule the folder
      // branch below uses; any other tenant is named and gated as the reader.
      const budgetPrefix = alertTenantId
        ? readerTenantPathPrefix(alertTenantId, personalTenantId)
        : prefix
      // The STORAGE scope goes to Settings → Storage, not Spending. Both are
      // `budget_threshold` rows and the registry gives them one action, but they
      // describe different axes: `/settings/budget` renders AI spend and — since
      // the money page stopped drawing a storage bar — carries no storage figure
      // at all. Sending a "you have used 80% of your storage" alert there landed
      // the reader on a screen that could not show the thing it named, with no
      // error to explain why. Storage's own page has the quota, the composition
      // and the reclaim actions, which is the whole of what that alert is for.
      navigate(d.budgetScope === 'storage'
        ? `${budgetPrefix}/settings/storage`
        : `${budgetPrefix}/settings/budget`)
    } else if (n.type === 'usage_credits_low') {
      // Settings → BILLING, not Spending: the alert's own body says "top up on
      // the Billing page", and buying credit is `manage_billing` where reading
      // spend is `manage_budget`. It had no destination at all until now, so
      // the one warning between a working org and a hard stop named its fix
      // and did not offer it.
      //
      // Same wrong-org guard as the budget alert above, and it matters more
      // here: an admin of two orgs who tops up the one they happen to be
      // looking at has spent real money on the wrong tenant. Emitted only to
      // owners/admins, so the destination is always usable.
      const creditTenantId = d.budgetTenantId ? String(d.budgetTenantId) : ''
      const creditPrefix = creditTenantId
        ? readerTenantPathPrefix(creditTenantId, personalTenantId)
        : prefix
      navigate(`${creditPrefix}/settings/subscription`)
    } else if (n.type === 'share_link_from_connection') {
      // Review a link a connected app (an MCP client) created as this person (review fixes §3.4): a dialog shows who
      // made it, when, through which connection and who can open it, with Keep / Revoke — never a revoke on click.
      // In the tenant that owns it — the bell shows every tenant's rows.
      const linkId = String(d.shareLinkId ?? '')
      if (!linkId) return
      setShareLinkReview({
        linkId,
        tenantId: d.shareLinkTenantId ? String(d.shareLinkTenantId) : null,
        orgEmail: d.shareLinkOrgEmail ? String(d.shareLinkOrgEmail) : null,
        targetName: d.linkTargetName ? String(d.linkTargetName) : null,
        connectionName: d.connectionName ? String(d.connectionName) : null,
        notifiedAt: n.createdAt ?? null,
      })
    } else if (describeNotification(n.type, d).action?.route === 'view-folder'
               || outcome?.action?.route === 'view-folder') {
      // Dispatched on the RESOLVED ROUTE, not on an enumerated type list — and
      // `describeNotification` is deliberately the same call `NotificationBell`
      // uses to decide the row is clickable at all (`!!desc.action`). That makes
      // this one invariant rather than two lists that must agree:
      //
      //     if the bell rendered it clickable with a view-folder action,
      //     this handler handles it.
      //
      // The list version could not state that, and drifted: `folder_access_granted`
      // (intra-org "you were added to a folder", #557) declared the action, was
      // rendered with a live "View folder" button, and was absent here — so the
      // click closed the dropdown and did nothing. Three of the four registry
      // entries declaring this route were named in the list this condition
      // replaces; nothing structural distinguished the fourth, and nothing
      // typechecks the relationship, so it
      // was invisible to the type system and to the whole suite. `outcome` could
      // not rescue it either: `resolveNotificationOutcome` returns undefined
      // without a `requestState`, and this row is informational.
      //
      // ONLY `view-folder` is dispatched this way, and that is the whole point of
      // the split. Every other branch does more than navigate — it re-validates
      // the request against the live inbox, resolves a cross-tenant identity, or
      // opens a popup — so its behaviour is NOT derivable from the route and must
      // stay keyed on the type. This branch is pure data-driven navigation.
      //
      // Position is load-bearing: it is an if/else-if chain, so a type with an
      // earlier popup branch still takes that branch. Moving this above them
      // would swallow the popup of any type that has both.
      //
      // "View folder" — open the shared folder DIRECTLY, even when it lives in a
      // DIFFERENT tenant than the active one (a user's notifications span all
      // their tenants). Navigate to the folder's OWN-tenant URL; the SPA switches
      // the active tenant from the route, so one click opens it in-context.
      const folderId = d.sharedFolderId ? String(d.sharedFolderId) : ''
      const folderTenantId = d.sharedFolderTenantId ? String(d.sharedFolderTenantId) : ''
      // Prefer the folder's OWN tenant; when the notification doesn't carry one
      // (older rows, connection-only establishes) fall back to the ACTIVE tenant
      // prefix — NOT `/~`, which would open an org folder at a personal-drive URL
      // that 404s.
      const base = !folderTenantId ? prefix
        : folderTenantId === personalTenantId ? '/~'
        : `/t/${folderTenantId}`
      // A FILE share must not navigate INTO the pointer (a file-target
      // `.shared` "opened as a folder" is an empty listing) — REVEAL it in
      // its parent instead (`?reveal=` selects + scrolls). The parent is
      // stamped at emit; a root-level parent (`papan_<tenant>` sentinel) or
      // a missing stamp falls back to the id-less /folder route. Folder
      // shares (and legacy rows without the stamp) keep navigate-into.
      if (d.sharedIsFolder === false && folderId) {
        const parentId = d.sharedParentFolderId ? String(d.sharedParentFolderId) : ''
        const parentSeg = parentId && !parentId.startsWith('papan_') ? `/folder/${parentId}` : '/folder'
        navigate(`${base}${parentSeg}?reveal=${folderId}`)
      } else {
        navigate(folderId ? `${base}/folder/${folderId}` : `${base}/folder`)
      }
    } else if (n.type === 'reshare_awaiting_source_approval') {
      // Approve DIRECTLY, even cross-tenant/-identity. The owner approves AS an
      // owner/admin of their org, which may not be the active identity. Resolve
      // the referral user-globally (every tenant the user owns/admins via ANY
      // email), pick an owner/admin email for that owner tenant, and pass BOTH as
      // identity overrides so the popup fetches + approves as that identity — no
      // tenant switch, no "switch first" prompt.
      const pending = await orgConnectionsApi.listReshareReferralsPending().catch(() => ({ referrals: [] }))
      const live = pending.referrals.find(r => r.requestId === requestId)
      if (!live) { stale(); return }
      const ownerTenant = tenantsList.find(t => t.id === live.ownerTenantId)
      const ownerEmail = ownerTenant?.emails.find(e => e.role === 'owner' || e.role === 'admin')?.email
        ?? ownerTenant?.emails[0]?.email ?? null
      setConnAction({
        kind: 'reshare-approve',
        requestId,
        referrerName: d.referrerName ? String(d.referrerName) : null,
        recipientEmail: d.recipientEmail ? String(d.recipientEmail) : null,
        asTenantId: live.ownerTenantId,
        asOrgEmail: ownerEmail,
      })
    } else if (n.type === 'connection_declined') {
      // Dead-end fix: jump to the Network tab to send a fresh request.
      navigate(`${prefix}/settings/network`)
    } else if (n.type === 'reshare_send_failed') {
      // Reshare failed post-connect — point them at the drive to share again.
      navigate(`${prefix}/folder`)
      toast.info('Open the file you want to share and try again')
    }
  }, [navigate, prefix, toast, tenantId, tenantsList])

  // Drive picker for member access dialog
  const [drivePickerOpen, setDrivePickerOpen] = useState(false)
  const drivePickerResolveRef = useRef<((result: { id: string; isFolder: boolean } | null) => void) | null>(null)
  const handlePickFile = useCallback((): Promise<{ id: string; isFolder: boolean } | null> => {
    return new Promise(resolve => {
      drivePickerResolveRef.current = resolve
      setDrivePickerOpen(true)
    })
  }, [])

  // Derive workspace state from URL (supports both /~/ and /t/:slug/ patterns)
  const isWorkspaceView = WORKSPACE_PATH_RE.test(location.pathname)
  const wsMatch = location.pathname.match(/(?:\/t\/[^/]+|\/~)\/workspace\/([^/?]+)/)
  const currentWorkspaceId = wsMatch?.[1] || null

  // ── OS-shell app deck state ────────────────────────────────────────
  // Which taskbar app owns the current URL (null = a non-deck route like
  // /file/:id). Drives BOTH the RouteSync below (mark app running) and
  // which keep-alive deck window is visible. `classifyDeck` is the ONE
  // pathname classifier — the hydration guard below reuses it so a persisted
  // deck route that no longer classifies as its deck cannot mis-seed the
  // frozen location (#504). See apps/web/src/lib/deckKind.ts for why it is
  // matched on the FIRST segment, not by substring.
  const deckKind: DeckKind | null = classifyDeck(location.pathname)

  // The forgot-password modal renders inside the settings deck's subtree but its
  // flag lives up here, so the two can come apart. `inert` blocks clicks, not
  // history: Back/Forward while it is open switches decks and leaves the dialog
  // portalled over whatever is showing now, and a back target in another tenant
  // unmounts the deck (and the modal) while the flag stays `true` — so it
  // reappears unbidden the next time Settings is opened. Closing it when the
  // deck stops being Settings covers both. Guarded on the VALUE, not a mounted
  // ref, because <StrictMode> double-invokes effects in dev. useLayoutEffect
  // rather than useEffect: a passive effect leaves one painted frame in which
  // the dialog still covers the newly-shown deck with the app root inert.
  useLayoutEffect(() => {
    if (deckKind !== 'settings') setForgotPwOpen(false)
  }, [deckKind])
  const isDriveView = deckKind === 'drive'

  // Standalone user app (mini-app / HTML run as a top-level window, Phase 2)
  // — /~/app/:fileId owns the URL. deckKind is null on these paths, so all
  // system windows hide and the matching user-app window shows.
  const appMatch = location.pathname.match(/(?:\/t\/[^/]+|\/~)\/app\/([^/?]+)/)
  const activeUserRef = appMatch?.[1] ?? null

  // Frozen locations: each deck window renders against the LAST location it
  // owned, so its inner <Routes location={...}> keeps matching (and
  // useParams keeps resolving) while the window is hidden — the tree never
  // remounts on app switch. Updated during render, same trick as the old
  // lastDrivePathRef (which this subsumes: the taskbar drive button
  // navigates back to the frozen path).
  const driveLocRef = useRef<{ pathname: string; search: string } | null>(null)
  if (deckKind === 'drive') driveLocRef.current = location
  const settingsLocRef = useRef<{ pathname: string; search: string } | null>(null)
  if (deckKind === 'settings') settingsLocRef.current = location
  const billingLocRef = useRef<{ pathname: string; search: string } | null>(null)
  if (deckKind === 'billing') billingLocRef.current = location
  // Per-instance frozen locations for user apps — the URL splat after
  // /app/:fileId/ tracks the app's in-app navigation, so the dock button
  // and reload restore the exact page.
  const userAppLocsRef = useRef<Record<string, { pathname: string; search: string; hash?: string }>>({})
  if (activeUserRef) userAppLocsRef.current[activeUserRef] = location

  // A tenant switch drops every deck's tenant-scoped content (the effect
  // further down). But the deck windows are keyed on tenantId, so they REMOUNT
  // on the render where tenantId flips. If we relied only on that effect to
  // null the frozen locations, the remount would happen against the PREVIOUS
  // tenant's frozen route FIRST (the effect runs after this commit's mount
  // effects). For drive that means a fresh DriveView mounts against the old
  // tenant's `/folder/<id>` and fires a breadcrumb fetch under the NEW
  // identity — the new tenant has no such folder, so it bounces with a
  // spurious "This folder is no longer available." (Repro: switch tenant while
  // inside a named folder like Library; drive root is immune since it has no
  // folder id.) Drop the stale frozen locations for the now-inactive decks
  // synchronously here — before the render below uses them to remount — so the
  // window simply doesn't render this tick instead of remounting on a dead id.
  const renderPrevTenantRef = useRef(tenantId)
  if (renderPrevTenantRef.current !== tenantId) {
    renderPrevTenantRef.current = tenantId
    if (deckKind !== 'drive') driveLocRef.current = null
    if (deckKind !== 'settings') settingsLocRef.current = null
    if (deckKind !== 'billing') billingLocRef.current = null
  }

  // RouteSync: visiting an app's URL marks it running (idempotent) — the
  // deck mounts its window and keeps it mounted after navigating away.
  const shell = useShell()
  const { openApp, resetApps, hydrateDock } = shell
  dockIdentityRef.current = (activeOrgEmail ?? user?.email ?? '').toLowerCase()
  useEffect(() => {
    // 0179: a stale /keybook URL on a keybook-disabled deployment shows the
    // notice but must NOT register (and persist) a running keybook app — the
    // taskbar item is hidden, so there would be no way to close it, and the
    // phantom entry would remount the deck at every boot once re-enabled.
    if (deckKind === 'keybook' && !keybookEnabled) return
    // Same for a /chat URL where chat is off (#368): it is redirected home a
    // moment later, and must not leave a running chat app behind.
    if (deckKind === 'chat' && !chatOn) return
    if (deckKind) openApp(deckKind)
  }, [deckKind, openApp, keybookEnabled, chatOn])

  // RouteSync for user apps: a cold /app/:fileId visit (deep link, "Open as
  // app") resolves the file to decide the kind — folder = miniapp, file =
  // html (AppWindow validates the rest) — then registers + persists it.
  // Hydration-restored instances are already running and skip this.
  // #538 — PDApp windows load like browser tabs after a restart: every running app keeps its dock entry,
  // but a window mounts (and starts its page, session, sandbox calls) only once it has been on screen in
  // this page load; from then on it stays alive behind the others as before. A refresh used to start
  // every restored window at once (six windows ≈ 100 requests) while only one was visible.
  const [shownApps, setShownApps] = useState<ReadonlySet<string>>(() => new Set())
  useEffect(() => {
    if (activeUserRef && !shownApps.has(activeUserRef)) setShownApps((prev) => new Set(prev).add(activeUserRef))
  }, [activeUserRef, shownApps])
  // …so a window not yet opened can't name its dock entry: ask for just its row (name + icon) once —
  // one small read per restored app, never its page. Only for entries that carry no name yet.
  const askedNamesRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    for (const a of shell.runningApps) {
      if ((a.kind !== 'miniapp' && a.kind !== 'html') || !a.ref || (a.title && a.title !== '…') || askedNamesRef.current.has(a.ref)) continue
      askedNamesRef.current.add(a.ref)
      const { kind, ref } = a
      const askedIn = tenantId
      filesApi.getFileInfo(ref).then((info) => {
        // A late answer for another tenant, or for an app closed meanwhile, names nothing (and `openApp` would
        // ADD it back to the dock).
        if (askedIn !== prevTenantRef.current || !runningAppsRef.current.some((x) => x.kind === kind && x.ref === ref)) return
        const md = (info as { metadataJson?: string | null }).metadataJson
        if (kind === 'miniapp') {
          if (!presentsAsMiniapp(info.name, info.fileType, info.isFolder)) return
          shell.openApp(kind, ref, appDisplayName(stripPointerSuffix(info.name)), parseMiniappIcon(md), parseMiniappIconRev(md))
        } else {
          let title = appDisplayName(info.name)
          // As the window titles it: "index" says nothing — its folder's name instead.
          if (title.toLowerCase() === 'index') {
            const segs = ((info as { path?: string }).path ?? '').split('/').filter(Boolean)
            if (segs.length >= 2) title = segs[segs.length - 2]
          }
          shell.openApp(kind, ref, title)
        }
      }).catch(() => { askedNamesRef.current.delete(ref) }) // asked again on the dock's next change
    }
  }, [shell.runningApps, shell.openApp, tenantId])
  const runningAppsRef = useRef(shell.runningApps)
  runningAppsRef.current = shell.runningApps
  useEffect(() => {
    if (!activeUserRef) return
    if (runningAppsRef.current.some(a => (a.kind === 'miniapp' || a.kind === 'html') && a.ref === activeUserRef)) return
    let cancelled = false
    filesApi.getFileInfo(activeUserRef).then(info => {
      if (cancelled) return
      // A folder is only a runnable app when it's actually a `.miniapp`. The
      // workspace-shell folder minted by "open .miniapp as workspace" is a
      // `.project` folder (`<App>.miniapp.project`, id `wsi_<hash>_<id>`) — NOT
      // a miniapp — so classifying it as one would pin a broken app window in
      // the dock. Bounce it to its real surface instead: the workspace view for
      // a `.project`/workspace shell, the folder view for any other plain folder.
      // (#327) A `.shared` mount of someone else's `.miniapp` is runnable too —
      // `presentsAsMiniapp` pairs the inner name with the row kind, so the mount
      // launches while a plain folder (or a hand-made `X.miniapp.shared`) still
      // bounces to its real surface.
      const runsAsMiniapp = presentsAsMiniapp(info.name, info.fileType, info.isFolder)
      if (info.isFolder && !runsAsMiniapp) {
        // Project routing stays keyed on the RAW name: a `.project.shared`
        // mount has no mount-rooted workspace to open yet (#327 Track B), so
        // it must keep falling through to the folder view rather than land on
        // a workspace route that can't resolve a pointer id.
        navigate(
          isProjectFolder(info.name)
            ? `${prefix}/workspace/${activeUserRef}`
            : `${prefix}/folder/${activeUserRef}`,
          { replace: true },
        )
        return
      }
      // (#327) NOT `info.isFolder` — a `.shared` pointer is a FILE row (only the
      // listing lifts `isFolder`, the detail endpoint returns the raw false), so
      // keying on it classified a mounted app as loose HTML and the window died
      // with "this item is not an HTML page".
      const kind = runsAsMiniapp || info.isFolder ? ('miniapp' as const) : ('html' as const)
      const md = (info as { metadataJson?: string | null }).metadataJson
      const icon = kind === 'miniapp' ? parseMiniappIcon(md) : undefined
      // The rev rides with the spec — the dock chip can't render an IMAGE icon without it.
      // Strip the pointer suffix first — otherwise a mounted app shows in the
      // dock as "MyApp.miniapp.shared" (appDisplayName only knows the inner exts).
      openApp(kind, activeUserRef, appDisplayName(stripPointerSuffix(info.name)), icon, kind === 'miniapp' ? parseMiniappIconRev(md) : null)
      void persistShellOpen(kind, activeUserRef, `${prefix}/app/${activeUserRef}`).catch(() => {})
    }).catch(() => {})
    return () => { cancelled = true }
  }, [activeUserRef, openApp, prefix, navigate])

  // "Recently opened" for the PDApps window: each time a PDApp becomes the
  // window on screen — from the dock, the PDApps window, the drive, a link —
  // once per arrival, not per in-app page. Waits for the instance to register
  // (its kind is known only then), so a loose HTML file is never recorded.
  // The server checks the caller can read the app before it stores the open.
  const recordedOpenRef = useRef<string | null>(null)
  useEffect(() => {
    if (!activeUserRef) { recordedOpenRef.current = null; return }
    const key = `${tenantId}:${activeOrgEmail ?? ''}:${activeUserRef}`
    if (recordedOpenRef.current === key) return
    if (!shell.runningApps.some(a => a.kind === 'miniapp' && a.ref === activeUserRef)) return
    recordedOpenRef.current = key
    recordAppOpen(queryClient, activeUserRef)
  }, [activeUserRef, shell.runningApps, tenantId, activeOrgEmail, queryClient])

  // Server persistence: hydrate the running set (background apps survive
  // reload) + write-through the active app's route. Restored routes seed the
  // frozen locations below so restored windows open on their last page.
  const restoredShell = useShellPersistence({
    tenantId,
    isReady,
    activeOrgEmail,
    deckKind,
    pathname: location.pathname,
    search: location.search,
    dockRevision,
  })
  if (restoredShell?.tenantId === tenantId) {
    const parseRoute = (r: string): { pathname: string; search: string } => {
      const q = r.indexOf('?')
      return q === -1 ? { pathname: r, search: '' } : { pathname: r.slice(0, q), search: r.slice(q) }
    }
    // Seed each deck's frozen location from its persisted route ONLY when the
    // route still classifies as that deck. A durable per-identity row can
    // outlive the route it named — the drive PDApps page moved from
    // `/~/miniapps` to `/~/pdapps` with no redirect, so a pre-rename row seeded
    // driveLocRef with a dead path; clicking Drive then navigated there and the
    // `*` catch-all bounced it to the launcher landing ("Drive opens Launcher",
    // #504). `restoredDeckRoute` returns null on a mismatch, leaving the ref
    // null so the taskbar button falls back to the deck's default entry.
    if (!driveLocRef.current && restoredShell.routes.drive) {
      driveLocRef.current = restoredDeckRoute('drive', restoredShell.routes.drive)
    }
    if (!settingsLocRef.current && restoredShell.routes.settings) {
      settingsLocRef.current = restoredDeckRoute('settings', restoredShell.routes.settings)
    }
    // User-app routes (miniapp/html) are not decks — they keep the raw parse.
    for (const [ref, route] of Object.entries(restoredShell.userRoutes)) {
      if (!userAppLocsRef.current[ref]) userAppLocsRef.current[ref] = parseRoute(route)
    }
  }

  // Tenant switch: every deck window's content and frozen location is
  // tenant-scoped — drop them all except the app the URL now shows.
  const deckKindRef = useRef(deckKind)
  deckKindRef.current = deckKind
  const prevTenantRef = useRef(tenantId)
  useEffect(() => {
    if (prevTenantRef.current === tenantId) return
    prevTenantRef.current = tenantId
    if (deckKindRef.current !== 'drive') driveLocRef.current = null
    if (deckKindRef.current !== 'settings') settingsLocRef.current = null
    if (deckKindRef.current !== 'billing') billingLocRef.current = null
    userAppLocsRef.current = {}
    // #538 — the other tenant's windows start over: none loaded until shown, names asked again.
    setShownApps(new Set())
    askedNamesRef.current = new Set()
    resetApps(deckKindRef.current ? { kind: deckKindRef.current, ref: '' } : null)
    // Pins are per-identity too; clear so the old tenant's dock entries don't
    // linger until hydration re-fetches the new identity's pins.
    hydrateDock([], [])
  }, [tenantId, resetApps, hydrateDock])

  // Auto-register workspace when navigating to an unregistered workspace URL
  // (e.g. from DriveView "Open in Project", or bookmarks)
  // Also sync active workspace when navigating to an already-open workspace
  // Uses a ref for openWorkspaces to avoid re-firing when the list changes (e.g. on close)
  const openWorkspacesLatestRef = useRef(openWorkspaces)
  openWorkspacesLatestRef.current = openWorkspaces
  // #1316 review round 1: a resolve dropped by a same-person session end is asked again as the session now here
  const [wsResolveRetry, setWsResolveRetry] = useState(0)
  useEffect(() => {
    if (!currentWorkspaceId || !wsLoaded || !isReady) return
    const alreadyOpen = openWorkspacesLatestRef.current.find(w => w.folderId === currentWorkspaceId)
    if (alreadyOpen) {
      wsManager.setActiveWorkspace(currentWorkspaceId)
      return
    }
    // Not in list — load config and register (e.g. navigated via bookmark or DriveView)
    // Retract any earlier failure first: the mark means "the last attempt
    // failed", and one is starting now. Without this a workspace that was gone
    // and has since been restored shows the terminal not-found copy for the
    // length of this round trip.
    wsManager.clearUnresolved(currentWorkspaceId)
    // Every async callback below is guarded by `stale`. Without it, attempts
    // INTERLEAVE: a slow attempt A stays in flight while the user navigates away
    // and back, B starts and clears the mark, then A's catch marks the workspace
    // unresolved — so the terminal "may have been deleted" copy shows for the
    // length of B's round trip. A stale A could also register a workspace, or
    // `navigate`, after the user had left it.
    let stale = false
    // #1316 review round 1 (D-M2): an answer dropped because its session ended is NOT "no config" — never the
    // create-or-open WRITE below (it would go out as the next identity) nor a "may have been deleted" mark. The resolve
    // is asked again from the start in the session now here (an identity / tenant switch); after an account end the
    // page reloads, so nothing is asked again then.
    const start = currentSessionSignal()
    const account = accountEpoch()
    const endedMidResolve = (err: unknown): boolean => {
      if (!sessionEndedSince(start, err)) return false
      if (accountEpoch() === account) setWsResolveRetry((n) => n + 1)
      return true
    }
    workspaceApi.loadConfig(currentWorkspaceId).then(config => {
      if (stale) return
      wsManager.openWorkspace({ id: config.id, folderId: config.folderId, name: config.name })
    }).catch((err: unknown) => {
      if (stale || endedMidResolve(err)) return
      // No config → the workspace may not EXIST yet. The wrench / mini-app
      // edit-settings navigate straight to /workspace/<folderId> WITHOUT creating
      // it, and loadConfig only READS — so re-reading just loops on the 404 and the
      // view sticks on "Project not found" (#273). CREATE-or-open it instead: a
      // `.miniapp`/`.project` opens as its own workspace (the route derives the name
      // from the folder), a plain folder is wrapped. If the resolved workspace lives
      // under a different id than the URL, redirect so the route matches.
      workspaceApi.openFolder(currentWorkspaceId, '').then(({ workspace }) => {
        if (stale) return
        wsManager.openWorkspace({ id: workspace.id, folderId: workspace.folderId, name: workspace.name })
        if (workspace.folderId && workspace.folderId !== currentWorkspaceId) {
          navigate(`${prefix}/workspace/${workspace.folderId}`, { replace: true })
        }
      }).catch((err: unknown) => {
        // Both steps failed. Which failure it was decides the message (#675).
        // openFolder is the create-or-open AUTHORITY and has already exhausted
        // its own transient retries by now, so:
        //   - a DEFINITIVE negative (403 — genuinely gone or inaccessible) is
        //     the only case that earns the terminal "may have been deleted or
        //     moved to bin" copy;
        //   - a TRANSIENT the retries could not outlast (5xx / network / stall)
        //     is NOT evidence the project is gone — surface the recoverable
        //     "Couldn't load / Retry" state instead, whose Retry re-fires this
        //     resolver. Before #675 both landed on the terminal copy, so an
        //     intermittent blip told the user their work "may have been deleted"
        //     and stuck there for the navigation.
        if (stale || endedMidResolve(err)) return
        if (isDefinitiveResolveFailure(err)) {
          // #1406 I3: refused AS A PROJECT is not "gone" — a hallway project, or one whose config
          // the person can't read, is a plain folder to them (owner, 2026-10-26). Whatever sent
          // them here (a drive row, an app's Edit button, a bookmark), browse it as the drive does.
          const refusedId = currentWorkspaceId
          void opensAsPlainFolder(refusedId).then((plain) => {
            if (stale) return
            if (plain) navigate(`${prefix}/folder/${refusedId}`, { replace: true })
            else wsManager.markUnresolved(refusedId)
          })
        } else {
          wsManager.markLoadFailed(currentWorkspaceId)
        }
      })
    })
    return () => { stale = true }
  }, [currentWorkspaceId, wsLoaded, isReady, wsResolveRetry]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-close workspace when its .workpapan folder (or ancestor) is deleted
  useEffect(() => {
    return onFileEvent((event) => {
      if (event.type === 'file:deleted') {
        // Refresh workspace list from server — handles both direct and ancestor deletes
        wsManager.refreshWorkspaces()

        // Navigate away if current workspace was removed
        const currentWs = location.pathname.match(/\/workspace\/([^/?]+)/)?.[1]
        if (currentWs) {
          // Small delay to let refreshWorkspaces update state
          setTimeout(() => {
            workspaceApi.listOpen().then(serverOpen => {
              const serverIds = new Set(serverOpen.map(w => w.folderId))
              if (!serverIds.has(currentWs)) {
                if (serverOpen.length > 0) {
                  navigate(`${prefix}/workspace/${serverOpen[0].folderId}`)
                } else {
                  navigate(`${prefix}/folder`)
                }
              }
            }).catch(() => {})
          }, 100)
        }
      }
    })
  }, [onFileEvent, wsManager, navigate, prefix, location.pathname])

  const handleViewChange = useCallback((view: View) => {
    switch (view) {
      case 'drive':
        navigate(driveLocRef.current ? driveLocRef.current.pathname + driveLocRef.current.search : `${prefix}/home`)
        break
      case 'bin':
        navigate(`${prefix}/bin`)
        break
      case 'quarantine':
        navigate(`${prefix}/quarantine`)
        break
      case 'workspace':
        // Re-open last active workspace (if it belongs to current tenant), or empty workspace view
        if (activeWorkspaceId && openWorkspaces.some(w => w.folderId === activeWorkspaceId)) {
          navigate(`${prefix}/workspace/${activeWorkspaceId}`)
        } else if (openWorkspaces.length > 0) {
          navigate(`${prefix}/workspace/${openWorkspaces[0].folderId}`)
        } else {
          navigate(`${prefix}/workspace`)
        }
        break
      case 'chat':
        navigate(`${prefix}/chat`)
        break
      case 'keybook':
        navigate(`${prefix}/keybook`)
        break
      case 'settings':
        navigate(settingsLocRef.current?.pathname ?? `${prefix}/settings`)
        break
      case 'billing':
        navigate(billingLocRef.current?.pathname ?? `${prefix}/billing`)
        break
      // No frozen-location ref: the launcher is ONE page with no sub-routes, so
      // there is no "last owned" location to restore — same as chat.
      case 'launcher':
        navigate(`${prefix}/launcher`)
        break
      // One page too, like the launcher.
      case 'apps':
        navigate(`${prefix}/apps`)
        break
    }
  }, [navigate, prefix, activeWorkspaceId, openWorkspaces])

  // "Open in new tab" for a system app — pop it into its own browser tab at
  // its deep-linkable route (uses the same frozen locations as in-shell nav).
  const handleOpenViewNewTab = useCallback((view: View) => {
    let path: string
    switch (view) {
      case 'drive': path = driveLocRef.current ? driveLocRef.current.pathname + driveLocRef.current.search : `${prefix}/home`; break
      case 'workspace':
        path = activeWorkspaceId && openWorkspaces.some(w => w.folderId === activeWorkspaceId)
          ? `${prefix}/workspace/${activeWorkspaceId}`
          : openWorkspaces.length > 0 ? `${prefix}/workspace/${openWorkspaces[0].folderId}` : `${prefix}/workspace`
        break
      case 'chat': path = `${prefix}/chat`; break
      case 'keybook': path = `${prefix}/keybook`; break
      case 'settings': path = settingsLocRef.current?.pathname ?? `${prefix}/settings`; break
      case 'billing': path = billingLocRef.current?.pathname ?? `${prefix}/billing`; break
      case 'launcher': path = `${prefix}/launcher`; break
      case 'apps': path = `${prefix}/apps`; break
      default: path = `${prefix}/home`
    }
    window.open(path, '_blank', 'noopener')
  }, [prefix, activeWorkspaceId, openWorkspaces])

  // #1316: `signOut` itself loads the sign-in page once the server cleared the cookie (the ONE account-end reload,
  // `reloadAfterAccountEnd`); a sign-out the server refused leaves through the login guard below (clean /login, #501).
  const handleSignOut = useCallback(async () => {
    await signOut()
  }, [signOut])

  // Taskbar running indicators — the shell's running set, unioned with the
  // workspace manager's own open list (workspaces keep their existing
  // server-persisted running-state; the shell doesn't duplicate it).
  const runningViews = useMemo<View[]>(() => {
    const views = shell.runningApps.filter(a => a.ref === '').map(a => a.kind as View)
    if (!views.includes('workspace') && openWorkspaces.length > 0) views.push('workspace')
    return views
  }, [shell.runningApps, openWorkspaces.length])

  // The app well, in dock order: pins and folders by pinOrder, then running-
  // but-unpinned apps in open order. The composition lives in core-ui
  // (rail/composeRailEntries.ts); this file only decorates what it cannot
  // know — the per-app "Edit icon" permission below.
  const railEntriesBase = useMemo(
    () => composeRailEntries({ running: shell.runningApps, pins: shell.pinnedApps, folders: shell.folders, activeRef: activeUserRef }),
    [shell.runningApps, shell.pinnedApps, shell.folders, activeUserRef],
  )

  // The "Edit icon" dock action is write-gated (server: requireMiniappManage).
  // Resolve each mini-app's effective permission reactively — usually a cache
  // hit (an open app seeds fileKeys.permission), a fetch for pinned-idle apps.
  // Fails closed: the item stays hidden until a write perm is confirmed.
  const miniappRefs = useMemo(
    () => railApps(railEntriesBase).filter(a => a.kind === 'miniapp').map(a => a.ref),
    [railEntriesBase],
  )
  const miniappPerms = useQueries({
    queries: miniappRefs.map(ref => permissionQueryOptions(queryClient, ref)),
  })
  const canEditByRef = useMemo(() => {
    const map: Record<string, boolean> = {}
    miniappRefs.forEach((ref, i) => {
      const perm = miniappPerms[i]?.data
      map[ref] = perm ? ['readwrite', 'admin', 'owner'].includes(perm) : false
    })
    return map
  }, [miniappRefs, miniappPerms])

  // Apps whose queued database writes failed (a trigger's import that never landed…): a dot on the tile, for the apps
  // the viewer can EDIT only (the server answers no one else). Polled — a failure happens after a run, unattended.
  const editableMiniappRefs = useMemo(() => miniappRefs.filter((ref) => canEditByRef[ref]).sort(), [miniappRefs, canEditByRef])
  const writeFailures = useQuery({
    queryKey: appWriteFailureKeys.counts(editableMiniappRefs),
    queryFn: () => appRunLogApi.writeFailureCounts(editableMiniappRefs),
    enabled: editableMiniappRefs.length > 0,
    staleTime: 30_000,
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  })

  const railEntries = useMemo<RailEntry[]>(() => {
    const failures = writeFailures.data ?? {}
    const decorate = (a: RailApp): RailApp => ({ ...a, canEdit: a.kind === 'miniapp' ? (canEditByRef[a.ref] ?? false) : false, writeFailures: a.kind === 'miniapp' ? failures[a.ref] : undefined, asleep: a.running && !a.active && (a.kind === 'miniapp' || a.kind === 'html') && !shownApps.has(a.ref) })
    return railEntriesBase.map(e => (e.type === 'folder' ? { ...e, members: e.members.map(decorate) } : decorate(e)))
  }, [railEntriesBase, canEditByRef, writeFailures.data, shownApps])

  const handleOpenUserApp = useCallback((ref: string) => {
    // Restore the exact in-app page (frozen splat), not the app's base.
    const frozen = userAppLocsRef.current[ref]
    navigate(frozen ? frozen.pathname + frozen.search : `${prefix}/app/${ref}`)
  }, [navigate, prefix])

  // "Open in new tab" — pop the app out into its own browser tab at its
  // deep-linkable route (the SPA resolves a cold /app/:ref visit standalone).
  // Signature is (kind, ref) to match TaskBar's call; kind is unused (the
  // route is /app/:ref for both miniapp and html) but must be accepted so
  // `ref` isn't the kind string (that bug opened /app/miniapp).
  const handleOpenUserAppNewTab = useCallback((_kind: 'miniapp' | 'html', ref: string) => {
    const frozen = userAppLocsRef.current[ref]
    window.open(frozen ? frozen.pathname + frozen.search : `${prefix}/app/${ref}`, '_blank', 'noopener')
  }, [prefix])

  // Pin/unpin apply locally first, then persist. #341 — the persist result used
  // to be discarded (`.catch(() => {})`), so a failed write left the UI showing
  // a pin the server never stored: it looked like it worked and silently
  // evaporated on the next reload. Roll the local state back instead, so what's
  // on screen keeps matching what will survive a refresh.
  const handlePinUserApp = useCallback((kind: 'miniapp' | 'html', ref: string, title?: string) => {
    shell.pinApp(kind, ref, title)
    void pinShellApp(kind, ref, title).catch(() => {
      shell.unpinApp(kind, ref)
      toast.error(tCore('taskBar.toast.pinFailed'))
    })
  }, [shell, toast, tCore])

  const handleUnpinUserApp = useCallback((kind: 'miniapp' | 'html', ref: string) => {
    // Capture the pin BEFORE dropping it so a failed persist restores the exact
    // entry (title included) instead of a degraded one. Keeps the signature the
    // GC path and the dock's toggle already call with.
    const previous = shell.pinnedApps.find(p => p.kind === kind && p.ref === ref)
    shell.unpinApp(kind, ref)
    void unpinShellApp(kind, ref).catch(() => {
      shell.pinApp(kind, ref, previous?.title)
      toast.error(tCore('taskBar.toast.unpinFailed'))
    })
  }, [shell, toast, tCore])

  // ── Folders (0195): local mirror first, then the server write ─────────
  // Every folder/rail write is fire-and-forget like the pin writes above, but
  // NOT silent: a failed persist leaves the tab showing a dock the server
  // does not hold, which reverts on reload — so it says so once.
  const persistDock = useCallback((p: Promise<unknown>) => {
    void p.catch(() => { toast.warning(tCoreUi('taskBar.persistFailed')) })
  }, [toast, tCoreUi])
  // The rail's top-level order right now, as keys — captured before a move so
  // its Undo can put the app back in the slot it came from.
  const railKeysRef = useRef<string[]>([])
  railKeysRef.current = railEntriesBase.map(e => (e.type === 'folder' ? railKeyOfFolder(e) : railKeyOfPin(e)))
  /** The SLOTTED keys only — folders and PINNED apps, in rail order.
   *  `composeRailEntries` appends running-but-unpinned apps after every slot,
   *  so they live in `railKeysRef` at the TAIL and `setShellRailOrder` ignores
   *  them (no pin row to number). Anything reasoning about rail POSITIONS has
   *  to use this list, or an unpinned-but-running app is treated as occupying
   *  the slot its tail position implies. */
  const railSlotKeysRef = useRef<string[]>([])
  railSlotKeysRef.current = railEntriesBase
    .filter(e => e.type === 'folder' || e.pinned)
    .map(e => (e.type === 'folder' ? railKeyOfFolder(e) : railKeyOfPin(e)))
  const appLabel = useCallback((kind: 'miniapp' | 'html', ref: string) =>
    shell.pinnedApps.find(p => p.kind === kind && p.ref === ref)?.title
      ?? shell.runningApps.find(a => a.kind === kind && a.ref === ref)?.title
      ?? tCoreUi('taskBar.app'), [shell, tCoreUi])
  const folderLabel = useCallback((id: string) => shell.folders.find(f => f.id === id)?.title || tCoreUi('taskBar.folder'), [shell, tCoreUi])

  /** Return an app to the rail at the position it held in `railBefore`.
   *
   *  The order is REBUILT from the live rail rather than replaying `railBefore`,
   *  for the reason `handleDeleteFolder` records at length: a whole-list write
   *  numbers every key it names and nothing else, so replaying a 6-second-old
   *  snapshot both reverts whatever was rearranged in the window and leaves any
   *  pin it does not name sharing a slot with one it does.
   *
   *  POSITION comes from slot keys, EMISSION from the full list. Anchoring on a
   *  running-but-unpinned app would read its tail position as a rail slot; but
   *  dropping the tail from what we SEND would unnumber an app that got pinned
   *  during the window. Slot keys are a prefix of the full list, so an index
   *  taken in one is valid in the other. */
  const handleMoveToRail = useCallback((kind: 'miniapp' | 'html', ref: string, railBefore: string[], unpinAfter = false) => {
    const key = `app:${kind}:${ref}`
    const live = railKeysRef.current.filter(k => k !== key)
    const slotted = new Set(railSlotKeysRef.current)
    const wasBefore = railBefore.slice(0, Math.max(0, railBefore.indexOf(key)))
    const anchors = wasBefore.filter(k => k !== key && slotted.has(k) && live.includes(k))
    const at = anchors.length ? Math.max(...anchors.map(k => live.indexOf(k))) + 1 : 0
    const order = [...live.slice(0, at), key, ...live.slice(at)]
    shell.movePinToFolder(kind, ref, null)
    shell.reorderRail(order)
    // The app was NOT pinned before the gesture — it was only pinned so it could
    // be filed (a folder member needs a row). Undoing has to take that back, or
    // a transient app is left on the dock for good once its window closes.
    if (unpinAfter) shell.unpinApp(kind, ref)
    // ONE CHAIN, not two concurrent writes on the same row. Fired alongside,
    // the unpin's DELETE could land before `setPinFolder`'s SELECT, which then
    // 404s — skipping the rail write and showing "couldn't save your dock"
    // after an Undo that in fact succeeded. Same ordering rule the pin-then-file
    // path two functions down states for the same reason.
    persistDock(
      setShellPinFolder(kind, ref, null)
        .then(() => setShellRailOrder(order))
        .then(() => (unpinAfter ? unpinShellApp(kind, ref) : undefined)),
    )
  }, [shell, persistDock])

  /** `handleMoveToFolder` recurses from its own Undo toast. Called directly, the
   *  identifier resolves to the callback from the render that CREATED the toast,
   *  whose `shell` still holds the pre-gesture dock — so the follow-up toast
   *  recomputed `previousFolderId` from stale state and its Undo was a silent
   *  no-op (`moveInDock` returns the dock unchanged when the folder already
   *  matches). Going through a ref always reaches the current one. */
  const handleMoveToFolderRef = useRef<(k: 'miniapp' | 'html', r: string, f: string | null) => void>(() => {})
  const handleMoveToFolder = useCallback((kind: 'miniapp' | 'html', ref: string, folderId: string | null) => {
    const previousFolderId = shell.pinnedApps.find(p => p.kind === kind && p.ref === ref)?.folderId ?? null
    const previousRail = railKeysRef.current
    // Whether it was pinned BEFORE this gesture. Filing an app requires a pin
    // row, so an unpinned one is pinned on the way in — and Undo must reverse
    // that too, not just the folder move.
    const wasPinned = shell.isPinned(kind, ref)
    if (folderId && !shell.isPinned(kind, ref)) {
      // Only PINNED apps can be grouped (a running-but-unpinned app has no
      // row to carry a folder_id): pin first, locally and on the server, and
      // let the folder write follow the pin write so the server sees the row.
      const title = shell.runningApps.find(a => a.kind === kind && a.ref === ref)?.title
      shell.pinApp(kind, ref, title)
      shell.movePinToFolder(kind, ref, folderId)
      persistDock(pinShellApp(kind, ref, title).then(() => setShellPinFolder(kind, ref, folderId)))
    } else {
      shell.movePinToFolder(kind, ref, folderId)
      persistDock(setShellPinFolder(kind, ref, folderId))
    }
    // Undo puts it back where it was: the folder it left, or its old rail slot.
    //
    // A FOLDER IT LEFT MAY NOT SURVIVE THE GESTURE. Taking the last member out
    // empties the source, and an empty folder is pruned on BOTH sides — locally
    // by `pruneEmptyFolders`, on the server by `pruneFolderIfEmpty`. Undo would
    // then name a dead id: the local move returns the dock unchanged and
    // `PATCH /pins/folder` 404s into "couldn't save your dock". That is the
    // "Undo did nothing" symptom again, from a different cause.
    //
    // Predicted here so the button is simply withheld — the stance
    // `handleDeleteFolder` already takes, and for the same reason: a button that
    // never works is worse than none. It cannot be fixed by re-creating the
    // folder, because `POST /folders` requires two members and a resurrected
    // one has exactly one.
    const sourceSurvives = previousFolderId !== null
      && shell.pinnedApps.filter(p => p.folderId === previousFolderId).length > 1
    /** The folder may ALSO die during the 6s window — another ungroup prunes it,
     *  or ungroup's own Undo re-creates it under a NEW id. Re-checked live at
     *  click time against the slot keys, so a stale toast falls back to the rail
     *  instead of erroring. */
    const restoreTarget = () => (
      previousFolderId !== null && railSlotKeysRef.current.includes(`folder:${previousFolderId}`)
        ? previousFolderId
        : null
    )
    const app = appLabel(kind, ref)
    if (folderId) {
      const undoable = previousFolderId === null || sourceSurvives
      toast.success(tCoreUi('taskBar.movedInto', { app, folder: folderLabel(folderId) }), 6000, undoable ? {
        label: tCoreUi('taskBar.undo'),
        onClick: () => {
          const back = restoreTarget()
          if (back) handleMoveToFolderRef.current(kind, ref, back)
          else handleMoveToRail(kind, ref, previousRail, !wasPinned)
        },
      } : undefined)
    } else if (previousFolderId) {
      toast.success(tCoreUi('taskBar.removedFrom', { app, folder: folderLabel(previousFolderId) }), 6000, sourceSurvives ? {
        label: tCoreUi('taskBar.undo'),
        // No rail fallback here: the app is ALREADY on the rail, so a vanished
        // folder means there is nothing left to undo.
        onClick: () => { const back = restoreTarget(); if (back) handleMoveToFolderRef.current(kind, ref, back) },
      } : undefined)
    }
  }, [shell, persistDock, toast, tCoreUi, appLabel, folderLabel, handleMoveToRail])
  handleMoveToFolderRef.current = handleMoveToFolder

  // Create is the one folder write whose RESULT the shell needs — the server
  // mints the id — so it awaits. Only PINNED apps can be grouped: an unpinned
  // side is pinned first (locally and on the server, awaited so the row exists
  // before the folder write names it). On failure nothing local has changed
  // except those pins, and the toast says why the tiles sprang back.
  const handleCreateFolder = useCallback(async (a: { kind: 'miniapp' | 'html'; ref: string }, b: { kind: 'miniapp' | 'html'; ref: string }, title = ''): Promise<string | null> => {
    const members = [a, b]
    for (const m of members) {
      if (shell.isPinned(m.kind, m.ref)) continue
      const title = shell.runningApps.find(x => x.kind === m.kind && x.ref === m.ref)?.title
      shell.pinApp(m.kind, m.ref, title)
      await pinShellApp(m.kind, m.ref, title).catch(() => {})
    }
    try {
      const { id } = await createShellFolder(members, title)
      // The folder takes the lowest member slot, as the server does — counting
      // only members already ON THE RAIL. A member inside another folder has a
      // folder-INTERNAL `pinOrder` (0, 1, 2), and mixing one in put the new
      // folder above everything. Must stay in step with `createFolder` in
      // shared/database/src/repositories/shell.ts, or the optimistic position
      // and the stored one disagree until the next hydrate.
      const orders = members
        .map(m => shell.pinnedApps.find(p => p.kind === m.kind && p.ref === m.ref))
        .filter((p): p is NonNullable<typeof p> => !!p && p.folderId === null)
        .map(p => p.pinOrder)
      // With no member on the rail the server APPENDS (`max(rail slot) + 1`,
      // counting folders). `pinnedApps.length + folders.length` counted pins
      // INSIDE folders too and matched nothing the server computes — 7 where
      // the server said 1, on a dock of 5 pins and 2 folders.
      const railMax = Math.max(
        -1,
        ...shell.pinnedApps.filter(p => p.folderId === null).map(p => p.pinOrder),
        ...shell.folders.map(f => f.pinOrder),
      )
      shell.groupPins({ id, title, pinOrder: orders.length ? Math.min(...orders) : railMax + 1, color: null }, members)
      return id
    } catch {
      toast.error(tCoreUi('taskBar.folderCreateFailed'))
      return null
    }
  }, [shell, toast, tCoreUi])

  // Rearranging: the whole top-level order, as keys, after a drop into a gap.
  const handleReorderRail = useCallback((order: string[], movedKey: string) => {
    // A running-but-unpinned app placed in a slot becomes pinned — arranging it
    // IS the intent to keep it — and the server only orders pins that exist, so
    // its pin write must land before the reorder.
    //
    // The moved entry, plus any running-but-unpinned app the drop landed
    // BEHIND. Two failure modes bracket this and both have shipped:
    //
    //  - Pin everything in `order` (the original): `composeRailEntries` appends
    //    every running-unpinned app to the list, so opening three apps from the
    //    drive and dragging a fourth wrote all three to `shell_pins` for good —
    //    no toast, no undo.
    //  - Pin only the moved entry: the rail renders pinned slots FIRST and
    //    unpinned ones after, so dropping a tile behind an unpinned one is not
    //    representable — it snapped back in front of it, immediately and
    //    permanently.
    //
    // An unpinned app the user dropped BEHIND now has a position that has to be
    // real, so it is pinned; one merely sitting further along the rail, which
    // the gesture never addressed, is left alone.
    const appOf = (k: string) => /^app:(miniapp|html):(.+)$/.exec(k)
    const movedAt = order.indexOf(movedKey)
    const unpinned = order
      .slice(0, movedAt < 0 ? 0 : movedAt + 1)
      .map(appOf)
      .filter((m): m is RegExpExecArray => !!m && !shell.isPinned(m[1] as 'miniapp' | 'html', m[2]!))
    for (const m of unpinned) {
      const title = shell.runningApps.find(a => a.kind === m[1] && a.ref === m[2])?.title
      shell.pinApp(m[1] as 'miniapp' | 'html', m[2]!, title)
    }
    shell.reorderRail(order)
    persistDock(Promise.all(unpinned.map(m => pinShellApp(m[1] as 'miniapp' | 'html', m[2]!, shell.runningApps.find(a => a.kind === m[1] && a.ref === m[2])?.title)))
      .then(() => setShellRailOrder(order)))
  }, [shell, persistDock])

  // Rearranging inside a folder: the member order as keys.
  const handleReorderFolder = useCallback((id: string, order: string[]) => {
    shell.reorderFolder(id, order)
    persistDock(setShellFolderOrder(id, order))
  }, [shell, persistDock])

  // A file dropped on the rail from the drive or the home grid: pin it with
  // its name, wait for the row, then place it — the reorder only orders pins
  // that exist.
  const handlePinToSlot = useCallback((apps: Array<{ kind: 'miniapp' | 'html'; ref: string; title: string }>, order: string[]) => {
    for (const a of apps) shell.pinApp(a.kind, a.ref, a.title)
    shell.reorderRail(order)
    // Every pin must exist BEFORE the order is written — the server only orders
    // pins it can find — and the order is written exactly ONCE for the drop.
    persistDock(Promise.all(apps.map(a => pinShellApp(a.kind, a.ref, a.title))).then(() => setShellRailOrder(order)))
  }, [shell, persistDock])

  const handleUpdateFolder = useCallback((id: string, patch: { title?: string; color?: FolderColorKey | null }) => {
    shell.updateFolder(id, patch)
    persistDock(updateShellFolder(id, patch))
  }, [shell, persistDock])

  // Ungroup, with Undo: the folder is re-created with the same members, title
  // and colour. Its members return to the rail meanwhile (the FK's SET NULL).
  const handleDeleteFolder = useCallback((id: string) => {
    // Captured BEFORE the ungroup: `railKeysRef` is rebuilt every render from
    // the live entries, so reading it inside the Undo handler would give the
    // post-ungroup order, which is the thing being undone.
    const railBefore = [...railKeysRef.current]
    const folder = shell.folders.find(f => f.id === id)
    const members = shell.pinnedApps.filter(p => p.folderId === id).sort((x, y) => x.pinOrder - y.pinOrder).map(p => ({ kind: p.kind as 'miniapp' | 'html', ref: p.ref }))
    shell.removeFolder(id)
    persistDock(deleteShellFolder(id))
    // Undo re-creates the folder through `POST /folders`, which requires TWO
    // or more distinct members (a deliberate, tested rule: grouping is
    // combining things). A ONE-member folder is a perfectly normal state —
    // pruning happens at ZERO, so a two-member folder that loses one survives
    // and renders — and offering Undo for it promised a restore the route
    // always answered 400 to, leaving the toast to say "couldn't group" and
    // the folder gone anyway.
    //
    // So: no button rather than a broken one. Ungrouping a one-member folder
    // is not undoable, which is a real limitation, but a wrapper around a
    // single app is the cheapest thing here to lose — and a button that never
    // works is worse than none.
    if (!folder || members.length < 2) return
    toast.success(tCoreUi('taskBar.ungrouped', { folder: folder.title || tCoreUi('taskBar.folder') }), 6000, {
      label: tCoreUi('taskBar.undo'),
      onClick: () => {
        void createShellFolder(members, folder.title)
          .then(({ id: newId }) => {
            shell.groupPins({ id: newId, title: folder.title, pinOrder: folder.pinOrder, color: folder.color }, members)
            if (folder.color) persistDock(updateShellFolder(newId, { color: folder.color }))
            // Put the RAIL back too, not just the folder. Ungrouping appends
            // the freed members after the last rail occupant, so re-creating
            // the folder from them takes the lowest of those APPENDED slots —
            // the end of the rail, not where the folder was. The client wrote
            // the old `pinOrder` optimistically, so it looked restored in place
            // and then moved on the next hydrate.
            //
            // Rebuilt from the CURRENT rail rather than replayed from the
            // captured one. The toast lives 6 seconds and a whole-list write
            // assigns `pin_order = index` to every key it names and nothing
            // else — so replaying a stale snapshot silently reverted anything
            // reordered in that window, and left any pin it did not name
            // sharing a slot with one it did. Only the folder's POSITION comes
            // from the snapshot, measured against the keys that still exist.
            const memberKeys = new Set(members.map(m => `app:${m.kind}:${m.ref}`))
            // SLOT keys only: a running-but-unpinned app sits at the tail of the
            // rendered rail regardless of where it belongs, and `setRailOrder`
            // numbers nothing for it — treating one as an anchor sent the
            // folder to the end.
            const survivors = railSlotKeysRef.current.filter(k => !memberKeys.has(k) && k !== `folder:${id}`)
            const wasBefore = railBefore.slice(0, Math.max(0, railBefore.indexOf(`folder:${id}`)))
            // AFTER EVERY surviving predecessor — the MAXIMUM of their current
            // positions, not the position of whichever came last BEFORE the
            // ungroup. `anchors` is in the old order while `survivors` is in the
            // live one, so taking the last of the former and indexing the latter
            // is only right while the two agree: reordering two predecessors
            // (drag B in front of A) then restored the folder ahead of A, which
            // the earlier count-based form got right.
            const anchors = wasBefore.filter(k => survivors.includes(k))
            const at = anchors.length ? Math.max(...anchors.map(k => survivors.indexOf(k))) + 1 : 0
            const restored = [...survivors.slice(0, at), `folder:${newId}`, ...survivors.slice(at)]
            shell.reorderRail(restored)
            persistDock(setShellRailOrder(restored))
          })
          .catch(() => { toast.error(tCoreUi('taskBar.folderCreateFailed')) })
      },
    })
  }, [shell, persistDock, toast, tCoreUi])

  const handleCloseUserApp = useCallback((kind: 'miniapp' | 'html', ref: string) => {
    shell.closeApp(kind, ref)
    delete userAppLocsRef.current[ref]
    // Deliberately NOT rolled back: resurrecting a window the user just
    // dismissed is worse than the stale row it leaves behind. Tell them
    // instead — the app reappears on reload and that should not be a surprise.
    void closeShellInstance(kind, ref).catch(() => {
      toast.error(tCore('taskBar.toast.closeFailed'))
    })
    if (activeUserRef === ref) navigate(`${prefix}/home`)
  }, [shell, activeUserRef, navigate, prefix, toast, tCore])

  // Taskbar "Edit icon" (mini-app dock entries) — ref = the app's folder id.
  const [iconEditorAppId, setIconEditorAppId] = useState<string | null>(null)
  const handleEditUserAppIcon = useCallback((_kind: 'miniapp' | 'html', ref: string) => {
    setIconEditorAppId(ref)
  }, [])

  // GC dock entries when their target app is deleted — mirrors the workspace
  // auto-close above so a deleted mini-app/HTML doesn't linger as a dead
  // running window or pin (client- AND server-side, via the handlers). Matches
  // the deleted fileId directly; an ANCESTOR delete falls through to
  // AppWindow's graceful "App not found" (same as a stale pin). Everything the
  // handler needs — shell state AND the close/unpin fns — is read through a
  // ref, so with the stable `onFileEvent` the subscription is set up ONCE and
  // never churns on open/close/pin/navigate.
  const gcRef = useRef({ running: shell.runningApps, pinned: shell.pinnedApps, close: handleCloseUserApp, unpin: handleUnpinUserApp })
  gcRef.current = { running: shell.runningApps, pinned: shell.pinnedApps, close: handleCloseUserApp, unpin: handleUnpinUserApp }
  useEffect(() => {
    return onFileEvent((event) => {
      if (event.type !== 'file:deleted') return
      const isUserApp = (k: string) => k === 'miniapp' || k === 'html'
      const { running, pinned, close, unpin } = gcRef.current
      for (const a of running) if (a.ref === event.fileId && isUserApp(a.kind)) close(a.kind as 'miniapp' | 'html', a.ref)
      for (const p of pinned) if (p.ref === event.fileId && isUserApp(p.kind)) unpin(p.kind as 'miniapp' | 'html', p.ref)
    })
  }, [onFileEvent])

  // Live-refresh a mini-app's dock chip icon when its icon is edited (from the
  // editor, the agent tool, or another tab). `setAppIcon` no-ops unless the
  // folder is currently open or pinned, so this is cheap. `setAppIcon` is a
  // stable callback, so the subscription is set up once.
  const { setAppIcon, setPinTitle } = shell
  useEffect(() => {
    return onFileEvent((event) => {
      if (event.type !== 'file:updated' || !event.file) return
      if (!presentsAsMiniapp(event.file.name, event.file.fileType, event.file.isFolder)) return
      setAppIcon('miniapp', event.fileId, parseMiniappIcon(event.file.metadataJson ?? null), parseMiniappIconRev(event.file.metadataJson ?? null))
      // A rename arrives on the same event: update the tile NOW so it does not
      // wait for a refetch. This is a mirror of local state only — nothing
      // persists it, and nothing needs to: `GET /pins` resolves the title from
      // the app folder itself (beside the icon), so a reload shows the current
      // name whether or not this ran. (`setPinTitle` never pins — an app off
      // the dock stays off.)
      setPinTitle('miniapp', event.fileId, appDisplayName(event.file.name))
    })
  }, [onFileEvent, setAppIcon, setPinTitle])

  // Close a system app (drive / keybook / settings) from its window
  // top-bar. The app stays PINNED on the taskbar rail (relaunchable) — closing
  // just tears down its running window and drops HOME. The home surface itself
  // has no close button (see `homeView` below), which is why closing can always
  // land there without the possibility of closing home onto itself.
  const handleCloseSystemApp = useCallback((kind: 'drive' | 'keybook' | 'settings' | 'billing' | 'launcher' | 'apps') => {
    shell.closeApp(kind)
    if (kind === 'settings') settingsLocRef.current = null
    if (kind === 'billing') billingLocRef.current = null
    void closeShellInstance(kind).catch(() => {})
    navigate(landingPath(prefix))
  }, [shell, navigate, prefix])

  // In-app navigation → URL splat sync. `replace` — the iframe's own
  // navigation already pushed a joint-session history entry; pushing an SPA
  // entry too would double every back-step. relPath '' = the default entry. Encoded
  // per segment: a page named `a#b.html` or `100%.html` must stay in the pathname.
  const activeUserRefRef = useRef(activeUserRef)
  activeUserRefRef.current = activeUserRef
  const handleAppNavigate = useCallback((kind: 'miniapp' | 'html', ref: string, relPath: string) => {
    const target = relPath ? `${prefix}/app/${ref}/${encodeServePath(relPath)}` : `${prefix}/app/${ref}`
    userAppLocsRef.current[ref] = { pathname: target, search: '' }
    // Background windows can navigate too (SSE auto-reload) — only the
    // ACTIVE app's navigation touches the address bar.
    if (activeUserRefRef.current === ref) navigate(target, { replace: true })
    void persistShellOpen(kind, ref, target).catch(() => {})
  }, [navigate, prefix])

  // The SDK openFile bridge for standalone app windows. Mirrors the workspace
  // host's resolver (WorkspaceView.handleOpenFileTarget) but adapted to the
  // shell: same-tenant targets navigate the shell to the SPA file/folder view;
  // cross-tenant / cross-workspace targets open a reusable browser tab. All
  // three target kinds (fileId / relativePath / drivePath) and the `workspaceId`
  // + `openTenant` opts are honored — the previous V1 handled only `fileId`.
  const handleAppOpenFileTarget = useCallback(async (
    target: OpenFileTargetWire,
    ctx: { appFolderId: string | null; opts: OpenFileOptsWire },
  ) => {
    // An app window opens files in SINGLE-FILE mode (the SPA `/file/:id`
    // SingleFileView) — it has no workspace/tab surface of its own, so the
    // `workspaceId` opt is deliberately IGNORED here (it would yank the user
    // into a full workspace, which is exactly what this surface avoids). A
    // FOLDER target has no single-file view, so it reveals in the drive.
    try {
      // ── Cross-tenant (openTenant lens) ────────────────────────────────
      const otOpt = ctx.opts.openTenant
      if (otOpt === 'linkTenant') {
        toast.error('Opening through a share link needs the shared view (coming soon)')
        return
      }
      const crossTenant =
        otOpt !== 'activeTenant' && target.kind === 'fileId' && target.fileTenantId !== tenantId
          ? { tenant: target.fileTenantId, fileId: target.fileId }
          : null
      if (crossTenant) {
        // Member of the file's tenant? Non-member = link-only → share-view (deferred #188).
        // Members: single-file view in that tenant (reusable browser tab — can't switch
        // the shell's tenant in place cleanly).
        let isMember = false
        try {
          const { tenants } = await request<{ tenants: Array<{ id: string }> }>('/api/orgs/my-tenants')
          isMember = tenants.some(t => t.id === crossTenant.tenant)
        } catch { isMember = false }
        if (!isMember) {
          toast.error('Opening in that tenant needs the shared view (coming soon)')
          return
        }
        window.open(`/t/${crossTenant.tenant}/file/${encodeURIComponent(crossTenant.fileId)}`, `papan:file:${crossTenant.fileId}`)
        return
      }

      // Resolve target → fileId (same-tenant). fileId is direct; paths resolve
      // native against the app's mount folder (relativePath) or the drive root
      // (drivePath). The server resolver permission-gates from `appFolderId`.
      let fileId: string
      let isFolder = false
      if (target.kind === 'fileId') {
        fileId = target.fileId
        // #788 — a `.pdapp` FOLDER id opens the FOLDER, the same answer mobile
        // and share view already gave. Launching an app is `openPdappByFolderId`.
        // `null` = could not tell even after a retry. Falling back to "file" keeps
        // the recoverable direction (a file view of a folder errors; a wrong
        // launch RUNS someone's app) — see `isFolderById`.
        isFolder = (await filesApi.isFolderById(fileId)) ?? false
      } else {
        const result = target.kind === 'relativePath'
          ? await filesApi.resolvePath(target.path, ctx.appFolderId ?? undefined)
          : await filesApi.resolvePath(`/papan/${target.path.replace(/^\/+/, '')}`)
        if (!result?.id) { toast.error('File not found'); return }
        fileId = result.id
        isFolder = result.file.isFolder
      }

      // Open in a NEW reusable browser tab (keyed by id) so the app window
      // stays running behind it: a file → single-file view (`/file/:id`), a
      // folder → the drive. The open rides the iframe click's user activation
      // (Chrome propagates it to ancestors), so it isn't popup-blocked.
      if (isFolder) {
        window.open(`${prefix}/folder/${encodeURIComponent(fileId)}`, `papan:folder:${fileId}`)
      } else {
        window.open(`${prefix}/file/${encodeURIComponent(fileId)}`, `papan:file:${fileId}`)
      }
    } catch {
      toast.error('Could not open the requested file')
    }
  }, [prefix, tenantId, toast])

  // #788 — `papan.openPdappByFolderId()` from a running app window: LAUNCH the
  // named app. Nothing is resolved here — the app route resolves and GATES the
  // target as the viewer, so an app they cannot open refuses there and this app
  // is told nothing either way (fire-and-forget, no oracle). Same tenant → the
  // shell's own app window (this one keeps running behind it, as the dock
  // expects); another tenant → a reusable browser tab, since the shell can't
  // switch tenant in place (the same split `handleAppOpenFileTarget` makes).
  const handleAppOpenPdappTarget = useCallback((target: OpenPdappTargetWire) => {
    try {
      if (target.tenantId === tenantId) {
        navigate(`${prefix}/app/${encodeURIComponent(target.folderId)}`)
      } else {
        window.open(`/t/${encodeURIComponent(target.tenantId)}/app/${encodeURIComponent(target.folderId)}`, `papan:app:${target.folderId}`)
      }
    } catch { /* fire-and-forget */ }
  }, [navigate, prefix, tenantId])

  // Drop-to-delete on the drive shell's Bin nav item (the old taskbar bin
  // target moved into the drive shell). Deletes by id — the DnD transfer
  // carries {id, name, isFolder}, never paths.
  // The drive's runner (#781): a dropped big folder runs as a followed job,
  // with the same toast as the row menu's Delete.
  const { deleteItems: deleteDroppedOnBin } = useDeleteRunner({
    // #801: a split binned only the removable branches (the folder stays) — undo
    // restores exactly those (not through a mount: its ids are the source tenant's).
    onDeleted: (target, outcome, at) => {
      if (!at || outcome.deletedInSource || (outcome.binId === undefined && isMountId(target.id))) return
      undo.recordDeleteResult(at, target.id, target.name, outcome)
    },
    onSettled: () => {
      // Refresh drive view if visible; a deleted .project folder also needs
      // the workspace list refreshed (same as DriveView's onAfterDelete).
      driveRefreshRef.current?.()
      wsManager.refreshWorkspaces()
    },
  })
  const handleShellBinDrop = useCallback(async (files: { id: string; name: string; isFolder: boolean }[]) => {
    await deleteDroppedOnBin(files.map((f) => ({ id: f.id, name: f.name })))
  }, [deleteDroppedOnBin])

  const handleDriveDrop = useCallback(async (files: { id: string; name: string; isFolder: boolean }[]) => {
    if (files.length === 0) {
      // Empty array = hover timer signal to navigate to root
      navigate(`${prefix}/folder`)
      return
    }
    // The one move helper every drop surface uses: it reports what the SERVER did — a
    // #801 split, a #753 copy, a refusal, and the answers that move nothing (an access
    // change to confirm, a chain escape, a reverse share) — never a blanket "Moved".
    await moveItemsInto(files, null, { t, toast, undo })
    const rootPath = `${prefix}/folder`
    if (location.pathname.match(/\/f\/.+/)) {
      navigate(rootPath)
    } else {
      driveRefreshRef.current?.()
    }
  }, [prefix, navigate, location.pathname, toast, t, undo])

  const handleDriveUpload = useCallback(async (fileList: FileList) => {
    // #1316 review round 2 (owner, 2026-10-28): the upload the person started FINISHES as the identity that started it —
    // never the next session's drive root; what it shows (toasts, the navigation) stops with its session
    const batch = pinnedBatch()
    const sent = await uploadFilesAsBatch(batch, Array.from(fileList), (file, opts) => filesApi.uploadFile(null, file.name, file, opts))
    const successCount = sent.done.length
    if (sent.failed > 0 && !batch.ended()) toast.error(t('driveView.toast.uploadFailed'))
    // a file whose identity was lost with its session is not sent — said so, as the upload dock does
    if (sent.lost > 0) toast.error(t('driveView.toast.uploadIdentityLost'))
    if (batch.ended()) return
    if (successCount > 0) {
      toast.success(t('driveView.toast.uploaded', { count: successCount }))
    }
    const rootPath = `${prefix}/folder`
    if (location.pathname.match(/\/f\/.+/)) {
      navigate(rootPath)
    } else {
      driveRefreshRef.current?.()
    }
  }, [prefix, navigate, location.pathname, toast, t])

  const handleWorkspaceDrop = useCallback(async (files: { id: string; name: string; isFolder: boolean }[]) => {
    try {
      const first = files[0]
      if (first.isFolder) {
        // Folder → open as workspace
        const res = await workspaceApi.openFolder(first.id, first.name)
        navigate(`${prefix}/workspace/${res.folderId}`)
      } else {
        // File → smart open (same as double-click)
        const openIds = openWorkspaces.map(w => w.folderId)
        const result = await workspaceApi.findForFile(first.id, openIds)
        if (result.workspaceFolderId) {
          navigate(`${prefix}/workspace/${result.workspaceFolderId}?open=${first.id}`)
        } else if (result.parentFolderId) {
          const ws = await workspaceApi.openFolder(result.parentFolderId, result.parentFolderName || 'Folder')
          navigate(`${prefix}/workspace/${ws.folderId}?open=${first.id}`)
        }
      }
    } catch (err) {
      console.error('[App] workspace drop failed:', err)
    }
  }, [prefix, navigate, openWorkspaces])

  // While the session is being checked, draw the shell that is about to appear.
  //
  // History: this was `bg-zinc-900` + a literal "Loading...", then themed
  // classes + i18n. The themed version was still wrong — this gate renders
  // ABOVE `ThemedArea`, so nothing in the tree carried a `.theme-*` class, and
  // `themed-text-muted` / `themed-bg-primary` resolved to undefined variables:
  // black text on an unpainted background, which in dark mode reads as broken.
  // <AppShellSkeleton> applies the theme class itself (the rule for anything
  // outside ThemedArea) and shows the shell's actual geometry instead of a
  // centred word, so the app fills in rather than swapping. The string survives
  // for screen readers only.
  if (isLoading) {
    return <AppShellSkeleton label={t('common:loading')} />
  }

  // Redirect to login if not authenticated. Carry ?next= so a mid-use session
  // death (or any unauth) returns the user where they were. `location` is the
  // LIVE location (line ~195), correct here because the guard renders at the top
  // of AppLayout, not inside a hidden (frozen-location) deck.
  if (!isAuthenticated) {
    // A DELIBERATE logout goes to a clean /login; only involuntary unauth
    // (session death, or hitting a protected URL while logged out) carries ?next=.
    return <Navigate to={signingOut ? '/login' : loginPathWithNext(location.pathname + location.search + location.hash)} replace />
  }

  // Hold the SAME skeleton until we know which surface is home.
  //
  // The landing is a runtime platform setting now, so the `<Navigate>` routes
  // below cannot resolve until `main.tsx`'s fetch settles. Nothing has mounted
  // behind this skeleton, so waiting here is invisible rather than a flash —
  // and it is nearly free: `isLoading` above already blocks on an unbounded
  // `getSession()` round trip with no timeout, while this one is a memoised
  // singleton PK lookup on a public route doing no auth work, running
  // CONCURRENTLY. Added latency is `max(0, landing − session) ≈ 0`.
  //
  // ⚠ ORDER IS THE CORRECTNESS PROPERTY, not style. This must stay AFTER the
  // `isAuthenticated` check and must not be folded into `isLoading ||
  // !landingReady`: doing so puts the landing wait ahead of the `/login`
  // redirect, so an API outage — which already fails `getSession()` and should
  // show a login form — becomes an indefinite skeleton instead. The signed-out
  // path has no use for this value and must never wait on it.
  //
  // ⚠ And do NOT add a timeout "to be safe". A timer firing on a slow network
  // paints the seeded value and reintroduces exactly the flash this exists to
  // remove. Only the fetch failing may end the wait early, which lands in
  // landing.ts's degraded path with the last value this browser was told.
  if (!landingReady) {
    return <AppShellSkeleton label={t('common:loading')} />
  }

  // Redirect unverified users to verification page (production only)
  const requireVerification = import.meta.env.VITE_REQUIRE_EMAIL_VERIFICATION === 'true'
  if (requireVerification && user && !user.emailVerified) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email)}`} replace />
  }

  // #341 Case A — the active tenant rejected us (403 `tenant_access_denied`).
  // Every tenant-scoped request is failing, so there is no working app to
  // render behind a dialog; say so and offer the two real exits. Named only if
  // the org is already in our own tenant list — the server's 403 deliberately
  // doesn't confirm a tenant exists, and this screen must not become the
  // oracle it refused to be.
  if (tenantDenied) {
    return (
      <TenantAccessDeniedScreen
        tenantName={tenantsList.find(t => t.id === tenantId)?.name ?? null}
        userEmail={user?.email ?? null}
        personal={tenantId.startsWith('p_')}
        onGoToMyDrive={() => {
          setTenantDenied(false)
          // An authenticated user always has a personal tenant; guard anyway so
          // we never switch to an empty tenant id — fall back to the drive route.
          if (personalTenantId) void switchTenant(personalTenantId, undefined)
          else navigate('/~/folder')
        }}
        onSignOut={() => { void signOut() }}
      />
    )
  }

  // Parse settings page/param/subparam from the settings window's frozen
  // location (/settings/teams/tm_xxx/access) — NOT the live URL, so the
  // hidden settings window holds its page while another app is active.
  const settingsMatch = settingsLocRef.current?.pathname.match(/\/settings(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?/)
  const settingsPage = settingsMatch?.[1] || undefined
  const settingsParam = settingsMatch?.[2] || undefined
  const settingsSubparam = settingsMatch?.[3] || undefined

  // The version-history panel's "Manage" button (#590 follow-up) links here
  // as `/settings/version-retention?file=<id>` — parsed from the SAME frozen
  // settings location as the page/param/subparam above (not the live URL),
  // so the query survives the settings window being hidden behind another
  // app and re-shown.
  const scrollToFileId = settingsLocRef.current
    ? new URLSearchParams(settingsLocRef.current.search).get('file') || undefined
    : undefined

  // The App-history panel's "Manage" button (app-version ladder follow-up)
  // links here as `/settings/version-retention?pdapp=<id>` — same frozen-
  // location parse as `scrollToFileId` above.
  const scrollToPdappId = settingsLocRef.current
    ? new URLSearchParams(settingsLocRef.current.search).get('pdapp') || undefined
    : undefined

  // Same frozen-location parse for the Usage & Billing window. The parser is
  // shared with MobileShell (and owned by the view itself) so both shells agree
  // on what a `/billing/...` URL means — including the `platform/` scope
  // segment, which is what makes a pasted link say WHICH tenants it edits.
  //
  // Four `/billing/*` paths now live in Settings: `usage` (Tier-3, moved when
  // the shelf was created) and `budget` / `limits` / `subscription` (moved when
  // the shelf became platform-only). Rewrite rather than 404 — those paths are
  // linkable, bookmarked and pasted into support tickets, and the shelf entry
  // point no longer exists for the people who used them, so a dead bookmark
  // would be the only thing they hit.
  const billingRoute = parseBudgetBillingPath(billingLocRef.current?.pathname ?? '')
  const billingPageKey = billingRoute.page
  const billingMovedTo = billingRoute.settingsPage

  // Taskbar highlight: deck owner of the URL. Null while a user app is
  // active (its dock entry highlights instead) and on non-deck routes
  // (/file/:id) — nothing pretends to own those.
  const getActiveView = (): View | null => activeUserRef ? null : deckKind

  // Every drive-app page renders inside the shared shell: side nav (Home /
  // Library / My Space / Projects, Bin + Quarantine at the bottom) + hero
  // content card. ThemedArea/ErrorBoundary live on the drive deck WINDOW
  // (below), not per-page — the window is mounted once and stays alive.
  const inDriveShell = (page: DriveShellPage, children: React.ReactNode) => (
    <DriveShell page={page} urlPrefix={prefix} tenantName={driveTenantName} isOrg={!isPersonal} orgEmail={activeOrgEmail} onBinDrop={handleShellBinDrop}>
      {children}
    </DriveShell>
  )

  return (
   <ShellTopBarProvider>
    <NewProjectProvider isOrg={!isPersonal} tenantId={tenantId} tenantName={driveTenantName} currentUserEmail={activeOrgEmail || user?.email || ''}>
    {/* The workspace-area theme class + ws-stage on the app root paint ONE
        continuous ambient background behind the whole shell — the glass
        taskbar and the workspace's floating cards both sit on it (their
        wrappers are ws-clear'd). Opaque views (drive, settings) paint their
        own surface on top and simply cover it. */}
    <div className={`h-screen flex overflow-hidden ${getThemeClass(settings.theme.workspace)} ws-stage relative isolate`}>
      {/* Drive ambient overlay — the teal drive stage lives HERE (not in the
          shell) so entering AND leaving the drive both crossfade. Negative
          z + `isolate` on the root slot it above the root's ws-stage but
          below all content. Full-width (left-0) so the ambient BLEEDS behind
          the taskbar too — its neutral translucent glass (--ws-taskbar-bg)
          tints with whatever stage is active. Workspace view has no overlay,
          so its taskbar keeps the base indigo untouched. */}
      <div
        aria-hidden
        data-on={isDriveView ? '' : undefined}
        className="ws-stage-drive ws-stage-drive-fade absolute inset-0 -z-10"
      />
      {/* User-app ambient overlay — third hue family (fuchsia+rose) behind
          standalone app windows. Sits after the drive overlay so its fade
          paints above it during crossfade overlap; same fade mechanics. */}
      <div
        aria-hidden
        data-on={activeUserRef || deckKind === 'apps' ? '' : undefined}
        className="ws-stage-app ws-stage-drive-fade absolute inset-0 -z-10"
      />
      {/* Keybook ambient overlay — warm amber. Keybook runs as an app on
          its own tinted stage; same crossfade mechanics as drive/app. */}
      <div
        aria-hidden
        data-on={deckKind === 'keybook' ? '' : undefined}
        className="ws-stage-keybook ws-stage-drive-fade absolute inset-0 -z-10"
      />
      {/* Chat home ambient overlay — violet+sky. The AI landing's hero chat
          paints no card, so this tint carries most of the page's identity.
          Shooting stars live HERE (full-viewport, -z-10) so they truly fall
          in from beyond the screen top and pass behind the taskbar/top-bar
          glass — inside the chat-home container they were clipped at its
          top edge. They inherit this overlay's crossfade for free. */}
      <div
        aria-hidden
        data-on={deckKind === 'chat' ? '' : undefined}
        className="ws-stage-chat ws-stage-drive-fade absolute inset-0 -z-10"
      >
        {deckKind === 'chat' && <ShootingStars />}
      </div>
      {/* The identity shell owns the whole screen (§8.2 / §8.3): it is a
          takeover, and a rail of destinations beside it would offer surfaces
          the person has just been told they do not have. Hidden rather than
          overlaid so the settings deck — which the shell's Settings item opens
          BEHIND it — comes up full-width instead of inset by a phantom rail. */}
      <ThemedArea area="workspace" className="flex ws-clear">
        {!identityShellActive && <TaskBar
          activeView={getActiveView()}
          homeView={homeView}
          showBilling={hasBudgetBilling}
          identityOnly={identityOnly}
          runningViews={runningViews}
          railEntries={railEntries}
          onCreateFolder={handleCreateFolder}
          onMoveToFolder={handleMoveToFolder}
          onReorderRail={handleReorderRail}
          onMoveToRail={handleMoveToRail}
          onReorderFolder={handleReorderFolder}
          onPinToSlot={handlePinToSlot}
          onUpdateFolder={handleUpdateFolder}
          onDeleteFolder={handleDeleteFolder}
          onOpenUserApp={handleOpenUserApp}
          onOpenUserAppNewTab={handleOpenUserAppNewTab}
          onCloseUserApp={handleCloseUserApp}
          onPinUserApp={handlePinUserApp}
          onUnpinUserApp={handleUnpinUserApp}
          onEditUserAppIcon={handleEditUserAppIcon}
          onCloseSystemApp={handleCloseSystemApp}
          onOpenViewNewTab={handleOpenViewNewTab}
          onViewChange={handleViewChange}
          onWorkspaceDrop={handleWorkspaceDrop}
          onDriveDrop={handleDriveDrop}
          onDriveUpload={handleDriveUpload}
          workspacePopup={(closePopup) => <WorkspacePopupContent tenantId={tenantId} onClose={closePopup} />}
          onNotificationSubscribe={onNotification}
          onNotificationAction={handleNotificationAction}
          user={user ? { name: user.name, email: user.email } : undefined}
          tenants={tenantsList}
          activeTenantId={tenantId}
          activeOrgEmail={activeOrgEmail}
          onSwitchTenant={switchTenant}
          onCreateOrg={() => setShowOrgCreate(true)}
          canCreateOrg={canCreateOrg}
          personalIsIdentityOnly={personalIsIdentityOnly}
          onSignOut={signOut}
          pendingInvites={pendingInvites}
          onAcceptInvite={handleAcceptInvite}
          onDeclineInvite={handleDeclineInvite}
          onBlockInvite={handleBlockInvite}
        />}
      </ThemedArea>

      {/* Content column: ONE permanent top bar (filled by the active app via
          a portal) over the window area. pt-1.5 gives the bar the same small
          top gap the drive/workspace cards used to own. min-h-0 +
          overflow-hidden keep tall window content (workspace panels) from
          growing the column and focus-scrolling the bar off-screen. */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden pt-1.5">
        <ShellTopBarSlot />

        {/* Drive top bar — account email (left) + drive theme toggle. Drive
            runs as an app now (chat home is the home base), so it closes. */}
        <ShellTopBarPortal active={isDriveView}>
          <AppTopBar
            area="drive"
            left={(activeOrgEmail || user?.email) ? (
              <span className="text-xs themed-text-muted truncate max-w-[220px] select-none cursor-default" title={activeOrgEmail || user?.email}>
                {activeOrgEmail || user?.email}
              </span>
            ) : undefined}
            onClose={() => handleCloseSystemApp('drive')}
            closeTitle={tCore('taskBar.closeApp')}
          />
        </ShellTopBarPortal>
        {/* Chat home top bar — account email (left) + star-density toggle +
            chat theme toggle. Chat is the home base, so no close button. */}
        <ShellTopBarPortal active={chatOn && deckKind === 'chat'}>
          <AppTopBar
            area="chat"
            left={(activeOrgEmail || user?.email) ? (
              <span className="text-xs themed-text-muted truncate max-w-[220px] select-none cursor-default" title={activeOrgEmail || user?.email}>
                {activeOrgEmail || user?.email}
              </span>
            ) : undefined}
            right={<ChatStarToggle />}
          />
        </ShellTopBarPortal>
        {/* Keybook / Settings run as apps — title + close in the shared bar. */}
        {/* `&& keybookEnabled` (0179): the disabled notice must not sit under
            a "Keybook" app title bar claiming an app is running. */}
        <ShellTopBarPortal active={deckKind === 'keybook' && keybookEnabled}>
          <AppTopBar title={tCore('taskBar.keybook')} onClose={() => handleCloseSystemApp('keybook')} closeTitle={tCore('taskBar.closeApp')} />
        </ShellTopBarPortal>
        <ShellTopBarPortal active={deckKind === 'settings'}>
          <AppTopBar title={tCore('taskBar.settings')} onClose={() => handleCloseSystemApp('settings')} closeTitle={tCore('taskBar.closeApp')} />
        </ShellTopBarPortal>
        <ShellTopBarPortal active={deckKind === 'billing'}>
          <AppTopBar title={tCore('taskBar.platform')} onClose={() => handleCloseSystemApp('billing')} closeTitle={tCore('taskBar.closeApp')} />
        </ShellTopBarPortal>
        {/* No close button when the launcher IS home — same rule chat has always
            had. Closing it would navigate to `landingPath`, which is this very
            route, so the window would re-open on the same tick. */}
        <ShellTopBarPortal active={deckKind === 'launcher'}>
          <AppTopBar
            title={tCore('taskBar.launcher')}
            onClose={homeView === 'launcher' ? undefined : () => handleCloseSystemApp('launcher')}
            closeTitle={tCore('taskBar.closeApp')}
          />
        </ShellTopBarPortal>
        <ShellTopBarPortal active={deckKind === 'apps'}>
          <AppTopBar title={tCore('taskBar.pdapps')} onClose={() => handleCloseSystemApp('apps')} closeTitle={tCore('taskBar.closeApp')} />
        </ShellTopBarPortal>

        {/* Window area — every deck window stacks here, display-toggled. */}
        <div className="flex-1 flex min-w-0 min-h-0 relative overflow-hidden">

      {/* All cached workspaces — always mounted, visibility toggled via CSS.
          Not mounted at all in an identity-only tenant: `workspace.create` is
          gated there (C1), so every workspace this would cache is one the
          server refuses to make. The notice above owns a stale URL. */}
      {!identityOnly && <WorkspaceCache
        tenantId={tenantId}
        tenantName={driveTenantName}
        activeWorkspaceId={currentWorkspaceId}
        isWorkspaceView={isWorkspaceView}
        userRole={activeRole}
        orgEmail={activeOrgEmail}
      />}

      {/* ── Keep-alive app deck ──────────────────────────────────────────
          System apps stay mounted once opened; the URL only switches which
          window is visible (same pattern as WorkspaceCache above). Windows
          with route-dependent content render against their FROZEN
          last-owned location so the hidden tree keeps matching and never
          remounts on app switch. Caveat: hooks reading the LIVE location
          (useSearchParams) see the foreign URL while a window is hidden —
          harmless today (?reveal / ?q are read-only inputs), but don't
          hang effects off the live location inside deck content. */}

      {/* ── The identity-only empty state, for every surface this tenant
             does not have ────────────────────────────────────────────────
          §8's "purpose-built empty state if the user reaches a stale Drive
          URL", generalised to the whole set — a bookmarked workspace or
          keybook URL deserves the same answer as a bookmarked Drive one.

          The URL is deliberately LEFT ALONE rather than redirected: a
          bookmark, a pasted link or the Back button should be told what
          happened, which is the rule the budget pages already follow
          ("refused in words, not redirected"). Bouncing to the launcher
          would leave someone who typed a Drive URL unsure the click landed.

          ONE site, driven by `IDENTITY_ONLY_HIDDEN_VIEWS`, rather than a
          branch inside each deck: adding a surface to that constant then
          covers its stale URL for free, where three copies would be three
          chances to forget. The decks themselves simply do not mount below. */}
      {identityOnly && deckKind && IDENTITY_ONLY_HIDDEN_VIEWS.includes(deckKind) && (
        <ThemedArea area="drive" className="flex-1 flex min-w-0 min-h-0 ws-clear">
          <IdentityOnlyNotice onGoHome={() => navigate(landingPathFor(prefix, 'identity-only'))} />
        </ThemedArea>
      )}

      {/* Drive window */}
      {!identityOnly && (shell.isRunning('drive') || isDriveView) && driveLocRef.current && (
        <ThemedArea area="drive" className="flex-1 flex min-w-0 min-h-0 ws-clear" style={{ display: isDriveView ? undefined : 'none' }}>
          <ErrorBoundary>
            {/* #1259: live reactions while hidden wait until shown (`useWhenShown`). #1316: `SessionDeck` remounts
                the views at an identity / account end and a tenant switch, and renders nothing while signing out. */}
            <DeckVisibility active={isDriveView}>
            <SessionDeck>
            <Routes location={driveLocRef.current}>
        {['/t/:tenant/home', '/~/home'].map(path => (
          <Route key={path} path={path} element={inDriveShell('home',
            <DriveHomeView key={tenantId} urlPrefix={prefix} isOrg={!isPersonal} orgEmail={activeOrgEmail} userName={user?.name} tenantName={driveTenantName} onRequestMiniappView={requestMiniappView} FolderPickerDialog={FolderPickerDialog} />,
          )} />
        ))}
        {['/t/:tenant/recent', '/~/recent'].map(path => (
          <Route key={path} path={path} element={inDriveShell('recent',
            <RecentView key={tenantId} urlPrefix={prefix} isOrg={!isPersonal} orgEmail={activeOrgEmail ?? undefined} tenantName={driveTenantName} />,
          )} />
        ))}
        {['/t/:tenant/projects', '/~/projects'].map(path => (
          <Route key={path} path={path} element={inDriveShell('projects',
            <ProjectsView key={tenantId} urlPrefix={prefix} isOrg={!isPersonal} orgEmail={activeOrgEmail} tenantName={driveTenantName} onRequestMiniappView={requestMiniappView} FolderPickerDialog={FolderPickerDialog} deckActive={isDriveView} />,
          )} />
        ))}
        {['/t/:tenant/pdapps', '/~/pdapps'].map(path => (
          <Route key={path} path={path} element={inDriveShell('pdapps',
            <ProjectsView key={tenantId} urlPrefix={prefix} isOrg={!isPersonal} orgEmail={activeOrgEmail} tenantName={driveTenantName} variant="miniapp" onRequestMiniappView={requestMiniappView} FolderPickerDialog={FolderPickerDialog} deckActive={isDriveView} />,
          )} />
        ))}
        {['/t/:tenant/shared', '/~/shared'].map(path => (
          <Route key={path} path={path} element={inDriveShell('shared',
            <SharedView key={tenantId} urlPrefix={prefix} isOrg={!isPersonal} orgEmail={activeOrgEmail ?? undefined} />,
          )} />
        ))}
        {['/t/:tenant/search', '/~/search'].map(path => (
          <Route key={path} path={path} element={inDriveShell('search',
            <SearchResultsView key={tenantId} urlPrefix={prefix} />,
          )} />
        ))}
        {['/t/:tenant/folder/:folderId', '/~/folder/:folderId', '/t/:tenant/folder', '/~/folder'].map(path => (
          <Route key={path} path={path} element={inDriveShell('drive',
            <DriveView key={tenantId} tenantId={tenantId} isOrg={!isPersonal} userRole={activeRole} orgEmail={activeOrgEmail} tenantName={driveTenantName} onRefreshRef={driveRefreshRef} onAfterDelete={wsManager.refreshWorkspaces} onRequestMiniappView={requestMiniappView} FilePickerDialog={DrivePickerDialog} deckActive={isDriveView} />,
          )} />
        ))}
        {['/bin', '/~/bin', '/t/:tenant/bin'].map(path => (
          <Route key={path} path={path} element={inDriveShell('bin',
            <BinView
              key={tenantId}
              canPurge={canCap('purge_file', true)}
              canEmptyBin={canCap('empty_bin', true)}
              onOpenFolder={(folderId) => navigate(`${prefix}/folder/${folderId}`)}
              renderRestoreTargetPicker={({ originalName, onSelect, onCancel }) => (
                <FolderPickerDialog
                  tenantId={tenantId}
                  onSelect={onSelect}
                  onClose={onCancel}
                  title={t('binView.picker.title', { name: originalName })}
                  confirmLabel={t('binView.actions.restoreHere')}
                />
              )}
            />,
          )} />
        ))}
        {['/quarantine', '/~/quarantine', '/t/:tenant/quarantine'].map(path => (
          <Route key={path} path={path} element={inDriveShell('quarantine',
            <QuarantineView key={tenantId} />,
          )} />
        ))}
            </Routes>
            </SessionDeck>
            </DeckVisibility>
          </ErrorBoundary>
        </ThemedArea>
      )}

      {/* Chat home window — the AI landing. No hero card wrapper on purpose:
          ChatHomeView floats its rail/widget cards and a BARE chat hero
          directly on the violet chat stage (ws-clear lets it through). */}
      {/* `chatOn` (#368): never on a launcher deployment — not for a /chat URL
          on its way home, nor a `chat` entry restored from a persisted dock. */}
      {chatOn && (shell.isRunning('chat') || deckKind === 'chat') && (
        <ThemedArea area="chat" className="flex-1 flex min-w-0 min-h-0 ws-clear" style={{ display: deckKind === 'chat' ? undefined : 'none' }}>
          <ErrorBoundary>
            <ChatHomeView
              key={tenantId}
              deckActive={deckKind === 'chat'}
              tenantId={tenantId}
              urlPrefix={prefix}
              orgEmail={activeOrgEmail}
              userName={user?.name}
              orgName={isPersonal ? undefined : driveTenantName}
            />
          </ErrorBoundary>
        </ThemedArea>
      )}

      {/* Keybook window — runs as an app: a rounded ws-card-hero floating on
          the amber keybook stage (ws-clear lets the tint show in the gutter). */}
      {/* A stale keybook URL on a keybook-disabled deployment (0179): refused
          in words, not redirected — same rule as the identity-only notice
          above. The deck below simply does not mount. */}
      {!identityOnly && !keybookEnabled && deckKind === 'keybook' && (
        <ThemedArea area="workspace" className="flex-1 flex min-w-0 min-h-0 ws-clear">
          <KeybookDisabledNotice onGoHome={() => navigate(landingPath(prefix))} />
        </ThemedArea>
      )}

      {!identityOnly && keybookEnabled && (shell.isRunning('keybook') || deckKind === 'keybook') && (
        <ThemedArea area="workspace" className="flex-1 flex min-w-0 min-h-0 ws-clear" style={{ display: deckKind === 'keybook' ? undefined : 'none' }}>
          <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden px-3 pb-3 pt-0.5">
            <div className="ws-card-hero hero-keybook flex-1 flex flex-col min-w-0 min-h-0">
              <ErrorBoundary>
                <SessionDeck>
                <KeybookGate
                  tenantId={tenantId}
                  isOrg={!isPersonal}
                  userRole={activeRole}
                >
                  <KeybookView
                    userId={user?.id}
                    userEmail={activeOrgEmail || user?.email}
                    userRole={activeRole}
                    tenantId={tenantId}
                    isOrg={!isPersonal}
                    FilePickerDialog={DrivePickerDialog}
                    deckActive={deckKind === 'keybook'}
                  />
                </KeybookGate>
                </SessionDeck>
              </ErrorBoundary>
            </div>
          </div>
        </ThemedArea>
      )}

      {/* Launcher window — the tenant's curated app grid. Same keep-alive deck
          pattern as Keybook above. No frozen-location ref: one page, no
          sub-routes, so there is nothing route-dependent to freeze — the
          window renders the same thing whatever the live URL says while it is
          hidden. `key={tenantId}` for the reason the other windows have it: the
          grid is tenant-scoped and must not survive a switch as stale content.
          Nothing on the taskbar points here yet (plan Phase 6 decides which
          surface a deployment lands on); it is reachable by URL. */}
      {(shell.isRunning('launcher') || deckKind === 'launcher') && (
        <ThemedArea area="workspace" className="flex-1 flex min-w-0 min-h-0 ws-clear" style={{ display: deckKind === 'launcher' ? undefined : 'none' }}>
          {/* No `ws-card-hero`, deliberately — the launcher paints nothing of
              its own and sits directly on the stage, the same way the chat
              landing does. It used to carry the hero card every APP window
              gets: a near-opaque fill, a hairline border and a lift shadow.
              That is right for a window you opened on top of something; the
              landing surface IS the something, so the card read as a white
              sheet laid over the background with a visible seam around it.
              The gutter stays — it keeps the grid off the window edges. */}
          <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden px-3 pb-3 pt-0.5">
            <div className="flex-1 flex flex-col min-w-0 min-h-0">
              <ErrorBoundary>
                <DeckVisibility active={deckKind === 'launcher'}>
                <LauncherView
                  key={tenantId}
                  deckActive={deckKind === 'launcher'}
                  tenantId={tenantId}
                  orgEmail={activeOrgEmail}
                  enabled={isReady}
                  onOpen={(fileId) => navigate(`${prefix}/app/${fileId}`)}
                  userName={user?.name}
                  orgName={isPersonal ? undefined : driveTenantName}
                />
              </DeckVisibility>
              </ErrorBoundary>
            </div>
          </div>
        </ThemedArea>
      )}

      {/* PDApps window — every app the viewer can open, newest-opened first.
          Keep-alive deck like the others; one page, so no frozen location.
          In an identity-only tenant it shows only the person's own Pinned and
          Recent (`historyOnly`): those follow the account, and there is no drive. */}
      {(shell.isRunning('apps') || deckKind === 'apps') && (
        <ThemedArea area="workspace" className="flex-1 flex min-w-0 min-h-0 ws-clear" style={{ display: deckKind === 'apps' ? undefined : 'none' }}>
          <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden px-3 pb-3 pt-0.5">
            <div className="ws-card-hero hero-app flex-1 flex flex-col min-w-0 min-h-0">
              <ErrorBoundary>
                <DeckVisibility active={deckKind === 'apps'}>
                  <AppsView
                    key={tenantId}
                    urlPrefix={prefix}
                    tenantId={tenantId}
                    isOrg={!isPersonal}
                    orgEmail={activeOrgEmail}
                    orgName={driveTenantName}
                    deckActive={deckKind === 'apps'}
                    historyOnly={identityOnly}
                    chooseIdentityIfUnset={chooseIdentityIfUnset}
                    // A card's More: the drive's own app menu, as Drive → PDApps has it.
                    FolderPickerDialog={FolderPickerDialog}
                    onRequestMiniappView={requestMiniappView}
                  />
                </DeckVisibility>
              </ErrorBoundary>
            </div>
          </div>
        </ThemedArea>
      )}

      {/* #1253: the page this load entered at — past every gate above, and
          before <Routes>, whose catch-all judges it (EntryUnroutedMark). */}
      <EntryCapture shell="desktop" />
      {/* Route-based views (non-deck) */}
      <Routes>
        {/* Workspace with ID: rendered by WorkspaceCache (shows not-found if missing) */}
        <Route path="/t/:tenant/workspace/:workspaceId" element={null} />
        <Route path="/~/workspace/:workspaceId" element={null} />
        {/* Empty workspace: auto-create flow */}
        <Route path="/t/:tenant/workspace" element={
          <ThemedArea area="workspace" className="flex-1 flex min-w-0">
            <ErrorBoundary>
              <EmptyWorkspaceView tenantId={tenantId} isOrg={!isPersonal} />
            </ErrorBoundary>
          </ThemedArea>
        } />
        <Route path="/~/workspace" element={
          <ThemedArea area="workspace" className="flex-1 flex min-w-0">
            <ErrorBoundary>
              <EmptyWorkspaceView tenantId={tenantId} isOrg={!isPersonal} />
            </ErrorBoundary>
          </ThemedArea>
        } />
        {/* Deck-owned paths — matched here ONLY so the `*` catch-all below
            doesn't redirect away; the windows render from the deck above. */}
        {[
          '/t/:tenant/home', '/~/home',
          '/t/:tenant/recent', '/~/recent',
          '/t/:tenant/projects', '/~/projects',
          '/t/:tenant/pdapps', '/~/pdapps',
          '/t/:tenant/shared', '/~/shared',
          '/t/:tenant/search', '/~/search',
          '/t/:tenant/folder/:folderId', '/~/folder/:folderId', '/t/:tenant/folder', '/~/folder',
          // Chat only where it is on (#368): elsewhere these fall to the
          // catch-all below, which sends the URL home.
          ...(chatOn ? ['/chat', '/~/chat', '/t/:tenant/chat'] : []),
          '/keybook', '/~/keybook', '/t/:tenant/keybook',
          '/bin', '/~/bin', '/t/:tenant/bin',
          '/quarantine', '/~/quarantine', '/t/:tenant/quarantine',
          '/settings/:page?/:param?/:subparam?', '/~/settings/:page?/:param?/:subparam?', '/t/:tenant/settings/:page?/:param?/:subparam?',
          // THREE optional segments, matching Settings above. `/billing/platform/
          // customers/:tenantId` is three deep, and a pattern one segment short
          // does not 404 — it falls through to the `*` catch-all and silently
          // redirects to /~/chat, i.e. clicking a customer bounces you out of
          // the console entirely.
          '/billing/:page?/:sub?/:subsub?', '/~/billing/:page?/:sub?/:subsub?', '/t/:tenant/billing/:page?/:sub?/:subsub?',
          '/launcher', '/~/launcher', '/t/:tenant/launcher',
          '/~/apps', '/t/:tenant/apps', '/~/apps/recent', '/t/:tenant/apps/recent',
          ...DESKTOP_APP_ROUTE_PATHS,
        ].map(path => <Route key={path} path={path} element={null} />)}
        {['/file/:fileId', '/~/file/:fileId', '/t/:tenant/file/:fileId'].map(path => (
          <Route key={path} path={path} element={
            // Runs as a top-level shell window: SingleFileView portals its
            // AppTopBar into the shell bar and floats the editor in a
            // ws-card-hero. ws-clear lets the app-stage tint show in the gutter;
            // min-h-0 clamps the card height (same as the Keybook/Settings windows).
            <ThemedArea area="workspace" className="flex-1 flex min-w-0 min-h-0 ws-clear">
              <ErrorBoundary>
                <DraftScopeProvider scope="single"><SingleFileView tenantId={tenantId} /></DraftScopeProvider>
              </ErrorBoundary>
            </ThemedArea>
          } />
        ))}
        {/* Home. `landingPath(prefix)` on BOTH, not the old hardcoded
            `/~/chat`: `/~` is the PERSONAL tenant, which was fine while chat
            was always home, and is wrong for a launcher deployment — a client's
            apps live in the client's ORG tenant, so `/~` lands a user on an
            empty grid one tenant away from their apps.

            Still resolved synchronously at this point, and still with no flash
            — but for a different reason than it used to be. The value is a
            runtime platform setting now; the `!landingReady` gate above means
            this render cannot happen until it has arrived, and `homeView`'s
            subscription re-renders these routes if it is ever corrected
            mid-session.

            `landingPathFor` rather than `landingPath` since Phase E: the
            catch-all is where a stale Drive URL lands after the drive routes
            stop matching, and in an identity-only tenant it must not bounce to
            a chat home that cannot resolve a workspace.

            …and BOTH wait on `tenantsSettled`, which the rest of the shell does
            not. `identityOnly` is false until `my-tenants` lands, which is the
            right default for chrome that can narrow a moment later and the
            WRONG one for a redirect that replaces the history entry: without
            this wait the override never fired at all, and an identity-only
            person landed on the chat home every time. The skeleton is the one
            already on screen, so the wait is invisible, and the flag settles on
            failure too — a redirect must not be able to hang. */}
        <Route path="/" element={tenantsSettled
          ? <Navigate to={landingPathFor(prefix, identityOnly ? 'identity-only' : 'full')} replace />
          : <AppShellSkeleton label={t('common:loading')} />} />
        {/* EntryUnroutedMark: when this catches the path the load ENTERED at,
            that entry was no destination and must not suppress the identity
            picker (#1253). */}
        <Route path="*" element={<><EntryUnroutedMark shell="desktop" />{tenantsSettled
          ? <Navigate to={landingPathFor(prefix, identityOnly ? 'identity-only' : 'full')} replace />
          : <AppShellSkeleton label={t('common:loading')} />}</>} />
      </Routes>

      {/* User app windows (mini-app / standalone HTML run as top-level
          shell windows, Phase 2). One keep-alive window per running
          instance, keyed by (kind, ref). Hidden via OPACITY (absolute-
          stacked, `opacity-0`) rather than `display:none` — display:none
          tears down the iframe's layout and its internal scroll clamps to 0,
          so switching tabs would reset the app's scroll. opacity keeps the
          window laid out (iframe scroll + state preserved) while hidden, and
          unlike `visibility` a child can't override it back on — HtmlViewer
          sets inline visibility on its double-buffer iframes. pointer-events-
          none lets clicks reach the active window behind. Only unmounts when
          actually closed (handleCloseUserApp). */}
      {shell.runningApps.filter(a => a.kind === 'miniapp' || a.kind === 'html').map(a => {
        const kind = a.kind as 'miniapp' | 'html'
        // In-app entry page from the frozen splat (deep-link / restore).
        // Not opened yet in this page load → nothing mounted (it loads when first shown).
        if (a.ref !== activeUserRef && !shownApps.has(a.ref)) return null
        // The page under the app and the app's OWN ?query#hash from the frozen (deep-link /
        // restore) location — never Papan's own params (`appDeepLinkEntry`).
        const entry = appDeepLinkEntry(userAppLocsRef.current[a.ref])
        return (
          <ThemedArea
            key={`${a.kind}:${a.ref}`}
            area="workspace"
            className={`absolute inset-0 flex min-w-0 ws-clear ${a.ref === activeUserRef ? 'z-[1] opacity-100' : 'opacity-0 pointer-events-none z-0'}`}
            // A hidden window stays absolute-stacked over the whole shell, so
            // its iframe (which defaults to `pointer-events: auto`, ignoring the
            // window's pointer-events-none) would intercept clicks/drags meant
            // for the active app underneath — e.g. workspace panel resize.
            // Force --iframe-pointer:none on the hidden subtree so its iframes
            // go click-through too.
            style={a.ref === activeUserRef ? undefined : ({ '--iframe-pointer': 'none' } as React.CSSProperties)}
          >
            <ErrorBoundary>
              <AppWindow
                refId={a.ref}
                kind={kind}
                isActive={a.ref === activeUserRef}
                initialPath={entry.initialPath}
                initialSuffix={entry.initialSuffix}
                onResolvedTitle={(title) => openApp(a.kind, a.ref, title)}
                onResolvedIcon={(icon, rev) => openApp(a.kind, a.ref, undefined, icon, rev)}
                onAppNavigate={(relPath) => handleAppNavigate(kind, a.ref, relPath)}
                onClose={() => handleCloseUserApp(kind, a.ref)}
                onOpenEditor={kind === 'miniapp' ? () => { requestMiniappView(a.ref, 'settings'); navigate(`${prefix}/workspace/${a.ref}`) } : undefined}
                onOpenFileTarget={handleAppOpenFileTarget}
                onOpenPdappTarget={handleAppOpenPdappTarget}
                onOpenNewTab={() => handleOpenUserAppNewTab(kind, a.ref)}
                isPinned={shell.isPinned(kind, a.ref)}
                onTogglePin={() => shell.isPinned(kind, a.ref) ? handleUnpinUserApp(kind, a.ref) : handlePinUserApp(kind, a.ref, a.title)}
                onRevealInDrive={(parentFolderId) => navigate(parentFolderId ? `${prefix}/folder/${parentFolderId}?reveal=${a.ref}` : `${prefix}/folder?reveal=${a.ref}`)}
                onOpenAppsList={() => navigate(`${prefix}/apps`)}
                tenantId={tenantId}
              />
            </ErrorBoundary>
          </ThemedArea>
        )
      })}

      {/* Settings window — page/param/subparam parse from the frozen
          settings location above, so the window holds its page while
          hidden. DOM order vs the Routes block is irrelevant: only one
          window is ever visible. */}
      {(shell.isRunning('settings') || deckKind === 'settings') && (
        <ThemedArea area="workspace" className="flex-1 flex min-w-0 min-h-0 ws-clear" style={{ display: deckKind === 'settings' ? undefined : 'none' }}>
          <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden px-3 pb-3 pt-0.5">
            <div className="ws-card-hero flex-1 flex flex-col min-w-0 min-h-0">
              {/* Decks stay MOUNTED behind `display: none` (that is what makes
                  a switch instant), so a page whose data arrives from a
                  mount-once effect never refetches — Settings → Spending showed
                  figures frozen at whichever moment the deck was first opened.
                  This publishes the hidden→shown transition so those pages can
                  reload; `active` is the SAME expression as the `display`
                  above, so the signal cannot disagree with what the user
                  sees. */}
              <DeckVisibility active={deckKind === 'settings'}>
              <ErrorBoundary>
                <SessionDeck>
                <SettingsView
                  deckActive={deckKind === 'settings'}
                  user={user ?? undefined}
                  onSignOut={handleSignOut}
                  identityOnly={identityOnly}
                  activePage={settingsPage}
                  activeParam={settingsParam}
                  activeSubparam={settingsSubparam}
                  onPageChange={(page, param, subparam) => {
                    const prefix = isPersonal ? '/~' : `/t/${tenantId}`
                    let url = `${prefix}/settings/${page}`
                    if (param) url += `/${param}`
                    if (subparam) url += `/${subparam}`
                    navigate(url, { replace: false })
                  }}
                  hasPassword={passwordActions.hasPassword}
                  onSetPassword={passwordActions.onSetPassword}
                  onChangePassword={passwordActions.onChangePassword}
                  onForgotPassword={() => setForgotPwOpen(true)}
                  onRetryHasPassword={passwordActions.onRetryHasPassword}
                  onUpdateName={async (newName) => {
                    const trimmed = newName.trim()
                    if (!trimmed) return { error: 'Name cannot be empty.' }
                    const { authClient } = await import('@papan/auth/client')
                    const result = await authClient.updateUser({ name: trimmed })
                    if (result.error) return { error: result.error.message || 'Failed to update name' }
                    // Refresh session so the cached avatar/taskbar pick up the
                    // new name without a hard reload.
                    void refreshSession().catch(() => {})
                    return {}
                  }}
                  isOrg={!isPersonal}
                  tenantId={tenantId}
                  tenantName={tenantsList.find(t => t.id === tenantId)?.name}
                  userRole={activeRole}
                  onDeleteOrg={() => {
                    setTenantsList(prev => prev.filter(t => t.id !== tenantId))
                    switchTenant(personalTenantId || '', undefined)
                  }}
                  // `tenantName` above is read out of `tenantsList`, so a rename
                  // has to land there or the field reverts on the next render
                  // and the taskbar keeps the old name until a reload. Patched
                  // in place (same shape as onDeleteOrg) rather than refetching
                  // `my-tenants` — the server has already committed the name.
                  onRenameOrg={(name) => {
                    setTenantsList(prev => prev.map(t => (t.id === tenantId ? { ...t, name } : t)))
                  }}
                  onPickFile={handlePickFile}
                  refreshKey={orgRefreshKey}
                  orgEmail={activeOrgEmail}
                  orgsByEmail={orgsByEmail}
                  onEmailsChanged={() => {
                    // Refresh session — primary-email swap changes user.email,
                    // so the auth context (and any subscribers, e.g. ModelSelector)
                    // need to pick up the new value. The server already cleared the
                    // session_data cookie cache in the swap response, so this
                    // getSession() rebuilds from the DB.
                    void refreshSession().catch(() => {})
                    import('@papan/orgs/client').then(({ orgsApi }) => {
                      orgsApi.listMyTenants().then(data => {
                        setTenantsList(data.tenants)
                        // If currently in an org, check if we still have access
                        if (!isPersonal) {
                          const stillHasAccess = data.tenants.some((t: { id: string }) => t.id === tenantId)
                          if (!stillHasAccess) {
                            navigate('/~/folder')
                          }
                        }
                      }).catch(() => {})
                      orgsApi.listMyInvites().then(data => setPendingInvites(data.invites)).catch(() => {})
                    })
                  }}
                  onListBlocked={async () => {
                    const { orgsApi } = await import('@papan/orgs/client')
                    const { orgConnectionsApi } = await import('@papan/org-connections/client')
                    // Blocked org invitations + cross-org connection blocks,
                    // merged into one list. The connection fetch is
                    // owner/admin-gated server-side; swallow its 403 for
                    // members so the invite list still renders.
                    const [inviteData, connData] = await Promise.all([
                      orgsApi.listBlockedInvites().catch(() => ({ blocked: [] as Array<{ inviteId: string; tenantId: string; tenantName: string; email: string; scope?: 'account' | 'address' }> })),
                      orgConnectionsApi.listBlocks().catch(() => ({ blocks: [] as Awaited<ReturnType<typeof orgConnectionsApi.listBlocks>>['blocks'] })),
                    ])
                    // #152: an invitation block IS a block row of this account's
                    // personal tenant, so on a personal drive the connection list
                    // returns the same rows — show each once (as the invite).
                    const inviteIds = new Set(inviteData.blocked.map(b => b.inviteId))
                    return [
                      ...inviteData.blocked.map(b => ({ ...b, kind: 'invite' as const })),
                      ...connData.blocks.filter(b => !inviteIds.has(b.id)).map(b => ({
                        inviteId: b.id,
                        tenantId: b.blockedTenantId ?? '',
                        tenantName: b.tenantName ?? b.blockedEmail ?? 'Unknown organization',
                        email: b.blockedEmail ?? b.reason ?? '',
                        kind: 'connection' as const,
                      })),
                    ]
                  }}
                  onUnblock={async (inviteId, kind) => {
                    if (kind === 'connection') {
                      const { orgConnectionsApi } = await import('@papan/org-connections/client')
                      await orgConnectionsApi.removeBlock(inviteId)
                    } else {
                      const { orgsApi } = await import('@papan/orgs/client')
                      await orgsApi.unblockInvite(inviteId)
                    }
                  }}
                  onRevealInDrive={(folderId, parentFolderId) => {
                    const prefix = isPersonal ? '/~' : `/t/${tenantId}`
                    const base = parentFolderId ? `${prefix}/folder/${parentFolderId}` : `${prefix}/folder`
                    navigate(`${base}?reveal=${folderId}`)
                  }}
                  // `null` when the deployment has the keybook off (0179);
                  // SettingsView reads the ambient store itself to hide the
                  // sidebar item and refuse a stale deep link in words.
                  secureStoragePage={keybookEnabled ? <SecureStoragePage userId={user?.id} /> : null}
                  renderLeaveNotice={(tid) => <LeavingKeysNotice tenantId={tid} />}
                  connectionsPage={tenantId ? <ConnectionsPage tenantId={tenantId} isPersonal={isPersonal} myTenants={tenantsList} /> : null}
                  linksPage={<LinksPage />}
                  // `personal` is load-bearing, not cosmetic: without it a
                  // personal drive renders the ORG waterfall, whose rows read
                  // "personal exhausted, team exhausted, drawing from your Role
                  // budget" — three lies about a drive with no roles or teams.
                  // The flag swaps in the plan allowance instead.
                  
                  // This tenant's OWN spend, back from the Usage & Billing
                  // shelf now that the shelf is platform-only. Same components,
                  // same server gates; only the surface they hang off changed.
                  // `SpendingPage` picks the VIEW from `manage_budget:read` —
                  // the org dashboard for an admin, the member's own spend for
                  // everyone else — so the route stops refusing a member who
                  // simply wanted their own figures.
                  budgetPage={<SpendingPage tenantId={tenantId} userEmail={activeOrgEmail || user?.email} personal={isPersonal} onNavigate={() => navigate(`${prefix}/settings/limits`)} onOpenBilling={() => navigate(`${prefix}/settings/subscription`)} />}
                  limitsPage={<BudgetsEditor tenantId={tenantId} userEmail={activeOrgEmail || user?.email} onNavigate={() => navigate(`${prefix}/settings/budget`)} />}
                  // Drive-usage breakdown (org AND personal — §3A mirror).
                  // Reuses the 'storage' page key freed by the retired static
                  // tab, so /settings/storage deep-links land here.
                  storagePage={<StorageUsagePage tenantId={tenantId} personal={isPersonal} userEmail={activeOrgEmail || user?.email} onOpenBin={() => navigate(`${prefix}/bin`)} onOpenQuarantine={() => navigate(`${prefix}/quarantine`)} onRevealFile={(folderId, fileId) => {
                    // Same shape as onRevealInDrive above: bare /folder for the
                    // drive root (papan_<tenant> has no route of its own).
                    const base = folderId && !folderId.startsWith('papan_') ? `${prefix}/folder/${folderId}` : `${prefix}/folder`
                    navigate(`${base}?reveal=${fileId}`)
                  }} onOpenFolder={(folderId) => navigate(`${prefix}/folder/${folderId}`)} onOpenSubscription={() => navigate(`${prefix}/settings/subscription`)} onOpenVersionRetention={() => navigate(`${prefix}/settings/version-retention`)} />}
                  // Dedicated version-retention settings page (#590 Task 9) —
                  // the hierarchical per-kind/per-extension ladder + the
                  // per-file overrides list. Same org-AND-personal mirror as
                  // `storagePage` just above; `StorageUsagePage`'s own
                  // one-line link navigates here.
                  versionRetentionPage={<VersionRetentionPage tenantId={tenantId} personal={isPersonal} scrollToFileId={scrollToFileId} scrollToPdappId={scrollToPdappId} />}
                  // Settings → Organization → File activity (#118, Task B8).
                  // Org-only — see `ORG_ONLY_SETTINGS` in SettingsView — and
                  // self-gates on `view_file_audit:read`, same as
                  // `BudgetsEditor` self-gates on `manage_budget:write`.
                  fileActivityPage={<FileActivityPage tenantId={tenantId} userEmail={activeOrgEmail || user?.email} />}
                  // `null` here means RESOLVED denial; the flag covers the
                  // window where the grant has not landed yet.
                  billingPageLoading={capsLoading}
                  billingPage={!canViewBilling ? null : (
                    // All three hand-off signals come from ONE helper, shared
                    // with MobileShell. They used to be derived inline and
                    // differently on the two shells — desktop off `activeRole`,
                    // mobile off `canManageBilling` — so a caller granted
                    // `manage_billing:write` without an admin role got a
                    // different destination depending on the device.
                    <BillingPage
                      canManageBilling={canManageBilling}
                      {...billingHandoff({
                        isPersonal,
                        // The CAPABILITY, not the role. Owner/admin hold it by
                        // default, but a read-only budget grant exists precisely
                        // so someone outside those roles can look.
                        canReadBudget: canCap('manage_budget', 'read'),
                        canManageBilling,
                        prefix,
                        navigate,
                      })}
                    />
                  )}
                />
                </SessionDeck>
              </ErrorBoundary>
              </DeckVisibility>
            </div>
          </div>
          {/* Rendered here rather than by routing to /forgot-password: that
              route is declared ahead of the `/*` catch-all, so navigating to it
              unmounts RootShell and every keep-alive deck with it, and drops a
              signed-in user onto anonymous chrome whose only exit is "Back to
              sign in" (on an iOS home-screen install, with no browser back
              button, that is a dead end). The Modal portals to <body> and marks
              the rest of the app `inert`, so the deck behind it cannot be
              switched while it is open. */}
          {forgotPwOpen && (
            <ForgotPasswordModal
              email={user?.email}
              onClose={() => setForgotPwOpen(false)}
            />
          )}
        </ThemedArea>
      )}

      {/* Usage & Billing window — the PLATFORM shelf (tenant ceilings, feature
          pricing, catalog). Same keep-alive deck pattern as the Settings window
          above; pages parse from the frozen `/billing/:page` location. */}
      {/* Two redirects, both into Settings.

          The first sends a moved path to its new home, so an old bookmark of
          `/billing/budget` lands on `/settings/budget` rather than on an empty
          platform shelf.

          The second catches anyone who is not a platform admin acting in the
          platform org — the shelf has nothing for them at all now. It waits for
          `budgetBilling.loading` to clear: `allowed` used to be false while the
          capability fetch was in flight for exactly the same reason it is false
          on a denial, so firing on it bounced every COLD load of a /billing URL
          to Settings. The platform predicate needs no fetch and `loading` is
          now always false, but the guard stays — it costs nothing and it is
          what the next gate with a query behind it will need. */}
      {deckKind === 'billing' && billingMovedTo && (
        <UsageRedirect
          to={`${isPersonal ? '/~' : `/t/${tenantId}`}/settings/${billingMovedTo}`}
          keepQuery
        />
      )}
      {deckKind === 'billing' && !billingMovedTo && !budgetBilling.loading && !hasBudgetBilling && (
        <UsageRedirect to={`${isPersonal ? '/~' : `/t/${tenantId}`}/settings/usage`} />
      )}
      {(shell.isRunning('billing') || deckKind === 'billing') && (
        <ThemedArea area="workspace" className="flex-1 flex min-w-0 min-h-0 ws-clear" style={{ display: deckKind === 'billing' ? undefined : 'none' }}>
          <div className="flex-1 flex min-w-0 min-h-0 overflow-hidden px-3 pb-3 pt-0.5">
            <div className="ws-card-hero flex-1 flex flex-col min-w-0 min-h-0">
              {/* Same as the settings deck above: mounted-but-hidden, so the
                  console's spend screens need the re-show signal to stop
                  serving whatever was true when the shelf was first opened. */}
              <DeckVisibility active={deckKind === 'billing'}>
              <ErrorBoundary>
                <SessionDeck>
                <BudgetBillingView
                  activePage={billingPageKey}
                  onPageChange={(page) => {
                    const bprefix = isPersonal ? '/~' : `/t/${tenantId}`
                    // Path comes from the view's own map, not the page key —
                    // every page nests under `platform/`, so concatenating the
                    // key would emit a URL the parser rejects.
                    navigate(`${bprefix}/billing/${budgetBillingPagePath(page)}`, { replace: false })
                  }}
                  capabilities={budgetBilling.capabilities}
                  {...buildPlatformPages({
                    access: budgetBilling,
                    isPlatformAdmin: user?.platformRole === 'admin',
                    activeTenantId: tenantId,
                    customerId: billingRoute.tenantId,
                    planId: billingRoute.planId,
                    onOpenCustomer: (id) => navigate(
                      `${isPersonal ? '/~' : `/t/${tenantId}`}/billing/${budgetBillingPagePath('customers')}/${id}`,
                    ),
                    // One plan's editor is a real URL, like a customer's page —
                    // it is a screen's worth of one subject and gets pasted
                    // into tickets. The `:subsub?` depth already covers it.
                    onOpenPlan: (id) => navigate(
                      `${isPersonal ? '/~' : `/t/${tenantId}`}/billing/${budgetBillingPagePath('pricebook')}/${id}`,
                    ),
                    // A real navigation, not history.back(): the operator may
                    // have arrived by pasted link with no list behind them.
                    onClosePlan: () => navigate(
                      `${isPersonal ? '/~' : `/t/${tenantId}`}/billing/${budgetBillingPagePath('pricebook')}`,
                    ),
                    // A real back NAVIGATION, not history.back(): the operator
                    // may have arrived by pasted link, where there is no list
                    // entry to go back to.
                    onCloseCustomer: () => navigate(
                      `${isPersonal ? '/~' : `/t/${tenantId}`}/billing/${budgetBillingPagePath('customers')}`,
                    ),
                    // Overview's alert tiles link into the queue that resolves
                    // them. Same navigate as `onPageChange`, so the URL is
                    // real and the back button works.
                    onOpenOperations: () => navigate(
                      `${isPersonal ? '/~' : `/t/${tenantId}`}/billing/${budgetBillingPagePath('operations')}`,
                    ),
                    // The grace ceiling is a configured number shown on Overview
                    // and edited on Platform settings; the tile links the two.
                    onOpenSettings: () => navigate(
                      `${isPersonal ? '/~' : `/t/${tenantId}`}/billing/${budgetBillingPagePath('settings')}`,
                    ),
                  })}
                  // Launcher curation (#342) is not part of buildPlatformPages'
                  // audience-shaped set; supplied explicitly as the sixth page.
                          />
                </SessionDeck>
              </ErrorBoundary>
              </DeckVisibility>
            </div>
          </div>
        </ThemedArea>
      )}

        </div>{/* window area */}
      </div>{/* content column */}

      {/* Mini-app icon editor — from a taskbar dock entry's context menu. */}
      {iconEditorAppId && (
        <MiniappIconEditorForApp fileId={iconEditorAppId} onClose={() => setIconEditorAppId(null)} />
      )}

      {/* Org creation dialog */}
      {/* Rendered here ONLY when the identity shell is down. While it is up the
          same element is handed to the shell's `overlay` slot instead, because
          this tree is both underneath its fog and inside the subtree it marks
          `inert`. One element, two possible mount points, no duplicated logic. */}
      {showOrgCreate && !identityShellActive && orgCreateDialog}

      {/* The identity shell (§8.2 / §8.3) — a full-screen takeover for an
          identity-only person the auto-switch cannot place: a picker when there
          are two or more full tenants, the avatar alone when there are none.
          It portals to the body, so its position in this tree is immaterial;
          it sits here beside the other account-level surfaces.

          `settingsOpen` is what lets its Settings item work without a second
          copy of SettingsView's prop wall — the shell steps aside and the
          MOUNTED settings deck (which is now full-width, the taskbar being
          hidden below) comes up behind it. */}
      {identityShellActive && user && (
        <IdentityShell
          user={{ name: user.name, email: user.email }}
          // Rows only when the PICKER is the reason we are up. In the org-less
          // state there is nothing to switch to, and passing candidates there
          // would render a picker with no candidates.
          pickerTenants={pickerActive ? switchableTenants : []}
          onPicked={markTenantChosen}
          activeTenantId={tenantId}
          activeOrgEmail={activeOrgEmail}
          pendingInvites={pendingInvites}
          onSwitchTenant={switchTenant}
          onCreateOrg={() => setShowOrgCreate(true)}
          canCreateOrg={canCreateOrg}
          onOpenSettings={() => navigate(`${prefix}/settings/profile`)}
          onSignOut={signOut}
          onAcceptInvite={handleAcceptInvite}
          onDeclineInvite={handleDeclineInvite}
          onBlockInvite={handleBlockInvite}
          settingsOpen={deckKind === 'settings'}
          overlay={showOrgCreate ? orgCreateDialog : undefined}
        />
      )}

      {/* Identity picker — fired when an org Drive can't resolve which of the
          user's emails to act as (2+ memberships, none selected). */}
      {identityRequired && identityRequired.tenantId === tenantId && !isPersonal && !activeOrgEmail && (
        <IdentityRequiredDialog
          tenantName={identityRequired.tenantName ?? tenantsList.find(t => t.id === tenantId)?.name ?? null}
          identities={identityRequired.identities}
          onPick={async (email) => {
            await setActiveOrgEmail(email)
            setIdentityRequired(null)
            // The selection is now stored server-side; reload so every query
            // that 401'd re-fires with the chosen identity.
            window.location.reload()
          }}
          onUsePersonal={() => { setIdentityRequired(null); navigate('/~/folder') }}
        />
      )}

      {/* Connection notification popups (member flow: accept / confirm) */}
      {shareLinkReview && (
        <ShareLinkReviewDialog
          target={shareLinkReview}
          place={(() => {
            // Editable in place only as the identity that owns it — the card's edit panel acts in the ACTIVE tenant
            // (and org email). Otherwise its edit button first switches there (an explicit switch, to the identity
            // that made the link — an org can hold several of the person's emails), staying on the same page in the
            // new org and keeping this dialog open.
            const linkTenant = shareLinkReview.tenantId ?? tenantId
            const sameEmail = !shareLinkReview.orgEmail || !activeOrgEmail
              || shareLinkReview.orgEmail.toLowerCase() === activeOrgEmail.toLowerCase()
            const owner = tenantsList.find((x) => x.id === linkTenant)
            return {
              editableHere: linkTenant === tenantId && sameEmail,
              orgName: owner && owner.type !== 'personal' ? owner.name : null,
              goInOwner: async (rest?: string) => {
                // `rest`: a page inside the link's org (e.g. the drive folder revealing its target); none = stay on
                // the current page, in that org.
                const prefix = readerTenantPathPrefix(linkTenant, personalTenantId)
                const to = `${prefix}${rest ?? location.pathname.replace(/^\/(~|t\/[^/]+)/, '')}`
                if (linkTenant === tenantId && sameEmail) { if (rest) navigate(to); return }
                await switchTenant(linkTenant, linkTenant === personalTenantId ? undefined : (shareLinkReview.orgEmail ?? undefined), { to })
              },
            }
          })()}
          onClose={() => setShareLinkReview(null)}
        />
      )}
      {connAction?.kind === 'accept' && (
        <AcceptConnectionPopup
          requestId={connAction.requestId}
          fromEmail={connAction.fromEmail}
          fromName={connAction.fromName}
          orgName={connAction.orgName}
          initiatorNote={connAction.note}
          viaReshare={connAction.viaReshare}
          shareDriven={connAction.shareDriven}
          referrerName={connAction.referrerName}
          referrerEmail={connAction.referrerEmail}
          declineTargetEmail={connAction.declineTargetEmail}
          sharedName={connAction.sharedName}
          sharedIsFolder={connAction.sharedIsFolder}
          targetTenantId={connAction.targetTenantId}
          myTenants={tenantsList}
          onClose={() => setConnAction(null)}
          onDone={() => setConnAction(null)}
        />
      )}
      {connAction?.kind === 'confirm' && (
        <ConfirmConnectionPopup
          requestId={connAction.requestId}
          asTenantId={connAction.asTenantId}
          recipientEmail={connAction.recipientEmail}
          recipientName={connAction.recipientName}
          pickedTenantName={connAction.pickedTenantName}
          notifiedEmail={connAction.notifiedEmail}
          initiatorOrgName={connAction.initiatorOrgName}
          // The request's initiator tenant — NOT the active one (this popup can
          // open from any tenant via the cross-tenant bell).
          initiatorIsPersonal={connAction.asTenantId === personalTenantId}
          asOrgEmail={connAction.asOrgEmail}
          source={connAction.source}
          linkPermission={connAction.linkPermission}
          linkCap={connAction.linkCap}
          shareDriven={connAction.shareDriven}
          sharedName={connAction.sharedName}
          onClose={() => setConnAction(null)}
          onDone={() => setConnAction(null)}
        />
      )}
      {connAction?.kind === 'reshare-approve' && (
        <ReshareApprovalPopup
          requestId={connAction.requestId}
          referrerName={connAction.referrerName}
          recipientEmail={connAction.recipientEmail}
          asTenantId={connAction.asTenantId}
          asOrgEmail={connAction.asOrgEmail}
          onClose={() => setConnAction(null)}
          onDone={() => setConnAction(null)}
        />
      )}

      {/* Drive picker dialog (triggered from member access) */}
      {drivePickerOpen && (
        <DrivePickerDialog
          tenantId={tenantId}
          onSelect={(id, isFolder) => {
            drivePickerResolveRef.current?.({ id, isFolder })
            drivePickerResolveRef.current = null
            setDrivePickerOpen(false)
          }}
          onClose={() => {
            drivePickerResolveRef.current?.(null)
            drivePickerResolveRef.current = null
            setDrivePickerOpen(false)
          }}
        />
      )}

      {/* Dev-only offline toggle (hidden — use browser console: devOfflineState.set(true)) */}
      {import.meta.env.DEV && devOffline && (
        <button
          onClick={() => {
            const next = !devOffline
            setDevOffline(next)
            devOfflineState.set(next)
          }}
          className={`fixed bottom-3 right-3 z-50 px-2.5 py-1 text-[10px] font-mono rounded-full border transition-colors ${
            devOffline
              ? 'bg-red-600/90 border-red-500 text-white'
              : 'bg-zinc-800/80 border-zinc-600 text-zinc-400 hover:text-zinc-200'
          }`}
          title="Toggle dev offline mode (disconnects realtime WebSocket)"
        >
          {devOffline ? 'OFFLINE' : 'online'}
        </button>
      )}
      <UploadDock />
      {/* #758. Mounted at the shell because the refusals it explains come
          back from four CHAT paths — the send, the AG-UI run, the attachment
          upload and a fork — so one dialog answers all four.
          NOT "every path": an earlier version of this line said "the chat,
          a drive upload or a fork", which contradicts the subscription
          itself ~2,700 lines up. That handler is deliberately scoped to
          `/api/agent`, because every drive write raises
          `STORAGE_QUOTA_EXCEEDED` too and those call sites render their own
          refusal — unscoped, this opened a modal on top of each of them
          (round 1 finding 9, re-stated here at full review 43C, C11).
          Nor "every path" on a phone: `MobileShell` replaces this shell
          outright and has no equivalent subscription, so a mobile member
          gets the banner but no explanation when the refusal lands
          (full review 43C, B10 — in dw/decisions-to-make.md as D25). */}
      {storageBlocked && (
        <StorageBlockedDialog
          kind={storageBlocked.kind}
          usedBytes={storageBlocked.usedBytes}
          limitBytes={storageBlocked.limitBytes}
          onClose={() => setStorageBlocked(null)}
          onManage={() => navigate(`${prefix}/settings/storage`)}
        />
      )}
    </div>
    </NewProjectProvider>
   </ShellTopBarProvider>
  )
}

function WorkspacePopupContent({ tenantId, onClose }: { tenantId: string; onClose?: () => void }) {
  const newProject = useNewProject()
  const toast = useToast()
  const { t } = useTranslation('files')
  const navigate = useNavigate()
  const location = useLocation()
  const { openWorkspaces, activeWorkspaceId, closeWorkspace, setActiveWorkspace, reorderWorkspaces } = useWorkspaceManager()
  const [draggedId, setDraggedId] = useState<string | null>(null)
  const [dragOverId, setDragOverId] = useState<string | null>(null)
  const [externalDropOnNew, setExternalDropOnNew] = useState(false)
  const [recentWorkspaces, setRecentWorkspaces] = useState<Array<{ folderId: string; name: string; path: string }>>([])

  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hoverWsIdRef = useRef<string | null>(null)

  // Load recent workspaces — per-email for orgs, per-tenant for personal
  useEffect(() => {
    setRecentWorkspaces([])
    workspaceApi.listRecent().then(items => {
      setRecentWorkspaces(items.map(w => ({ folderId: w.folderId, name: w.name, path: w.path })))
    }).catch(() => {})
  }, [tenantId])

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    hoverWsIdRef.current = null
  }, [])

  /** Close popup after workspace has time to render */
  const closeAfterNavigate = useCallback(() => {
    // Use a real delay — workspace config loads async (API calls),
    // so rAF is too fast. 500ms gives React time to mount & render.
    setTimeout(() => onClose?.(), 500)
  }, [onClose])

  // Clean up all drag state when drag ends (Escape key or drop outside).
  // Note: right-click cannot cancel HTML5 drag in Chrome — Chrome suppresses all
  // opposite-button events while a drag is active. Escape is the standard cancel.
  useEffect(() => {
    const handleDragCleanup = () => {
      clearHoverTimer()
      setDragOverId(null)
      setDraggedId(null)
      setExternalDropOnNew(false)
    }
    window.addEventListener('dragend', handleDragCleanup)
    return () => window.removeEventListener('dragend', handleDragCleanup)
  }, [clearHoverTimer])

  const handleDragStart = useCallback((e: React.DragEvent, wsId: string) => {
    setDraggedId(wsId)
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent, wsId: string) => {
    e.preventDefault()
    // Accept both internal reorder and external file drops
    if (draggedId && draggedId !== wsId) {
      setDragOverId(wsId)
    } else if (isPapanDrag(e.dataTransfer)) {
      setDragOverId(wsId)
      // Start hover-to-open timer (1s) — only for external drags
      if (hoverWsIdRef.current !== wsId) {
        clearHoverTimer()
        hoverWsIdRef.current = wsId
        hoverTimerRef.current = setTimeout(() => {
          navigate(`/t/${tenantId}/workspace/${wsId}`)
          closeAfterNavigate()
        }, 1000)
      }
    }
  }, [draggedId, clearHoverTimer, closeAfterNavigate, navigate, tenantId])

  /** Link external items to an existing workspace (folders directly, files via parent) */
  const handleLinkItems = useCallback(async (wsFolderId: string, items: { id: string; name: string; isFolder: boolean }[]) => {
    // #1316 review round 2 (owner, 2026-10-28): the links the person started FINISH as the identity that started them;
    // the navigation to the workspace is skipped once that session ended (it is the old tenant's)
    const batch = pinnedBatch()
    try {
      const folders = items.filter(f => f.isFolder)
      const files = items.filter(f => !f.isFolder)

      // Link folders directly
      for (const folder of folders) {
        await workspaceApi.addLink(wsFolderId, folder.id, batch.opts())
      }

      // For files, look up parent folder and link that
      const linkedParents = new Set<string>()
      let openFileId: string | null = null
      for (const file of files) {
        if (!openFileId) openFileId = file.id
        try {
          const info = await filesApi.getFileInfo(file.id, batch.opts())
          if (info.parentId && !linkedParents.has(info.parentId)) {
            linkedParents.add(info.parentId)
            await workspaceApi.addLink(wsFolderId, info.parentId, batch.opts())
          }
        } catch {
          // Skip files we can't look up
        }
      }

      if (batch.ended()) return
      // Notify workspace views to reload config
      window.dispatchEvent(new CustomEvent('papan:workspace-changed', { detail: { folderId: wsFolderId } }))
      const openParam = openFileId ? `?open=${openFileId}` : ''
      navigate(`/t/${tenantId}/workspace/${wsFolderId}${openParam}`)
      closeAfterNavigate()
    } catch (err) {
      console.error('[Workspace] link items failed:', err)
    }
  }, [tenantId, navigate, closeAfterNavigate])

  const handleDrop = useCallback((e: React.DragEvent, targetWsId: string) => {
    e.preventDefault()
    clearHoverTimer()

    // External drop (from DriveView) → link to workspace
    if (isPapanDrag(e.dataTransfer) && !draggedId) {
      const transfer = readPapanDrag(e.dataTransfer)
      // A drag from another drive's tab can't be linked into this drive's project.
      if (transfer?.tenantId && transfer.tenantId !== tenantId) {
        toast.error(t('driveView.toast.crossTenantMove'))
        setDragOverId(null)
        return
      }
      if (transfer && transfer.files.length > 0) {
        const ws = openWorkspaces.find(w => w.folderId === targetWsId)
        if (ws) handleLinkItems(ws.folderId, transfer.files)
      }
      setDragOverId(null)
      return
    }

    // Internal reorder
    if (!draggedId || draggedId === targetWsId) return
    const fromIdx = openWorkspaces.findIndex(w => w.folderId === draggedId)
    const toIdx = openWorkspaces.findIndex(w => w.folderId === targetWsId)
    if (fromIdx !== -1 && toIdx !== -1) {
      reorderWorkspaces(fromIdx, toIdx)
    }
    setDraggedId(null)
    setDragOverId(null)
  }, [draggedId, openWorkspaces, reorderWorkspaces, handleLinkItems, clearHoverTimer, tenantId, toast, t])

  const handleDragEnd = useCallback(() => {
    setDraggedId(null)
    setDragOverId(null)
    clearHoverTimer()
  }, [clearHoverTimer])

  /** Handle drop on "+ New project" button — open the access dialog (org) /
   *  auto-create (personal) via the provider, then link the dropped items. */
  const handleNewWorkspaceDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    setExternalDropOnNew(false)

    const transfer = readPapanDrag(e.dataTransfer)
    if (!transfer || transfer.files.length === 0) return
    // A drag from another drive's tab can't be linked into a project of this drive.
    if (transfer.tenantId && transfer.tenantId !== tenantId) {
      toast.error(t('driveView.toast.crossTenantMove'))
      return
    }

    try {
      newProject.open({
        autoCreatePersonal: true,
        onDone: (folderId) => { void handleLinkItems(folderId, transfer.files) },
      })
    } catch (err) {
      console.error('[Workspace] new workspace drop failed:', err)
    }
  }, [handleLinkItems, newProject, tenantId, toast, t])

  const handleClose = useCallback((folderId: string) => {
    const idx = openWorkspaces.findIndex(w => w.folderId === folderId)
    const remaining = openWorkspaces.filter(w => w.folderId !== folderId)
    closeWorkspace(folderId)

    // Only navigate if we're currently viewing this workspace
    const isViewing = location.pathname.includes(`/workspace/${folderId}`)
    if (!isViewing) return

    if (remaining.length > 0) {
      // Switch to next workspace (or previous if closing the last one)
      const nextIdx = Math.min(idx, remaining.length - 1)
      const next = remaining[nextIdx]
      setActiveWorkspace(next.folderId)
      navigate(`/t/${tenantId}/workspace/${next.folderId}`)
    } else {
      // No workspaces left — go to empty workspace view
      navigate(`/t/${tenantId}/workspace`)
    }
  }, [openWorkspaces, closeWorkspace, setActiveWorkspace, navigate, tenantId, location.pathname])

  // Recents minus whatever is already open — an open workspace belongs under
  // ACTIVE, not twice. Mirrors MobileWorkspaceSwitcher's split.
  const openIds = new Set(openWorkspaces.map((w) => w.folderId))
  const recents = recentWorkspaces.filter((w) => !openIds.has(w.folderId))
  // The submenu is quick access to the few most recent, not an archive — older
  // ones live in the Drive. Deliberately unnumbered: a count would advertise
  // more than the submenu shows and read as a dead end.
  const MAX_RECENT_IN_SUBMENU = 3
  // Where the card being dragged currently sits — decides which side of a
  // target the insertion line belongs on. -1 when nothing is being dragged.
  const draggedIdx = draggedId ? openWorkspaces.findIndex((w) => w.folderId === draggedId) : -1
  // The fan-out stagger runs across the WHOLE panel (active cards, then
  // recents, then the footer actions), so the list unfurls as one sweep rather
  // than each section restarting from zero.
  let fanIdx = 0
  const fan = () => ({ animationDelay: `${fanIdx++ * 40}ms` })

  return (
    // No surface of its own: the cards float directly on the full-app fog, the
    // way the mobile sheet does.
    //
    // Deliberately NOT a scroll container. MAX_OPEN_WORKSPACES bounds ACTIVE at
    // 6, so the panel can't grow unbounded and doesn't need one — and an
    // overflow container here would CLIP the Recent submenu (overflow-y:auto
    // forces overflow-x to auto, so a `left-full` flyout gets cut off rather
    // than escaping). The submenu carries its own scroll instead.
    <div className="ml-1 flex w-[320px] flex-col gap-2 p-3">
      <p className="mobile-switcher-label px-1 text-[10px] uppercase tracking-wide themed-text-mid">Active projects</p>
      {openWorkspaces.map((ws, i) => {
        const isExternalDrop = !draggedId && dragOverId === ws.folderId
        const isReorderDrop = !!draggedId && dragOverId === ws.folderId
        const active = ws.folderId === activeWorkspaceId
        // Which SIDE the dragged card actually lands on. reorderWorkspaces
        // splices (remove at from, insert at to), so dragging DOWN puts it after
        // the target and dragging UP puts it before. Drawing the line on the
        // wrong side would promise a position the drop doesn't deliver.
        const insertAfter = draggedIdx !== -1 && draggedIdx < i
        return (
          <div
            key={ws.folderId}
            role="button"
            tabIndex={0}
            draggable
            style={fan()}
            onDragStart={(e) => handleDragStart(e, ws.folderId)}
            onDragOver={(e) => handleDragOver(e, ws.folderId)}
            // dragleave also fires when the pointer crosses onto a CHILD (the
            // grip, the label, the ✕). Clearing on those makes the line strobe
            // as you move within one card. relatedTarget is where the pointer
            // went — if it's still inside this card, we never left.
            onDragLeave={(e) => {
              if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
              setDragOverId(null)
              clearHoverTimer()
            }}
            onDrop={(e) => handleDrop(e, ws.folderId)}
            onDragEnd={handleDragEnd}
            onClick={() => {
              setActiveWorkspace(ws.folderId)
              navigate(`/t/${tenantId}/workspace/${ws.folderId}`)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setActiveWorkspace(ws.folderId)
                navigate(`/t/${tenantId}/workspace/${ws.folderId}`)
              }
            }}
            aria-current={active ? 'true' : undefined}
            className={`ws-home-tile ws-fan-item relative shrink-0 cursor-pointer ${active ? 'is-selected' : ''}${
              draggedId === ws.folderId ? ' opacity-50' : ''
            }${isExternalDrop ? ' !border-green-500 bg-green-600/20' : ''}`}
          >
            {active && <span className="mobile-shine-border" aria-hidden />}
            {/* Reorder indicator: a line in the GAP showing where the card will
                land, not a border round the neighbour. A border highlights a
                card — the wrong idea entirely, since the drop inserts BETWEEN
                cards rather than onto one. Sits in the flex `gap-2`, so it never
                shifts the layout. External file-drops keep their border: that
                one really does target the card (drop INTO this workspace). */}
            {isReorderDrop && (
              <span
                aria-hidden
                className={`pointer-events-none absolute inset-x-0 z-10 h-0.5 rounded-full bg-purple-500 ${insertAfter ? '-bottom-1' : '-top-1'}`}
              />
            )}
            {/* Desktop-only affordance (mobile can't reorder). Always visible:
                the whole card is draggable, but reordering is invisible without
                a handle to advertise it — hiding this until hover meant nobody
                knew the cards could be rearranged at all. */}
            <GripVertical
              size={14}
              className="shrink-0 cursor-grab themed-text-muted"
              aria-hidden
            />
            <span className="ws-project-glyph shrink-0">
              <LayoutGrid size={16} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm themed-text" title={getWorkspaceName(ws.name)}>{getWorkspaceName(ws.name)}</span>
              {ws.path && <span className="block truncate text-xs themed-text-mid" title={ws.path}>{ws.path}</span>}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); handleClose(ws.folderId) }}
              aria-label={`Close ${getWorkspaceName(ws.name)}`}
              className="relative z-10 -mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg themed-text-mid hover:themed-text hover:themed-bg-tertiary"
            >
              <X size={14} />
            </button>
          </div>
        )
      })}
      {openWorkspaces.length === 0 && (
        <p className="px-1 text-xs themed-text-muted">No open projects</p>
      )}

      {/* RECENT — one row that flies its list out on hover, rather than N rows
          inline. With the cap evicting workspaces into here, this list only
          grows, so keeping it collapsed is what holds the panel to a readable
          height. */}
      {recents.length > 0 && (
        <div className="group/recent relative shrink-0" style={fan()}>
          <div className="ws-home-tile ws-fan-item w-full cursor-default">
            <span className="ws-project-glyph shrink-0">
              <Clock size={15} />
            </span>
            <span className="flex-1 text-sm themed-text">Recent projects</span>
            <ChevronRight size={14} className="shrink-0 themed-text-muted" />
          </div>
          {/* pl-2 is the bridge, not decoration: it keeps the cursor inside the
              hover group while crossing the gap to the submenu. */}
          <div className="absolute left-full top-0 hidden pl-2 group-hover/recent:block">
            {/* No scroll container needed: the slice bounds this at 3, so it
                always fits. */}
            <div className="flex w-[320px] flex-col gap-2">
              {recents.slice(0, MAX_RECENT_IN_SUBMENU).map((rw, i) => (
                <div
                  key={rw.folderId}
                  role="button"
                  tabIndex={0}
                  // Its own stagger, restarting at 0 — the submenu is a surface
                  // that opens on its own, so it fans out from its own edge
                  // rather than continuing the parent panel's sweep. Replays on
                  // every hover: going display:none -> block restarts the
                  // animation.
                  style={{ animationDelay: `${i * 40}ms` }}
                  onClick={() => { navigate(`/t/${tenantId}/workspace/${rw.folderId}`); onClose?.() }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/t/${tenantId}/workspace/${rw.folderId}`)
                      onClose?.()
                    }
                  }}
                  className="ws-home-tile ws-fan-item shrink-0 cursor-pointer"
                >
                  <span className="ws-project-glyph shrink-0">
                    <Clock size={15} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm themed-text" title={getWorkspaceName(rw.name)}>{getWorkspaceName(rw.name)}</span>
                    {rw.path && <span className="block truncate text-xs themed-text-mid" title={rw.path}>{rw.path}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <button
        style={fan()}
        onClick={() => navigate(`/t/${tenantId}/workspace`)}
        onDragOver={(e) => {
          if (isPapanDrag(e.dataTransfer)) {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            setExternalDropOnNew(true)
          }
        }}
        onDragLeave={() => setExternalDropOnNew(false)}
        onDrop={handleNewWorkspaceDrop}
        className={`ws-home-tile ws-fan-item mt-1 shrink-0${
          externalDropOnNew ? ' !border-green-500 bg-green-600/20 text-green-400' : ''
        }`}
      >
        <span className="ws-project-glyph shrink-0">
          <Plus size={15} />
        </span>
        <span className="text-sm themed-text">New project</span>
      </button>
    </div>
  )
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        {/* BrandProvider sits ABOVE ThemeProvider (white-label layer 3). It has
            no data dependencies of its own — no session, no tenant — so it can
            live at the very top, which is what makes one provider cover the
            login page, the share view, and the app shell alike. Its snapshot
            comes from the module store that main.tsx already kicked off, so
            mounting order here doesn't matter. */}
        <BrandProvider>
        <ThemeProvider>
          <ToastProvider>
            <ConfirmProvider>
              <ConflictProvider>
                <UndoProvider>
                  <AuthProvider>
                    {/* Deployment-wide, so it sits ABOVE TenantProvider: the
                        charging currency does not change when you switch org,
                        and re-fetching it per tenant would say it might. */}
                    <BillingCurrencyProvider>
                    <TenantProvider>
                      <DraftStoreReset />
                      {/* The identity portal every tenant / org-email switch plays (switchTenant). */}
                      <IdentitySwitchCurtainHost />
                      <TenantScopedWriteStatus>
                      <TenantScopedRealtime>
                        <SessionCacheSync />
                        <UploadManagerProvider>
                          <TenantScopedWorkspaceManager>
                            <ShellManagerProvider>
                            <IconAssetUrlContext.Provider value={spaIconAssetResolver}>
                            <Routes>
                              <Route path="/invite/:token" element={<InviteAcceptPage />} />
                              {/* Promo landing (P4). Sits beside /invite for the
                                  same reason: a link handed to someone who may
                                  not be signed in yet, which must survive the
                                  login detour and then let them CHOOSE where the
                                  grant lands. */}
                              <Route path="/promo/:code" element={<PromoRedeemPage />} />
                              {/* Remote MCP connect (docs/plans/2026-10-13_MCP_OPTION_B_BUILD.md): Logto's
                                  consent bridge sends the browser here, and Connected apps lives beside it.
                                  Outside RootShell — a standalone card, like sign-in. */}
                              <Route path="/oauth/consent" element={<McpConsentPage />} />
                              <Route path="/oauth/connections" element={<McpConnectionsPage />} />
                              <Route path="/oauth/connections/:id" element={<McpConnectionsPage />} />
                              <Route path="/share-view/:sessionId" element={<ShareViewerChrome />} />
                              {/* #605 file handoff: Papan's own picker for an app's
                                  pickAndRead — a modal iframe in Papan, a popup on
                                  pd.app. Outside RootShell: no taskbar, no shell. */}
                              <Route path="/__handoff/:pid" element={<HandoffPickerPage />} />
                              <Route path="/t/:tenant/__handoff/:pid" element={<HandoffPickerPage />} />
                              <Route path="/login" element={<LoginPage />} />
                              <Route path="/app-signin/:token" element={<AppSignInContinuePage />} />
                              {/* Logto's sign-in bridge returns a signed-out person here (PAPAN_BRIDGE_SIGN_IN_URL) */}
                              <Route path="/sign-in" element={<LogtoBridgeSignInPage />} />
                              <Route path="/share-signin/:token" element={<ShareSignInContinuePage />} />
                              <Route path="/signup" element={<SignupPage />} />
                              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                              <Route path="/reset-password" element={<ResetPasswordPage />} />
                              <Route path="/verify-email" element={<VerifyEmailPage />} />
                              {/* Legal chrome. Public and unauthenticated by
                                  design: the share gate on the ppn.gg zone
                                  links recipients here, and those people have
                                  no account — a policy behind a login is a
                                  policy nobody subject to it can read. */}
                              <Route path="/privacy" element={<PrivacyPage />} />
                              <Route path="/agui-lab" element={<AguiLabPage />} />
                              <Route path="/*" element={<RootShell />} />
                            </Routes>
                            </IconAssetUrlContext.Provider>
                            </ShellManagerProvider>
                          </TenantScopedWorkspaceManager>
                        </UploadManagerProvider>
                      </TenantScopedRealtime>
                      </TenantScopedWriteStatus>
                    </TenantProvider>
                    </BillingCurrencyProvider>
                  </AuthProvider>
                </UndoProvider>
              </ConflictProvider>
            </ConfirmProvider>
          </ToastProvider>
        </ThemeProvider>
        </BrandProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
